/**
 * The UMED board-parity table (EP-010 §9–§12).
 *
 * == Why this looks like Monday, deliberately =============================
 *
 * Everywhere else in eMerge, reproducing a board's presentation would be a
 * mistake. Here it is the requirement. Josie reconciles high volumes by moving
 * across ten familiar tabs, reading a colour, and scanning a wide row — and
 * that learned fluency is a real operational control, not a habit to be
 * retrained for its own sake. So the tabs are the board's tabs, in the board's
 * order, under the board's names; the colours are the board's colours; and the
 * table scrolls sideways because reconciliation genuinely wants the columns.
 *
 * == What is NOT reproduced ===============================================
 *
 * The architecture. Nothing here moves a row into a group. Every tab is a
 * named predicate over one native case record, decided by the server, and the
 * same predicate serves the tab's badge, its rows and its export — so three
 * people cannot read three numbers and each be right.
 *
 * And one presentation rule the board does not have: the colour never carries
 * a fact by itself. Every chip has its word beside it, and the word eMerge
 * shows is the SOURCE word, never a verdict. The board's APPROVED is coloured
 * and named "Approved in Monday · not approved in eMerge" (P3), because approval is the UMED operator's
 * act of moving the case to Approved, with FLMMIS reflected on record
 * (2026-10-06), and a table cannot be allowed to quietly say otherwise.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Why, firstReason } from '../../components/emerge/WithReason';
import MonthsNotReflecting from '../../components/corrections/MonthsNotReflecting';
import {
  umedApi, personName, shortDate, boardDate, relativeDay, parseTermDates, STAGE_LABEL, stageLabelOf, NOCA_LABEL, FLMMIS_LABEL, ENROLLMENT_LABEL, PACKET_BLOCKER_OWNER_LABEL,
  canDo, freshKey, isoDay, parseTermsLine, BoardTabDef, BoardTabKey, BoardPage, BoardRow, BoardSort, LegendEntry, OtherUmed, Scope,
  Noca, Flmmis, NocaResult, FlmmisResult, CorrectionType,
  CaseDisposition, Disposition, DISPOSITION_WORDS, DISPOSITION_LABEL,
  PAID_IN_FULL_GROUP_LABEL, paidInFullOption,
  MondayItem, MONDAY_APPROVAL_CLAIM,
  DCF_BENEFITS_STUCK_LABEL,
  PARTIAL_APPROVAL_WORD, PARTIAL_APPROVAL_HEX, monthsLabel,
  REVERSAL_REQUESTER_LABEL, CORRECTION_NOTE_WITHHELD,
} from './umedApi';
import type { CaseFocus, ReversalRequester, ResubmittalKind, StartResubmittalBody, CorrectionStatus, CorrectionCompleted, ReversalStartStatus, UmedCase } from './umedApi';
// 2026-10-19: UMED starts a DCF resubmittal; its corrected terms are typed in the same inputs Finance's start uses.
import { TermsInputs } from '../finance/ResubmittalsKanban';
import { EMPTY_TERMS, termsBody, termsLine } from '../finance/resubmittalsModel';
import type { TermsFields } from '../finance/resubmittalsModel';
import { updatedStamp, mondayRetiredLine } from '../../components/workspaceFreshness';
// Gwynn, 2026-10-12: "(STUCK) Can't link Benefits" -- the same record form as the case's DCF account section.
import { BenefitsForm, benefitsLine, stuckFactsOfRow } from './DcfBenefitsLink';
import { residentsApi } from '../advocate/residentsApi';
import BuildingOrClinic from '../../components/emerge/BuildingOrClinic';
import { MondayDcfEmailMarker } from './MondayDcfEmailMarker';
import { PaidInFullChip, PaidInFullForm, PAID_IN_FULL_CLEAR_EFFECT } from './PaidInFullMark';
import { FlmmisCheckMarker } from './FlmmisCheckMarker';
import StatusPicker from './StatusPicker';
// 2026-10-15: the confirm step for a genuinely shared file (FLMMIS evidence may be; a NOCA never is).
import SharedFileReason, { readSharedRefusal } from '../documents/SharedFileReason';
// The owed second channel (ruling, Isaiah Cotto 2026-10-13): the row's small 'Fax owed' / 'Upload owed' marker.
import { secondChannelMarker } from './PacketSecondChannel';
import { FINANCIAL_RESPONSIBILITY_LABEL, FinancialResponsibilityType } from '../advocate/advocateApi';
// Isaiah Cotto, 2026-10-06: every colour-coded status is FILLED with its Monday colour, from ONE shared map.
import FilledStatusChip from '../../components/status/FilledStatusChip';
import { UMED_STATUS, mondayHex, normalizeMondayWord, filledChipStyle } from '../../theme/mondayPalette';
import { Link, useInRouterContext } from 'react-router-dom';
// 2026-10-20: "Open in Finance" on a row approved here, only when this person can open the Finance workspace.
import { useOpsWorkspaces } from '../../components/workspaceSidebar/useOpsWorkspaces';

const usd = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

/**
 * Copy one value without opening the case.
 *
 * Josie Evans, 2026-09-23: "I also want to be able to right click on
 * everything instead of going into the names, facility name, dcf account &
 * password."
 *
 * Reading a name off a row and retyping it into DCF is the work; opening a
 * case to get it is not. Selecting the text by hand works too -- see the row
 * handler, which stands down when there is a selection -- but a button is one
 * action instead of three and does not depend on a steady hand in a table
 * this dense.
 *
 * The DCF password is deliberately NOT copyable from here. It is not in the
 * list payload at all: it is revealed on one case, for a stated purpose, and
 * audited before anything is decrypted. A copy button on a row would be a way
 * to take a credential without that record existing.
 */
function CopyCell({ value, what }: { value: string; what: string }) {
  const [done, setDone] = useState(false);
  return (
    <button type="button" className="um-copy" aria-label={`Copy ${what}`} title={`Copy ${what}`}
      onClick={async (e) => {
        e.stopPropagation();
        try {
          await navigator.clipboard.writeText(value);
          setDone(true);
          window.setTimeout(() => setDone(false), 1200);
        } catch { /* a refused clipboard is not worth an error state: the text is selectable. */ }
      }}>
      {done ? 'Copied' : 'Copy'}
    </button>
  );
}
/**
 * Reveal the DCF password and put it on the clipboard, in one press.
 *
 * Josie Evans, 2026-09-24: "I need to be able to copy and paste both."
 *
 * The password is never rendered and never held in component state longer
 * than the write to the clipboard. Each press is one audited reveal on the
 * server, with the reason recorded -- which is what makes 1,433 stored
 * credentials safe to keep at all.
 */
function CopyPassword({ caseId, disabled }: { caseId: string; disabled?: boolean }) {
  const [state, setState] = useState<'idle' | 'working' | 'done' | 'refused'>('idle');
  if (disabled) return null;
  return (
    <button
      type="button"
      className="um-cred"
      aria-label="Reveal and copy the DCF password"
      title="Reveals the password and copies it. Each reveal is recorded."
      disabled={state === 'working'}
      onClick={async (e) => {
        e.stopPropagation();
        setState('working');
        try {
          const r = await umedApi.revealDcfCredential(caseId, 'Copied from the UMED board to sign in to DCF.');
          if (!r.success || !r.data?.password) { setState('refused'); window.setTimeout(() => setState('idle'), 1800); return; }
          await navigator.clipboard.writeText(r.data.password);
          setState('done');
          window.setTimeout(() => setState('idle'), 1400);
        } catch {
          setState('refused');
          window.setTimeout(() => setState('idle'), 1800);
        }
      }}
    >
      {state === 'working' ? 'Revealing…' : state === 'done' ? 'Copied' : state === 'refused' ? 'Refused' : 'Copy password'}
    </button>
  );
}

/**
 * "(STUCK)" where the DCF account is read (Josie Evans, 2026-10-15, on a submitted case in Review for Approvals: "I need
 * to be able to click 'STUCK' because I am unable to get into his account"). The SAME act as the status menu's and the
 * case panel's -- BenefitsForm, recording "(STUCK) Can't link benefits" with her reason (markDcfBenefitsStuck, account
 * rule included) -- offered in the board's DCF account cell to who manages DCF accounts: any open case not approved.
 * A stuck mark in force is cleared here too ("Benefits linked"). Nothing new is recorded by this control.
 */
export function DcfStuckCellAct({ row }: { row: BoardRow }) {
  const acts = React.useContext(BoardActs);
  const [mode, setMode] = useState<'none' | 'stuck' | 'linked'>('none');
  const opener = React.useRef<HTMLButtonElement | null>(null);
  const refocus = React.useRef(false);
  useOnRowChange(row.id, () => setMode('none'));
  useEffect(() => { if (mode === 'none' && refocus.current) { refocus.current = false; opener.current?.focus({ preventScroll: true }); } }, [mode]);
  const clear = stuckClearable(row, acts);
  if (mode !== 'none') {
    return (
      <BenefitsForm caseId={row.id} mode={mode} account={stuckFactsOfRow(row.dcf_account, row.readiness?.purpose || row.creation_reason || null)}
        onClose={() => { refocus.current = true; setMode('none'); }}
        onDone={(m) => { acts?.announce(`${personName(row)}: ${m}`, row.id); }} />
    );
  }
  if (!clear && !stuckOffered(row, acts)) return null;
  const name = personName(row);
  return (
    <button type="button" className="um-link um-stuckact" ref={opener} data-testid="dcf-cell-stuck"
      aria-label={clear ? `Benefits linked for ${name}: clear the stuck mark` : `Mark ${name} ${DCF_BENEFITS_STUCK_LABEL}`}
      title={clear ? 'Clear the stuck mark: the benefits are linked.' : 'Record that the resident’s DCF benefits cannot be linked or reached, with your reason.'}
      onClick={(e) => { e.stopPropagation(); setMode(clear ? 'linked' : 'stuck'); }}>
      {clear ? 'Mark benefits linked…' : 'Mark stuck…'}
    </button>
  );
}

/**
 * A generated DCF login, ready for Gwynn to register (UM-R4, 2026-10-02):
 * copy the password, register at DCF, then record it here. Recording keeps the
 * generated password -- nothing is retyped. Two presses, so a stray click on a
 * dense row does not mark an account made.
 */
export function DcfLoginReady({ caseId, name = null }: { caseId: string; name?: string | null }) {
  const acts = React.useContext(BoardActs);
  const [state, setState] = useState<'idle' | 'confirm' | 'working' | 'done' | 'refused'>('idle');
  if (state === 'done') return <>Account made · recorded</>;
  return (
    <>
      Login ready · register at DCF
      <CopyPassword caseId={caseId} />
      <button
        type="button"
        className="um-cred"
        aria-label="Record that this DCF login is registered"
        title="Records the account as made at DCF, keeping the generated password."
        disabled={state === 'working'}
        onClick={async (e) => {
          e.stopPropagation();
          if (state === 'idle' || state === 'refused') { setState('confirm'); return; }
          if (state !== 'confirm') return;
          setState('working');
          try {
            const r = await umedApi.dcfAccountCreated(caseId, {});
            // The button is gone once it is recorded: say so in the board's status line, focus back on the row (P2-4).
            if (r.success) {
              setState('done');
              if (acts) acts.announce(`The DCF login${name ? ` for ${name}` : ''} is recorded as registered.`, caseId);
              return;
            }
          } catch { /* refused below */ }
          setState('refused');
        }}
      >
        {/* Gwynn's words for this act (2026-10-12): the DCF account was made with this email. */}
        {state === 'confirm' ? 'Confirm DCF email created' : state === 'working' ? 'Recording…' : state === 'refused' ? 'Refused' : 'DCF email created'}
      </button>
    </>
  );
}

const dollars = (v: number | null | undefined) => (v === null || v === undefined ? null : usd.format(v));
const cents = (v: number | null | undefined) => (v === null || v === undefined ? null : usd.format(v / 100));

/* ── The legend ───────────────────────────────────────────────────────── */

/**
 * One status chip, FILLED with the word's Monday colour as Monday shows it
 * (Isaiah Cotto, 2026-10-06), from the one shared map
 * (theme/mondayPalette.ts: Monday's fixed hexes, board 8146038673). The label
 * is white or near-black, whichever reads at 4.5:1 or better on that fill
 * (every UMED word does), so contrast never depends on luck. The word is always
 * rendered: the colour is never the fact by itself.
 */
/*
 * UM-R2 (Josie Evans, 2026-10-02): the label IS the board's word, in her
 * colours, and it moves when she records a native act -- the server writes the
 * word with the act. `source` says who set it: a word set by an act here says
 * so on hover and to a screen reader, because the board and eMerge behave
 * differently on the next sync.
 */
/** What the APPROVED chip says on a case a UMED operator moved to Approved (2026-10-06, P2-6). */
export const APPROVED_IN_EMERGE = 'Approved in eMerge: a UMED operator moved this case to Approved.';
/**
 * What the APPROVED chip says on a case Monday calls approved and eMerge does not (final verification P2-D):
 * the claim's one name (P3) in the tooltip and to a screen reader; the chip itself keeps the short "· Monday's word".
 */
export const APPROVED_MONDAYS_WORD = `${MONDAY_APPROVAL_CLAIM}: nobody has moved this case to Approved here.`;

export function StatusChip({ word, legend, source = null, approvedInEmerge = false }: {
  word: string | null; legend: LegendEntry | null; source?: 'EMERGE' | 'MONDAY' | 'ADOPTED' | null;
  /** The case carries the approval act. The board's caveat ("the board's word ...") is then not the truth about it. */
  approvedInEmerge?: boolean;
}) {
  if (!word) return <span className="um-text none">No status</span>;
  // Authority ruling 2026-10-05: an ADOPTED word is the one the case carried into eMerge; only eMerge moves it now.
  const by = source === 'EMERGE' ? 'Recorded in eMerge' : source === 'ADOPTED' ? 'Carried into eMerge at adoption' : null;
  // Until the bulk adoption a case the board still forwards carries Monday's word (verification of #378, 1): said so,
  // in the title and in words a screen reader reads, so a row in a status view is never mistaken for eMerge's word.
  const mondays = source === 'MONDAY' ? "Monday's word: the board still forwards this case" : null;
  // Monday's own hex for the word, from the shared map; the server's legend only where the map has none.
  const hex = mondayHex(UMED_STATUS, word) || (legend ? legend.color : null);
  if (!legend || !hex) {
    // A word the legend does not know is shown plainly and never given a
    // guessed colour. It also shows up under the `unmapped` exception, which
    // is where it gets fixed.
    return <span className="um-chip unmapped" title="eMerge's vocabulary does not map this source word.">{word}</span>;
  }
  // APPROVED on a case approved here is eMerge's own word, said so in visible words -- never "the board's word".
  const approvedHere = approvedInEmerge && legend.word === 'APPROVED';
  /*
   * APPROVED on a case nobody approved here is Monday's claim, and says so in
   * visible words and a dashed outline on the fill (final verification
   * 2026-10-06, P2-D; #355's "· Monday's word" stays) -- not only in a tooltip
   * a touch screen never shows.
   */
  const mondaysClaim = !approvedInEmerge && legend.word === 'APPROVED';
  const caveat = approvedHere ? APPROVED_IN_EMERGE : mondaysClaim ? APPROVED_MONDAYS_WORD : legend.caveat;
  const title = [caveat || legend.label, approvedHere ? null : by, mondaysClaim ? null : mondays].filter(Boolean).join(' — ');
  const sr = [caveat ? ` — ${caveat}` : '', by && !approvedHere ? (source === 'ADOPTED' ? ' (carried into eMerge at adoption)' : ' (recorded in eMerge)') : '',
    mondays && !mondaysClaim ? " (Monday's word)" : ''].join('');
  return (
    <FilledStatusChip word={word} hex={hex} label={legend.label} variant={mondaysClaim ? 'claim' : 'solid'} title={title}
      className={`um-chip${by ? ' native' : ''}${approvedHere ? ' approved' : ''}${mondaysClaim ? ' claim' : ''}`}
      suffix={approvedHere ? <span className="um-chip-by" aria-hidden="true"> · in eMerge</span>
        : mondaysClaim ? <span className="um-chip-by claim" aria-hidden="true"> · Monday&rsquo;s word</span> : null}
      srText={sr || undefined} />
  );
}

/**
 * The open correction's state beside the status word (Josie Evans, CORRECTIONS, 2026-10-28, R4): "open, to send" until
 * its corrected UMED went to DCF, then "sent, awaiting review" -- the words are the server's (readModel.correctionStatusOf).
 * A paid in full correction says so (director's ruling 1: a derived label, never the settled word).
 */
export function CorrectionStateChip({ status }: { status: CorrectionStatus | null | undefined }) {
  if (!status) return null;
  const sent = status.state === 'SENT';
  return (
    <span className={`um-tag ${sent ? 'ok' : 'attn'}`} data-testid="correction-state" data-state={status.state}
      title={sent && status.submitted_at ? `${status.label}. Last sent to DCF ${shortDate(status.submitted_at)}.` : status.label}>
      {status.label}
    </span>
  );
}
/** The legend colour PAID IN FULL CORRECTION wears (Monday's), lent to the derived label that stands in for SFS CORRECTION. */
const PIF_CORRECTION_HEX = '#007eb5';
/**
 * A paid in full correction requested before 2026-10-28 wears SFS CORRECTION, which was never true of it (ruling 1). The
 * row shows what it is in the word's place, and says what the record holds.
 */
export function PifCorrectionWordChip({ status }: { status: CorrectionStatus }) {
  return (
    <FilledStatusChip word="SFS CORRECTION" hex={PIF_CORRECTION_HEX} label="Paid in full correction" className="um-chip native"
      title="A paid in full correction. The status word on record is SFS CORRECTION, written before eMerge told the two apart."
      srText={` — ${status.label}. The status word on record is SFS CORRECTION.`} />
  );
}

/**
 * The disposition a case carries, as a word (2026-10-02).
 *
 * A tag, not a coloured dot: the word is always there, and the tone only
 * echoes it. Who recorded it is said in words too -- "recorded in eMerge" or
 * "the board's word" -- for a screen reader and on hover, because the two
 * behave differently on the next sync and an operator may need to know which
 * one she is looking at.
 */
const DISPOSITION_TONE: Record<CaseDisposition['word'], string> = {
  NO_LONGER_QUALIFIES: 'attn', NEEDS_MEDICAID_RECERT: 'attn', DECEASED: '', PAID_IN_FULL: 'ok', DCF_RESUBMISSION_NEEDED: 'attn', PROBLEM_ACCOUNT: 'attn',
};
/** The disposition chip would only repeat the status word on the row (its board word IS the word shown). */
export const dispositionRepeatsWord = (row: Pick<BoardRow, 'disposition' | 'source_status_raw'>): boolean => {
  const d = row.disposition;
  if (!d) return false;
  const w = DISPOSITION_STATUS_WORD[d.word as Disposition];
  return Boolean(w) && normalizeMondayWord(w) === normalizeMondayWord(row.source_status_raw);
};

export function DispositionChip({ disposition }: { disposition: CaseDisposition | null | undefined }) {
  if (!disposition) return null;
  const by = disposition.source === 'EMERGE' ? 'recorded in eMerge' : 'the board’s word';
  // Filled with the Monday colour of the board word the disposition IS (2026-10-06); the label stays eMerge's words.
  const hex = mondayHex(UMED_STATUS, DISPOSITION_STATUS_WORD[disposition.word as Disposition] || null);
  return (
    <FilledStatusChip word={disposition.word} hex={hex} label={disposition.label} size="sm" title={`${disposition.label} — ${by}`}
      className={`um-tag um-disposition ${DISPOSITION_TONE[disposition.word] || ''}`.trim()} srText={` (${by})`} />
  );
}

export function StatusLegend({ legend }: { legend: LegendEntry[] }) {
  const [open, setOpen] = useState(false);
  if (!legend.length) return null;
  return (
    <div className="um-legend">
      <button type="button" className="um-link" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        {open ? 'Hide' : 'Show'} the status legend
      </button>
      {open && (
        <ul className="um-legend-list" aria-label="Status legend">
          {legend.map((s) => (
            <li key={s.word}>
              {/* The chip exactly as the board shows it: Monday's fill, the word on it (2026-10-06). */}
              <FilledStatusChip word={s.word} hex={mondayHex(UMED_STATUS, s.word) || s.color} label={s.label} size="sm" />
              {/* The label is the board's word now (UM-R2); the word is repeated only where they differ. */}
              {s.label !== s.word && <span className="um-legend-word">{s.word}</span>}
              {s.caveat && <span className="um-legend-caveat">{s.caveat}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ── Sorting ──────────────────────────────────────────────────────────── */

/**
 * A sortable header. Sorting is a view control and changes no business state,
 * which the header says out loud through `aria-sort` rather than only through
 * a caret.
 */
function SortHead({ col, label, page, onSort, className, title }: {
  col: BoardSort | null; label: string; page: BoardPage; onSort: (s: BoardSort, d: 'asc' | 'desc') => void; className?: string; title?: string;
}) {
  if (!col || !page.sorts.includes(col)) return <th scope="col" className={className} title={title}>{label}</th>;
  const on = page.sort === col;
  const next = on && page.dir === 'asc' ? 'desc' : 'asc';
  return (
    <th scope="col" className={className} title={title} aria-sort={on ? (page.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
      <button type="button" className="um-sort" onClick={() => onSort(col, next)}>
        {label}<span className="um-caret" aria-hidden="true">{on ? (page.dir === 'asc' ? '▲' : '▼') : '↕'}</span>
      </button>
    </th>
  );
}

/**
 * The table's Sort control (Isaiah Cotto, 2026-10-14: "I would want the UMED table to default to most recent rather
 * than longest waiting, but make that a filterable option"). Most recent is the default and says so; Longest waiting
 * is the second choice; then the sorts of the columns this view shows (never a column the caller cannot see). The
 * column headers still sort too, and the control follows them. It names the order the server says the rows are in.
 */
export const NAMED_ORDER_LABEL: Record<'recent' | 'waiting', string> = { recent: 'Most recent', waiting: 'Longest waiting' };
export function SortControl({ page, columns, onSort }: {
  page: BoardPage; columns: Array<{ sort: BoardSort; label: string }>; onSort: (s: BoardSort, d: 'asc' | 'desc') => void;
}) {
  const named = (['recent', 'waiting'] as const).filter((k) => page.sorts.includes(k));
  const offered = columns.filter((c) => page.sorts.includes(c.sort) && !named.includes(c.sort as 'recent' | 'waiting'));
  // A server without the named orders (before 2026-10-14) has nothing truthful to name here.
  if (named.length === 0) return null;
  const defaultSort = page.default_sort || 'recent';
  // Null is a search across every case with no sort asked: its own order, open first then by name.
  const value = page.sort || (page.scope === 'EVERYWHERE' ? '' : defaultSort);
  return (
    <label className="um-sortpick">
      <span>Sort</span>
      <select aria-label="Sort the table" value={value}
        onChange={(e) => { const s = e.target.value as BoardSort; if (s) onSort(s, s === 'recent' ? 'desc' : 'asc'); }}>
        {value === '' && <option value="" disabled>Open first, by name (search)</option>}
        {named.map((k) => <option key={k} value={k}>{NAMED_ORDER_LABEL[k]}{k === defaultSort ? ' (default)' : ''}</option>)}
        {offered.map((c) => (
          <option key={c.sort} value={c.sort}>{c.label}{page.sort === c.sort ? (page.dir === 'desc' ? ', newest/highest first' : ', oldest/lowest first') : ''}</option>
        ))}
      </select>
    </label>
  );
}

/* ── The SSN, in the table and behind a click ─────────────────────────── */

/**
 * Isaiah, 2026-09-22: "We should make the SSN viewable when clicked on, not
 * necessarily out in the open. It should be in the table and clickable
 * because it is very important for the work they do."
 *
 * So the column is here, and the number is one click away. Four things make
 * that safe rather than merely tidy:
 *
 *   1. **The list payload never carries it.** The board route sends
 *      `ssn_on_file`, a boolean. There is no number in the response that
 *      draws this table, so no amount of scrolling, exporting or
 *      screen-scraping produces one.
 *   2. **Each reveal is its own audited request.** The server writes the
 *      audit row BEFORE it decrypts, naming the operator, the purpose and
 *      this case. Ten revealed rows are ten records of who looked and why.
 *   3. **It is never held.** The value lives in this cell's state and nowhere
 *      else — not in the page's data, not in storage, not in a URL. Moving to
 *      another tab or re-sorting unmounts the cell and the number is gone.
 *   4. **It hides again.** One click shows it, the next puts it away, and a
 *      re-reveal is a second audited request rather than a cached value —
 *      because the audit trail should say how many times it was actually
 *      looked at.
 */
/**
 * What the SERVER said about revealing on this screen: capability AND the
 * `reveal` write group, from GET /residents. `null` means we have not been
 * told yet.
 *
 * It is a context because the column table is a module constant — it cannot
 * close over state — and because a prop threaded through it would still be one
 * answer for the whole table. One request for the board, not one per row.
 */
const CanReveal = React.createContext<boolean | null>(null);

/**
 * `can` is what the SERVER said about revealing here: capability AND the
 * `reveal` write group, from GET /residents. `null` means we have not been
 * told yet.
 *
 * This used to offer the control unconditionally. On 2026-09-22 the `reveal`
 * group was closed in production, so the route did not exist, and every click
 * returned a 404 that this cell rendered as "Could not show it." — which told
 * the operator nothing and told us nothing either. A screen offers nothing it
 * has not been told it may do, and when a refusal does arrive it says which
 * refusal it was.
 */
function SsnCell({ row }: { row: BoardRow }) {
  const can = React.useContext(CanReveal);
  const [value, setValue] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // A different resident in this cell's position is a different secret.
  const [copied, setCopied] = useState(false);
  useEffect(() => { setValue(null); setErr(null); setCopied(false); }, [row.id]);

  if (!row.ssn_on_file) {
    return row.readiness_blockers.includes('SSN_REQUIREMENT')
      ? <span className="um-tag attn">Needed</span>
      : <span className="um-text none">Not on file</span>;
  }
  // On file, but not for this caller on this screen. Say that it exists — that
  // is a fact the operator needs — without dangling a control that cannot work.
  if (can === false) return <span className="um-text none" title="On file. Revealing it is not switched on for you here.">On file</span>;
  if (value) {
    return (
      <span className="um-ssn">
        <span className="um-mono">{value}</span>
        <CopyCell value={value} what={`the SSN for ${personName(row)}`} />
        <button type="button" className="um-link" onClick={() => setValue(null)} aria-label={`Hide the SSN for ${personName(row)}`}>Hide</button>
      </span>
    );
  }
  return (
    <span className="um-ssn">
      <button type="button" className="um-reveal" disabled={busy || !row.patient_id} aria-label={`Show the SSN for ${personName(row)}`} onClick={async () => {
        if (!row.patient_id) return;
        setBusy(true); setErr(null);
        try {
          const r = await residentsApi.revealSsn(row.patient_id, { purpose: 'UMED_PROCESSING', context_type: 'UMED_CASE', context_id: row.id });
          if (r.success && r.data) setValue(r.data.ssn);
          // Each refusal is a different thing to do about it: ask for the act
          // to be switched on, ask for access, or try again.
          else if (r.status === 404) setErr('Not switched on here.');
          else if (r.code === 'SSN_NOT_ON_FILE') setErr('Not on file.');
          else if (r.code === 'DENY_NO_CAPABILITY' || r.status === 403) setErr('Not permitted.');
          else setErr(r.message || 'Could not show it.');
        } catch { setErr('Could not reach the server.'); } finally { setBusy(false); }
      }}>{busy ? '…' : '•••-••-••••'}</button>
      {/*
        * Copy it without showing it.
        *
        * Josie Evans, 2026-09-29: "The S.S# is fine if it's hidden but I should
        * be able to still copy & paste it if I need to run it in FLMMIS. Right
        * now it's not letting me copy & paste."
        *
        * A clipboard copy IS a disclosure, so this goes through the SAME
        * audited reveal as the eye — same purpose, same case context, same
        * audit row. The only difference is that the digits reach her clipboard
        * instead of her screen, which is what she actually needs for FLMMIS and
        * is strictly less exposure than revealing it on a shared monitor.
        */}
      <button type="button" className="um-link" disabled={busy || !row.patient_id}
        aria-label={`Copy the SSN for ${personName(row)} without showing it`}
        title="Copies to the clipboard without showing it. Recorded in the access log, the same as showing it."
        onClick={async (ev) => {
          ev.stopPropagation();
          if (!row.patient_id) return;
          setBusy(true); setErr(null);
          try {
            const r = await residentsApi.revealSsn(row.patient_id, { purpose: 'UMED_PROCESSING', context_type: 'UMED_CASE', context_id: row.id });
            if (r.success && r.data) {
              await navigator.clipboard.writeText(r.data.ssn);
              setCopied(true);
              window.setTimeout(() => setCopied(false), 1400);
            } else if (r.status === 404) setErr('Not switched on here.');
            else if (r.code === 'SSN_NOT_ON_FILE') setErr('Not on file.');
            else if (r.code === 'DENY_NO_CAPABILITY' || r.status === 403) setErr('Not permitted.');
            else setErr(r.message || 'Could not copy it.');
          } catch { setErr('Could not copy it.'); } finally { setBusy(false); }
        }}>{copied ? 'Copied' : 'Copy'}</button>
      {err && <span className="um-text none" role="alert">{err}</span>}
    </span>
  );
}

/* ── Recording an outcome from the row ────────────────────────────────── */

/**
 * What this caller may record from a row, and how the board hears that it
 * changed. One answer for the table, like CanReveal, because the column set
 * is a module constant and cannot close over state.
 */
interface BoardActsValue {
  canReview: boolean;
  canCorrect: boolean;
  canMedicaid: boolean;
  /** Isaiah Cotto, 2026-09-28: anyone with UMED access may settle terms. */
  canTerms: boolean;
  /** 2026-10-06: record a sending Monday knows about and eMerge does not (umed.submission.prepare). */
  canMarkSubmitted: boolean;
  openCase: (id: string) => void;
  /** Gwynn, 2026-10-12: DCF EMAIL CREATED and the benefits link (umed.dcf_account.manage, write group `dcf-account`). */
  canDcf?: boolean;
  /** Open the case where the chosen act continues: Confirm DCF upload, or the DCF account form. */
  continueCase?: (id: string, kind: CaseFocus['kind']) => void;
  /** Attach a NOCA from the row (umed.document.write), 2026-09-28. */
  canDocument: boolean;
  /** The correction types the SERVER offers. SFS is the one Josie named. */
  sfs: boolean;
  /** Record a disposition (UMED_APPROVAL_REVIEW, write group `disposition`), 2026-10-02. */
  canDispose: boolean;
  /** UM-R2: start (2026-10-19), send and answer a DCF resubmittal from the row (umed.reversal.manage). */
  canReverse: boolean;
  /** The FLMMIS-issue correction type, when the server offers it. */
  flmmisIssue: boolean;
  /** The paid-in-full correction type, when the server offers it (2026-10-20, the "Paid in full" group). */
  pifCorrection?: boolean;
  recheckDays: number;
  reload: () => void;
  /**
   * Say what a row act did, in the board's one status line, re-read the board,
   * and put focus back on that row's name -- or on the line itself when the row
   * has left the tab (design verification 2026-10-06, P1-b). An act whose
   * control disappears on success must never leave focus on the page body.
   */
  announce: (msg: string, rowId: string, opts?: AnnounceOpts) => void;
  /** Processes 5 and 6: record a PL correction / benefits lost and answer a PL correction (`can.record_after_approval`). */
  canAfterApproval?: boolean;
  /** The server's colour legend (the two new words have no Monday hex) and its re-vet window. */
  legend?: LegendEntry[];
  revetDays?: number;
  /** 2026-10-20: a row approved here stays on the board, with what the approval did, until the board is left. */
  approvedInPlace?: (row: BoardRow, outcome: ApprovedOutcome) => void;
  /** The row was approved here: `kept` when the server no longer lists it in this view and the board shows its copy. */
  approvedHere?: (id: string) => { outcome: ApprovedOutcome; kept: boolean } | null;
  /** The Finance dossier for a resident, when this person can open Finance; null when they cannot. */
  financeHref?: (patientId: string) => string | null;
  /** Open a case's documents (umed.document.view, `documents_visible` in the scope): the NOCA / FLMMIS icons are links only with it. */
  canViewDocuments?: boolean;
}
/**
 * `keepFocus`: leave focus where it is (the control is still there and still has it).
 * `focusTo`: after the re-read, put focus HERE -- the control that opened the act (verification 2026-10-06,
 * P2-4) -- falling back to the row's name, then the status line, when that control is gone.
 */
interface AnnounceOpts { keepFocus?: boolean; focusTo?: () => HTMLElement | null }

/**
 * Focus back on the control that opened an in-row editor once it closes -- on Cancel, Escape, or after the act
 * (P2-4). The ref goes on the opener; `restore()` is called as the editor closes.
 */
function useReturnFocus<T extends HTMLElement>(open: boolean) {
  const opener = React.useRef<T | null>(null);
  const pending = React.useRef(false);
  useEffect(() => {
    if (!open && pending.current) { pending.current = false; opener.current?.focus(); }
  }, [open]);
  return { opener, restore: () => { pending.current = true; } };
}
export const BoardActs = React.createContext<BoardActsValue | null>(null);
/**
 * The cells around are a read-only copy (the row kept after an Approve here; second verification of #446). A disabled
 * fieldset turns off form controls but not every way in -- a file dropped on a drop zone is an event on a span -- so a
 * cell that writes by any other means asks this and offers nothing.
 */
export const ReadOnlyRow = React.createContext(false);

/**
 * What this caller may record, from the scope alone (2026-11-03): the one answer the table's row acts and the docked
 * record's "Set status" share, so the two can never offer different acts for one case. `canDo` already requires writes
 * to be NATIVE, so a process with the gate shut offers nothing.
 */
export function boardActsCapabilities(scope: Scope): Omit<BoardActsValue, 'openCase' | 'reload' | 'announce'> {
  const types = scope.correction_types || [];
  return {
    canReview: canDo(scope, 'review_approval'),
    canCorrect: canDo(scope, 'manage_corrections'),
    canMedicaid: canDo(scope, 'prepare_submission'),
    canTerms: canDo(scope, 'prepare_submission'),
    canMarkSubmitted: canDo(scope, 'prepare_submission'),
    canDcf: canDo(scope, 'manage_dcf_account'),
    canDocument: canDo(scope, 'add_document'),
    canViewDocuments: Boolean(scope.documents_visible),
    sfs: types.includes('SFS_CORRECTION'),
    canDispose: canDo(scope, 'record_disposition'),
    canReverse: canDo(scope, 'manage_reversals'),
    flmmisIssue: types.includes('SFS_CORRECTION_FLMMIS_ISSUE'),
    pifCorrection: types.includes('PAID_IN_FULL_CORRECTION'),
    recheckDays: scope.recheck_days || 10,
    canAfterApproval: canDo(scope, 'record_after_approval'),
    legend: scope.status_legend || [],
    revetDays: scope.after_approval?.revet_days,
  };
}

/**
 * Run `reset` when the row in this cell's position becomes ANOTHER case -- never on the first run (round 4): a
 * mount-time reset that is flushed late would close a grid or a drop highlight the operator had just opened.
 */
function useOnRowChange(id: string, reset: () => void) {
  const seen = React.useRef(id);
  useEffect(() => {
    if (seen.current === id) return;
    seen.current = id;
    reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
}

/**
 * Where an approval went, in plain words (2026-10-20): the Finance Kanban's own lane for a handoff Finance has not
 * received yet (financeBoard STATES `not-received`, Kanban column "New handoff").
 */
export const APPROVED_TO_FINANCE = 'Now in Finance → New handoff, waiting for Finance to receive.';
/** Ruling #1 (round 2): the episode already had its one handoff, and this approval is recorded on it -- no new one. */
export const APPROVED_ON_EXISTING = 'It is recorded on the Finance handoff this resident already has.';
/** The chip a row approved here carries until the board is left (2026-10-20). */
export const APPROVED_HERE_CHIP = 'Approved · Finance handoff open';
/** Ruling B: the same row when the claim opened NO handoff of its own (Finance already held the resident's), so it must not say one is open. */
export const APPROVED_HERE_CHIP_BLOCKED = 'Approved · second handoff not opened';
/** Beside the name of that row once the view no longer lists it. */
export const KEPT_NOTE = 'Approved here. No longer in this view, shown as it was: open the case to change anything. It goes when you move on.';

/** What the approve call answered, as far as the board needs it (POST /umed/cases/:id/approve). */
export interface ApprovedOutcome { replayed: boolean; handoff: 'OPENED' | 'ADOPTED' | 'OPEN' | 'BLOCKED' }
export const approvedChipOf = (o: ApprovedOutcome | null | undefined) => (o && o.handoff === 'BLOCKED' ? APPROVED_HERE_CHIP_BLOCKED : APPROVED_HERE_CHIP);
export const approvedOutcomeOf = (d: { replayed?: boolean; financeHandoffCreated?: boolean; financeHandoffAdopted?: boolean; financeHandoff?: string } | null | undefined): ApprovedOutcome => ({
  replayed: Boolean(d && d.replayed),
  handoff: d && d.financeHandoffCreated ? 'OPENED' : d && d.financeHandoffAdopted ? 'ADOPTED' : d && d.financeHandoff === FINANCE_HANDOFF_BLOCKED ? 'BLOCKED' : 'OPEN',
});
/**
 * Ruling B (Isaiah Cotto, 2026-10-30): a PORTFOLIO claim opens no second Finance handoff beside the resident's live one.
 * The server answers financeHandoff BLOCKED_EXISTING_HANDOFF and its own plain words (financeHandoffBlocked.message).
 */
export const FINANCE_HANDOFF_BLOCKED = 'BLOCKED_EXISTING_HANDOFF';
export const APPROVED_SECOND_HANDOFF_BLOCKED = 'No second Finance handoff was opened: Finance already holds this resident\'s handoff (this claim is SFS PORTFOLIO in Monday), and Finance is flagged to review it.';
/**
 * " <the server's words>" when an answer says ruling B blocked a second handoff, else "": appended to a success line
 * (the row's outcome panel, the case panel's DCF resubmittals). Falls back to the board's own sentence.
 */
export const blockedWords = (d: unknown): string => {
  const x = (d && typeof d === 'object' && 'data' in (d as Record<string, unknown>) ? (d as { data?: unknown }).data : d) as
    { financeHandoff?: string; financeHandoffBlocked?: { message?: string } } | null | undefined;
  if (!x || x.financeHandoff !== FINANCE_HANDOFF_BLOCKED) return '';
  return ` ${(x.financeHandoffBlocked && x.financeHandoffBlocked.message) || APPROVED_SECOND_HANDOFF_BLOCKED}`;
};

/** The approval act's one result sentence, whichever control made it (P2-6); 2026-10-20: it says where the case went. */
export const approvedSentence = (name: string, o: ApprovedOutcome) => {
  if (o.replayed) return `${name} was already approved.`;
  if (o.handoff === 'OPENED') return `${name} is approved. ${APPROVED_TO_FINANCE}`;
  if (o.handoff === 'ADOPTED') return `${name} is approved. ${APPROVED_ON_EXISTING}`;
  if (o.handoff === 'BLOCKED') return `${name} is approved. ${APPROVED_SECOND_HANDOFF_BLOCKED}`;
  return `${name} is approved. The Finance handoff is open.`;
};

/**
 * A row approved here stays on the board until the board is left (2026-10-20): the approval can move the case out of
 * the tab, and the re-read used to drop the row -- and the case panel open under it -- mid-act. `withApprovedKept`
 * puts each such row back where it was, as the copy taken at the approval, marked approved (the server answered
 * success, so that much is known; nothing else about the copy is re-derived). A row the server still lists is the
 * server's row, never the copy. Pure.
 */
export interface KeptApproval { row: BoardRow; index: number; outcome: ApprovedOutcome }
export function withApprovedKept(items: BoardRow[], kept: ReadonlyMap<string, KeptApproval>): { items: BoardRow[]; keptIds: Set<string> } {
  const keptIds = new Set<string>();
  if (!kept.size) return { items, keptIds };
  const out = items.slice();
  const listed = new Set(items.map((r) => r.id));
  Array.from(kept.values()).sort((a, b) => a.index - b.index).forEach((k) => {
    if (listed.has(k.row.id)) return;
    out.splice(Math.min(Math.max(k.index, 0), out.length), 0, k.row);
    keptIds.add(k.row.id);
  });
  return { items: out, keptIds };
}
/** The copy kept on the board: approved, so no evidence cell offers an edit and no Approve is offered again. */
const approvedCopy = (row: BoardRow): BoardRow => ({ ...row, approval: { ...row.approval, canonical: 'PROVEN', ready_to_approve: false } });

/** A row's evidence is kept as it was once the case is approved (verification 2026-10-06, P1-A). */
const isApproved = (row: Pick<BoardRow, 'approval'>) => row.approval.canonical === 'PROVEN';
export const APPROVED_LOCK = 'Approved: kept as recorded. To undo an approval, record a reversal or correction.';

/** Escape on a confirmation cancels it, like its Cancel button (P2-2). */
const escapeCancels = (cancel: () => void) => (e: React.KeyboardEvent) => {
  if (e.key === 'Escape') { e.stopPropagation(); cancel(); }
};

/**
 * Today where the OPERATOR is, never `toISOString().slice(0, 10)`.
 *
 * Eastern time is behind UTC, so after 20:00 ET that call returns tomorrow —
 * and a date field whose maximum is tomorrow is a date field that accepts a
 * follow-up nobody has made yet.
 */
export function todayLocal(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * The two approval facts as a REVIEW would state them.
 *
 * The case's vocabulary is wider than a review's: a case can be REJECTED,
 * EXCEPTION, or WITHHELD (withheld meaning this caller may not read it).
 * A review has no word for any of those, so those cases return null and the
 * quick controls stand down rather than carry a fact forward as something it
 * is not. Recording "not recorded" over a rejected NOCA would be eMerge
 * erasing a fact to keep a button working.
 */
/** The three answers a review can give, in the words the case detail uses. */
const NOCA_RESULT_WORDS: Record<NocaResult, string> = {
  NOT_RECORDED: 'Not recorded', PENDING: 'Pending', RECEIVED: 'Received',
};
const FLMMIS_RESULT_WORDS: Record<FlmmisResult, string> = {
  NOT_RECORDED: 'Not recorded', NOT_REFLECTED: 'Not reflected', REFLECTED: 'Reflected',
};

export const nocaForward = (n: Noca): NocaResult | null =>
  (n === 'RECEIVED' ? 'RECEIVED' : n === 'PENDING' ? 'PENDING' : n === 'UNKNOWN' ? 'NOT_RECORDED' : null);
const flmmisForward = (f: Flmmis): FlmmisResult | null =>
  (f === 'REFLECTED' ? 'REFLECTED' : f === 'NOT_REFLECTED' ? 'NOT_REFLECTED' : f === 'UNKNOWN' ? 'NOT_RECORDED' : null);

/**
 * Why FLMMIS cannot be recorded from the row, or null when it can. A review carries the NOCA as it stands, so it is
 * never written as "not recorded" over a NOCA a review cannot express (REJECTED). One rule for FlmmisCell and for the
 * APPROVED tile's first step, which records FLMMIS through the same writer (writeFlmmis).
 */
const flmmisBlocked = (row: Pick<BoardRow, 'approval'>): string | null => (nocaForward(row.approval.noca) === null
  ? `NOCA is ${NOCA_LABEL[row.approval.noca as keyof typeof NOCA_LABEL] || row.approval.noca}, which a review has no word for. Open the case to record the FLMMIS.`
  : null);

/** The lifecycles the approval act refuses (409 QUALIFICATION_SUSPENDED); readModel leaves ready_to_approve off for them too. */
const SUSPENDED_LIFECYCLES: readonly string[] = ['NO_LONGER_QUALIFIES', 'NEEDS_RECERT', 'DECEASED'];

/** The stages before a sending (approveCase moves each of them, with SUBMITTED, to APPROVED). */
const PRE_SENDING_STAGES: readonly string[] = ['CREATED', 'NEEDS_DCF_ACCOUNT', 'READY'];
type ApprovalRow = Pick<BoardRow, 'approval' | 'stage' | 'lifecycle'> & Partial<Pick<BoardRow, 'first_submission_id' | 'sent_to_dcf_on' | 'exception' | 'exception_open'>>;
/**
 * The UMED already went to DCF once, whatever stage the case is at now (2026-10-28): a sending on record, a DCF sending
 * date, or a DCF resubmission open (it exists only because DCF had it). Josie could not approve 3 cases at stage
 * CREATED with DCF RESUB NEEDED although the approval act accepts them -- the tiles were SUBMITTED-only.
 */
export const sentToDcfBefore = (row: Partial<Pick<BoardRow, 'first_submission_id' | 'sent_to_dcf_on' | 'exception' | 'exception_open'>>): boolean =>
  Boolean(row.first_submission_id || row.sent_to_dcf_on || (row.exception === 'RESUBMISSION' && row.exception_open));
/** Where an approval tile may be offered: sent to DCF and waiting, or an earlier stage of a UMED that already went (above). */
export const approvalStageOpen = (row: ApprovalRow): boolean => row.stage === 'SUBMITTED'
  || (PRE_SENDING_STAGES.includes(row.stage) && sentToDcfBefore(row));

/**
 * APPROVED in the status grid (Josie Evans, 2026-10-09: "I need an approval button"; Isaiah Cotto, 2026-10-10:
 * "UNDER REVIEW FOR APPROVALS ... 'APPROVED' - GOLD").
 *
 * Offered to an approval reviewer wherever the server offers the approval (ready_to_approve), and on every case sent
 * to DCF and waiting (stage SUBMITTED) that nobody has approved yet. Approval is NOCA received AND FLMMIS reflected on
 * record AND her explicit act (Kaleigh Vazquez & Josie Evans, "PA Sale to Approval - IF THIS THEN THAT", 2026-10-08,
 * Process 2 Step 3 and APR 03; Isaiah Cotto's ruling of 2026-10-13), so a case without both on record opens the two
 * steps (ApproveInTwoSteps): record NOCA received and FLMMIS reflected in one review, then approve, each confirmed on
 * its own. A missing NOCA never hides the tile: the first step collects it. Never a tile that only opens to say no,
 * so it is NOT offered where either step would be refused:
 *   - the case is closed, approved, or its qualification is suspended (the approval act refuses those);
 *   - the evidence cannot be recorded from the row (flmmisBlocked: a NOCA a review has no word for, such as
 *     REJECTED, is never written over from here; WITHHELD is not this viewer's to see);
 *   - the viewer's approval scope does not cover the building (`in_approval_scope`, the server's own predicate;
 *     both the review and the approval would answer 403 NOT_IN_APPROVAL_SCOPE). A row without the field is read
 *     as out of scope. `ready_to_approve` is computed with that same predicate, so a ready row is in scope;
 *   - both facts are already on record and the server still does not offer the approval (by readModel's formula,
 *     the scope again).
 */
export function approvalOffered(row: ApprovalRow, canReview: boolean): boolean {
  if (!canReview || isApproved(row) || row.stage === 'CLOSED') return false;
  if (row.approval.ready_to_approve) return true;
  /*
   * The evidence-first path is for the Review for Approvals population only: a case sent to DCF and waiting on its
   * answer (verification of #373, P2-2). Never a case never sent (CREATED, NEEDS_DCF_ACCOUNT, READY with no earlier
   * sending: 2026-10-28, approvalStageOpen), and never a historical row Monday calls APPROVED or HANDED_TO_FINANCE
   * without the act -- recording approval evidence there is not what the tile is for.
   */
  if (!approvalStageOpen(row)) return false;
  if (SUSPENDED_LIFECYCLES.includes(row.lifecycle || '')) return false;
  // The evidence step is offered only where the server would take it from this viewer.
  if (row.approval.in_approval_scope !== true) return false;
  if (row.approval.flmmis === 'WITHHELD') return false;
  if (approvalEvidenceOnRecord(row)) return false;
  return flmmisBlocked(row) === null;
}

/**
 * Why no APPROVED tile is on a row the reviewer would expect one on, in plain words, or null (2026-10-28: never hide it
 * silently). Only for an approval reviewer, on a case not approved, not closed or suspended, that went to DCF
 * (approvalStageOpen), where the tile is not offered and the evidence is not both on record -- the review has to come
 * first, in the case (the row cannot record it: a NOCA a review has no word for, or out of this viewer's reach).
 */
export function approvalWhyNotNote(row: ApprovalRow, canReview: boolean): string | null {
  if (!canReview || isApproved(row) || row.stage === 'CLOSED' || SUSPENDED_LIFECYCLES.includes(row.lifecycle || '')) return null;
  if (!approvalStageOpen(row) || approvalOffered(row, canReview) || row.approval.ready_to_approve) return null;
  if (approvalEvidenceOnRecord(row)) return null;
  const missing = approvalEvidenceMissing(row);
  return `Record the review first: ${missing.length ? missing.join(' and ') : 'NOCA received and FLMMIS reflected'}.`;
}

/** Both facts the approval act requires are on record: NOCA received and FLMMIS reflected (2026-10-13). */
export const approvalEvidenceOnRecord = (row: Pick<BoardRow, 'approval'>): boolean =>
  row.approval.noca === 'RECEIVED' && row.approval.flmmis === 'REFLECTED';

/** What the approval still needs on record, in words: "NOCA received", "FLMMIS reflected", or both. Empty when both are. */
export function approvalEvidenceMissing(row: Pick<BoardRow, 'approval'>): string[] {
  const out: string[] = [];
  if (row.approval.noca !== 'RECEIVED') out.push('NOCA received');
  if (row.approval.flmmis !== 'REFLECTED') out.push('FLMMIS reflected');
  return out;
}

/**
 * Whether the reviewer can record NOCA received from the row on a case whose FLMMIS is already reflected (the partial
 * approval's first step): NOCA not received yet, and a value a review can carry (never written over REJECTED).
 */
const nocaRecordable = (row: Pick<BoardRow, 'approval'>): boolean =>
  row.approval.noca !== 'RECEIVED' && nocaForward(row.approval.noca) !== null;

/**
 * APPROVED - SFS CORRECTION (Kaleigh Vazquez & Josie Evans, 2026-10-08, PAR 01; Isaiah Cotto's ruling of 2026-10-13):
 * FLMMIS shows the new patient liability for only some of the months. Offered next to APPROVED to an approval reviewer
 * on a case sent to DCF (SUBMITTED) whose FLMMIS is reflected: where the server offers the approval
 * (`ready_to_approve`: NOCA received and FLMMIS reflected, not approved, not suspended, in her approval scope), and --
 * 2026-10-13, the approval needs the NOCA too -- where only the NOCA is missing and she may record it (in her scope, a
 * NOCA a review can carry): the panel records NOCA received first. Never on any other case: there is no FLMMIS-first
 * path for it.
 */
export function partialApprovalOffered(row: ApprovalRow, canReview: boolean): boolean {
  // 2026-10-28: also at an earlier stage of a UMED that already went to DCF (approvalStageOpen), as approvePartially accepts.
  if (!canReview || isApproved(row) || !approvalStageOpen(row)) return false;
  if (SUSPENDED_LIFECYCLES.includes(row.lifecycle || '')) return false;
  if (row.approval.flmmis !== 'REFLECTED') return false;
  if (row.approval.ready_to_approve === true) return true;
  return row.approval.in_approval_scope === true && nocaRecordable(row);
}

/**
 * Verification 2, V2: the row's open correction is an open PL correction's -- it completes through PL CORRECTION OUTCOME
 * (which also resolves the PL correction), never through SFS CORRECTION COMPLETED (the server refuses it).
 */
export function plCorrectionHoldsCorrection(row: Pick<BoardRow, 'after_approval' | 'correction'>): boolean {
  const pl = row.after_approval?.pl_correction;
  return Boolean(pl && pl.state === 'OPEN' && row.correction && pl.correction_id === row.correction.id);
}

/** PAR 03: an APPROVED - SFS CORRECTION case whose correction is still open -- its APPROVED tile completes it. */
export function partialApprovalOpen(row: Pick<BoardRow, 'partial_approval' | 'stage'>): boolean {
  return Boolean(row.partial_approval && row.partial_approval.correction_open) && row.stage !== 'CLOSED';
}

/** What the partial approval does, said before it is done (PAR 01). */
export const PARTIAL_APPROVE_EFFECT = (months: string) => `Finance can collect the approved months now; a correction opens for ${months || 'the months you name'}; the case stays in New Approvals - NOCA until FLMMIS shows the correction. The status becomes ${PARTIAL_APPROVAL_WORD}.`;
/** What its completion does (PAR 03). */
export const PARTIAL_COMPLETE_EFFECT = (name: string, months: string) => `FLMMIS now shows ${months || 'the corrected months'} for ${name}: the SFS correction is completed and recorded on the same Finance handoff (no second handoff), and the status becomes APPROVED. The case leaves New Approvals - NOCA; Finance keeps it.`;
/** The NOCA gate (2026-10-13) in the partial panels: the step that records it, and the refusal that puts her back on it. */
export const PARTIAL_NOCA_FIRST = 'The NOCA is not on record, and approval needs NOCA received with FLMMIS reflected. Record NOCA received first (FLMMIS stays reflected); this alone approves nothing.';
export const PARTIAL_COMPLETE_NOCA_FIRST = 'The NOCA is not on record, and APPROVED needs it. Record NOCA received first; this alone changes nothing else.';
export const NOCA_NOT_ON_RECORD = 'NOCA received is not on record. Record it first; nothing was approved.';

const MONTH_INPUT_RE = /^\d{4}-(0[1-9]|1[0-2])$/;

/**
 * APPROVED - SFS CORRECTION, from the status grid (PAR 01). The month(s) FLMMIS does not reflect yet (one at least,
 * picked as months), an optional note, and the consequence written above the button. One POST: the server opens the
 * approval, its ONE Finance handoff and the SFS correction together, or none of them.
 */
export function PartialApprovalPanel({ row, onDone, onCancel }: {
  row: Pick<BoardRow, 'id' | 'first_name' | 'last_name' | 'approval' | 'stage'>;
  onDone: (msg: string) => void;
  onCancel: () => void;
}) {
  const [months, setMonths] = useState<string[]>([]);
  const [pick, setPick] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // 2026-10-13: the approval needs the NOCA on record too. Missing, it is recorded first (one review, with FLMMIS as reflected).
  const [nocaDone, setNocaDone] = useState(row.approval.noca === 'RECEIVED');
  // A second click in the same tick, before `busy` has rendered, is not a second act.
  const inFlight = React.useRef(false);
  const name = personName(row);
  const recordNoca = async () => {
    if (inFlight.current || busy || nocaDone) return;
    inFlight.current = true;
    setBusy(true); setErr(null);
    try {
      const refused = await writeApprovalEvidence(row);
      if (refused) { setErr(refused); return; }
      setNocaDone(true);
    } catch { setErr('Could not reach the server. Nothing was recorded.'); } finally { inFlight.current = false; setBusy(false); }
  };
  const add = () => {
    const m = pick.trim();
    if (!MONTH_INPUT_RE.test(m)) { setErr('Pick a month (YYYY-MM).'); return; }
    setErr(null);
    setMonths((was) => (was.includes(m) ? was : [...was, m].sort()));
    setPick('');
  };
  const save = async () => {
    if (inFlight.current || busy || months.length === 0 || !nocaDone) return;
    inFlight.current = true;
    setBusy(true); setErr(null);
    try {
      const r = await umedApi.approvePartially(row.id, { missing_months: months, ...(note.trim() ? { note: note.trim() } : {}) });
      if (!r.success) {
        if (r.code === 'NOCA_REQUIRED') { setNocaDone(false); setErr(NOCA_NOT_ON_RECORD); return; }
        setErr(r.code === 'FLMMIS_REQUIRED' ? 'Record FLMMIS reflected first.' : refusal(r)); return;
      }
      onDone(r.data?.replayed
        ? `${name} was already approved with an SFS correction open. Nothing changed.`
        : `${name}: ${PARTIAL_APPROVAL_WORD}. Finance can collect the approved months; a correction is open for ${monthsLabel(months)}.`);
    } catch { setErr('Could not reach the server. Nothing was recorded.'); } finally { inFlight.current = false; setBusy(false); }
  };
  return (
    <div className="um-rowpanel um-confirm" role="group" aria-label={`${PARTIAL_APPROVAL_WORD} for ${name}`}
      onClick={(e) => e.stopPropagation()} onKeyDown={busy ? undefined : escapeCancels(onCancel)}>
      <p className="um-rowpanel-what">{PARTIAL_APPROVE_EFFECT(monthsLabel(months))}</p>
      {!nocaDone && (
        <div className="um-step">
          <p className="um-step-what">{PARTIAL_NOCA_FIRST}</p>
          <button type="button" className="um-open um-primary" aria-disabled={busy} onClick={recordNoca}>
            {busy ? 'Recording…' : 'Record NOCA received'}
          </button>
        </div>
      )}
      <div className="um-rowfield"><span>Month(s) FLMMIS does not show yet</span>
        <span>
          <input type="month" value={pick} disabled={busy} max={todayLocal().slice(0, 7)}
            onChange={(e) => setPick(e.target.value)} aria-label="A month FLMMIS does not show yet" />
          <button type="button" className="um-link" disabled={busy || !pick} onClick={add}>Add month</button>
        </span>
      </div>
      {months.length > 0 && (
        <ul className="um-chips" aria-label="Months awaiting the correction">
          {months.map((m) => (
            <li key={m}>{monthsLabel([m])}{' '}
              <button type="button" className="um-link" disabled={busy} aria-label={`Remove ${monthsLabel([m])}`}
                onClick={() => setMonths((was) => was.filter((x) => x !== m))}>×</button>
            </li>
          ))}
        </ul>
      )}
      <label className="um-rowfield"><span>Note</span>
        <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={2000} disabled={busy}
          placeholder="What FLMMIS shows -- kept with the correction" aria-label="Note for the correction" />
      </label>
      <Why id={`why-partial-${row.id}`} className="um-sub" reason={firstReason([!nocaDone, 'Record NOCA received first (the button above).'], [months.length === 0, 'Name at least one month FLMMIS does not show yet.'])} />
      {err && <p className="um-notice stop" role="alert">{err}</p>}
      <div className="um-rowpanel-acts">
        <button type="button" className="um-open um-primary" disabled={months.length === 0 || !nocaDone} aria-disabled={busy} aria-describedby={months.length === 0 || !nocaDone ? `why-partial-${row.id}` : undefined} onClick={save}>
          {busy ? 'Recording…' : `Record ${PARTIAL_APPROVAL_WORD}`}
        </button>
        <button type="button" className="um-link" disabled={busy} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

/**
 * APPROVED on an APPROVED - SFS CORRECTION case (PAR 03): FLMMIS now shows the corrected months. One POST: the
 * correction completes on the same Finance handoff and the status becomes APPROVED. The day FLMMIS showed it defaults
 * to today and is never in the future.
 */
export function PartialCompletePanel({ row, onDone, onCancel }: {
  row: Pick<BoardRow, 'id' | 'first_name' | 'last_name' | 'partial_approval' | 'approval'>;
  onDone: (msg: string) => void;
  onCancel: () => void;
}) {
  const [on, setOn] = useState(todayLocal());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  /*
   * 2026-10-13: APPROVED needs the NOCA on record (PAR 01 received it). A partial approval made before the NOCA gate
   * may not have it: the case is approved, so it is recorded with the after-approval NOCA act (recordLateNoca) first.
   * WITHHELD is not this viewer's to judge; the server answers for it.
   */
  const [nocaDone, setNocaDone] = useState(row.approval?.noca === 'RECEIVED' || row.approval?.noca === 'WITHHELD');
  const inFlight = React.useRef(false);
  const name = personName(row);
  const months = monthsLabel(row.partial_approval?.missing_months || []);
  const recordNoca = async () => {
    if (inFlight.current || busy || nocaDone) return;
    inFlight.current = true;
    setBusy(true); setErr(null);
    try {
      const r = await umedApi.recordLateNoca(row.id, {});
      if (!r.success) { setErr(refusal(r)); return; }
      setNocaDone(true);
    } catch { setErr('Could not reach the server. Nothing was recorded.'); } finally { inFlight.current = false; setBusy(false); }
  };
  const save = async () => {
    if (inFlight.current || busy || !nocaDone) return;
    inFlight.current = true;
    setBusy(true); setErr(null);
    try {
      const r = await umedApi.completePartialApproval(row.id, { reflected_on: on });
      if (!r.success) {
        if (r.code === 'NOCA_REQUIRED') { setNocaDone(false); setErr(NOCA_NOT_ON_RECORD); return; }
        setErr(refusal(r)); return;
      }
      onDone(r.data?.replayed ? `${name} was already APPROVED. Nothing changed.` : `${name} is APPROVED: FLMMIS shows ${months || 'the corrected months'}.`);
    } catch { setErr('Could not reach the server. Nothing was recorded.'); } finally { inFlight.current = false; setBusy(false); }
  };
  return (
    <div className="um-rowpanel um-confirm" role="group" aria-label={`Approve ${name}`}
      onClick={(e) => e.stopPropagation()} onKeyDown={busy ? undefined : escapeCancels(onCancel)}>
      <p className="um-rowpanel-what">{PARTIAL_COMPLETE_EFFECT(name, months)}</p>
      {!nocaDone && (
        <div className="um-step">
          <p className="um-step-what">{PARTIAL_COMPLETE_NOCA_FIRST}</p>
          <button type="button" className="um-open um-primary" aria-disabled={busy} autoFocus onClick={recordNoca}>
            {busy ? 'Recording…' : 'Record NOCA received'}
          </button>
        </div>
      )}
      <label className="um-rowfield"><span>The day FLMMIS showed it</span>
        <input type="date" value={on} max={todayLocal()} disabled={busy}
          onChange={(e) => setOn(e.target.value)} aria-label="The day FLMMIS showed the corrected months" />
      </label>
      {err && <p className="um-notice stop" role="alert">{err}</p>}
      <div className="um-rowpanel-acts">
        <button type="button" className="um-open um-primary" disabled={!nocaDone} aria-disabled={busy} autoFocus={nocaDone} aria-describedby={!nocaDone ? `why-partial-complete-${row.id}` : undefined} onClick={save}>{busy ? 'Approving…' : 'Approve'}</button>
        <Why id={`why-partial-complete-${row.id}`} reason={!nocaDone && 'Record NOCA received first (the button above).'} className="um-text um-why" />
        <button type="button" className="um-link" disabled={busy} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

/** What sending the correction again does, said before it is done (2026-10-28). */
export const CORRECTION_RESEND_EFFECT = 'Opens the correction again: Gwynn is told the corrected UMED must be prepared again and faxed to DCF, and a new packet revision is prepared. The status goes back to SFS CORRECTION; when the new revision is sent, it is SFS CORRECTION SUBMITTED again and your 10-day review starts over. The correction stays the same one: nothing goes to Finance.';

/**
 * SFS CORRECTION · SEND AGAIN (Josie Evans, 2026-10-28): the month(s) DCF still does not show (one at least, as the
 * partial approval picks them) and the reason, then one POST. The server re-checks everything: sent since opened, no fax
 * in flight, the correction still open. A retry of the same press is the same request (its key).
 */
export function CorrectionResendPanel({ row, onDone, onCancel }: {
  row: Pick<BoardRow, 'id' | 'first_name' | 'last_name' | 'correction'>;
  onDone: (msg: string) => void;
  onCancel: () => void;
}) {
  const [months, setMonths] = useState<string[]>([]);
  const [pick, setPick] = useState('');
  const [reason, setReason] = useState('');
  // The note for Gwynn on this re-send (2026-11-03, SCRUM-131): optional, its own note for this sending.
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [key] = useState(() => freshKey());
  const inFlight = React.useRef(false);
  const name = personName(row);
  const why = reason.trim();
  const forGwynn = note.trim();
  const noteOk = forGwynn.length === 0 || (forGwynn.length >= 3 && forGwynn.length <= 2000);
  /*
   * Josie, 2026-11-03: October 2026 was in the picker, "Add month" not pressed, and the button stayed grey with "Name at
   * least one month". A whole month in the picker counts as picked: it is listed with the others at once, counted for the
   * button and sent. "Add month" keeps it and clears the picker for another. Not added on change, so a year typed digit
   * by digit (0002, 0020, 0202, 2026) shows each passing value as the pending chip but keeps none of them: only the
   * value in the picker at submit is sent, and the server refuses an out-of-range year.
   */
  const picked = pick.trim();
  const pending = MONTH_INPUT_RE.test(picked) && !months.includes(picked) ? picked : null;
  const chosen = pending ? [...months, pending].sort() : months;
  const ready = chosen.length > 0 && why.length >= 3 && noteOk;
  const add = () => {
    const m = pick.trim();
    if (!MONTH_INPUT_RE.test(m)) { setErr('Pick a month (YYYY-MM).'); return; }
    setErr(null);
    setMonths((was) => (was.includes(m) ? was : [...was, m].sort()));
    setPick('');
  };
  const save = async () => {
    if (inFlight.current || busy || !ready || !row.correction) return;
    inFlight.current = true;
    setBusy(true); setErr(null);
    try {
      const r = await umedApi.resendCorrection(row.correction.id, { months: chosen, reason: why, ...(forGwynn ? { note: forGwynn } : {}), idempotency_key: key });
      if (!r.success) { setErr(refusal(r)); return; }
      onDone(r.data?.replayed
        ? `${name}: the SFS correction was already asked to be submitted. Nothing changed.`
        : `${name}: the SFS correction is to be submitted to DCF for ${monthsLabel(chosen)}. Gwynn is told${r.data?.noteId ? ', with your note' : ''}; the status is SFS CORRECTION.`);
    } catch { setErr('Could not reach the server. Nothing was recorded.'); } finally { inFlight.current = false; setBusy(false); }
  };
  return (
    <div className="um-rowpanel um-confirm" role="group" aria-label={`Submit the SFS correction to DCF for ${name}`}
      onClick={(e) => e.stopPropagation()} onKeyDown={busy ? undefined : escapeCancels(onCancel)}>
      <p className="um-rowpanel-what"><strong>Submit the SFS correction to DCF.</strong> {CORRECTION_RESEND_EFFECT}</p>
      <div className="um-rowfield"><span>Month(s) DCF still does not show</span>
        <span>
          <input type="month" value={pick} disabled={busy} onChange={(e) => setPick(e.target.value)} aria-label="A month DCF still does not show" />
          <button type="button" className="um-link" disabled={busy || !pick} onClick={add}>Add month</button>
        </span>
      </div>
      {chosen.length > 0 && (
        <ul className="um-chips" aria-label="Months still not reflecting">
          {chosen.map((m) => (
            <li key={m}>{monthsLabel([m])}{' '}
              <button type="button" className="um-link" disabled={busy} aria-label={`Remove ${monthsLabel([m])}`}
                onClick={() => {
                  // The same month still in the picker would come straight back as the pending chip: clear it too.
                  if (m === pending || m === picked) setPick('');
                  setMonths((was) => was.filter((x) => x !== m));
                }}>×</button>
            </li>
          ))}
        </ul>
      )}
      <label className="um-rowfield"><span>Reason</span>
        <input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={2000} disabled={busy}
          placeholder="What DCF or FLMMIS still shows -- kept with the correction" aria-label="Why the SFS correction is submitted" />
      </label>
      <label className="um-rowfield"><span>Note for Gwynn (optional)</span>
        <textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={2000} rows={3} disabled={busy}
          placeholder="What Gwynn needs to send this time -- she is told with it" aria-label="Note for Gwynn with this re-send" />
      </label>
      <Why id={`why-resend-${row.id}`} className="um-sub" reason={firstReason([chosen.length === 0, 'Name at least one month DCF still does not show.'], [why.length < 3, 'Say why it is submitted (at least 3 characters).'], [!noteOk, 'A note for Gwynn is at least 3 characters, or leave it empty.'])} />
      {err && <p className="um-notice stop" role="alert">{err}</p>}
      <div className="um-rowpanel-acts">
        <button type="button" className="um-open um-primary" disabled={!ready} aria-disabled={busy} aria-describedby={!ready ? `why-resend-${row.id}` : undefined} onClick={save}>
          {busy ? 'Recording…' : 'Submit the SFS correction'}
        </button>
        <button type="button" className="um-link" disabled={busy} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

/*
 * "Already sent to DCF outside eMerge" (Josie Evans / Isaiah Cotto, Teams, 2026-10-29): a correction whose corrected UMED
 * went to DCF before eMerge carried it (Monday era) has no sending here, so the row's completion is greyed. This is the
 * existing attested completion (#543: completeCorrection with sent_outside_emerge, the case panel's "Complete it anyway"),
 * reached from the greyed tile's explanation behind its own confirmation: the box ticked, the day it went, and how --
 * kept in the completion evidence. It completes only what DCF now shows; one DCF does not show yet is sent again by
 * recording its terms so Gwynn can send it (the panel says so first).
 */
export const OUTSIDE_SENT_CONFIRM = 'I know this correction already went to DCF outside eMerge, and DCF now shows it.';
export const OUTSIDE_SENT_NOT_YET = 'Only when DCF now shows the correction. If DCF still does not show it, do not complete it: record the terms on this case so Gwynn can send it again.';
export const CORRECTION_OUTSIDE_LABEL = 'Already sent to DCF outside eMerge';
/** How it was sent: the server keeps 3 to 500 characters (OUTSIDE_EVIDENCE_INVALID otherwise). */
export const OUTSIDE_NOTE_MAX = 500;
export function CorrectionOutsidePanel({ row, onDone, onCancel }: {
  row: BoardRow;
  onDone: (msg: string) => void;
  onCancel: () => void;
}) {
  const [on, setOn] = useState('');
  const [how, setHow] = useState('');
  const [sure, setSure] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const inFlight = React.useRef(false);
  const name = personName(row);
  const label = outcomeLabel('CORRECTION_COMPLETE', row);
  const word = outcomeWord('CORRECTION_COMPLETE', row);
  const today = todayLocal();
  const dayOk = /^\d{4}-\d{2}-\d{2}$/.test(on) && on <= today;
  const note = how.replace(/\s+/g, ' ').trim();
  // The server's own rule (OUTSIDE_EVIDENCE_INVALID, verification of #558): 3 to 500 characters.
  const ready = sure && dayOk && note.length >= 3 && note.length <= OUTSIDE_NOTE_MAX;
  const save = async () => {
    if (inFlight.current || busy || !ready || !row.correction) return;
    inFlight.current = true;
    setBusy(true); setErr(null);
    try {
      const r = await umedApi.completeCorrection(row.correction.id, { sentOutsideEmerge: true, evidence: { sentOutsideOn: on, note } });
      if (!r.success) { setErr(refusal(r)); return; }
      onDone(r.data?.alreadyCompleted
        ? `${name}: this correction was already completed. Nothing changed.`
        : `${label} recorded for ${name}, sent to DCF outside eMerge on ${on}. ${correctionCompletedWords(r.data)}`);
    } catch { setErr('Could not reach the server. Nothing was recorded.'); } finally { inFlight.current = false; setBusy(false); }
  };
  return (
    <div className="um-rowpanel um-confirm" role="group" aria-label={`Complete the correction sent outside eMerge for ${name}`}
      onClick={(e) => e.stopPropagation()} onKeyDown={busy ? undefined : escapeCancels(onCancel)}>
      <p className="um-rowpanel-what"><strong>{CORRECTION_OUTSIDE_LABEL}?</strong> {OUTSIDE_SENT_NOT_YET}</p>
      <p className="um-rowpanel-what">
        {isPifCorrection(row) ? PIF_CORRECTION_COMPLETE_EFFECT : correctionCompleteEffect(isApproved(row))} The status becomes {word}.
      </p>
      <label className="um-rowfield"><span>The day it went to DCF</span>
        <input type="date" value={on} max={today} disabled={busy} required
          onChange={(e) => setOn(e.target.value)} aria-label="The day the correction went to DCF outside eMerge" />
      </label>
      <label className="um-rowfield"><span>How it was sent</span>
        <textarea value={how} onChange={(e) => setHow(e.target.value)} maxLength={OUTSIDE_NOTE_MAX} rows={3} disabled={busy} required
          placeholder="Fax or upload, who sent it, and what shows it went" aria-label="How the correction was sent outside eMerge" />
      </label>
      <label className="um-rowcheck">
        <input type="checkbox" checked={sure} disabled={busy} onChange={(e) => setSure(e.target.checked)} /> {OUTSIDE_SENT_CONFIRM}
      </label>
      <Why id={`why-outside-${row.id}`} className="um-sub" reason={!ready && firstReason(
        [!on, 'Enter the day it went to DCF.'],
        [!dayOk, 'The day it went cannot be in the future.'],
        [note.length < 3, 'Say how it was sent (at least 3 characters).'],
        [note.length > OUTSIDE_NOTE_MAX, `Keep how it was sent to ${OUTSIDE_NOTE_MAX} characters.`],
        [!sure, 'Tick the box to confirm it went to DCF.'],
      )} />
      {err && <p className="um-notice stop" role="alert">{err}</p>}
      <div className="um-rowpanel-acts">
        <button type="button" className="um-open um-primary" disabled={!ready} aria-disabled={busy} aria-describedby={!ready ? `why-outside-${row.id}` : undefined} onClick={save}>
          {busy ? 'Recording…' : `Record ${label}`}
        </button>
        <button type="button" className="um-link" disabled={busy} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

/*
 * "Add a note for Gwynn" (Josie Evans, Teams, 2026-10-29: "It's supposed to be an SFS correction. I need to put a note in
 * for her to send."): on any open correction, the note the request would have carried (POST /umed/corrections/:id/note;
 * the same COR_NOTE as a paid in full correction's). One per correction, kept as written; the server refuses a second.
 */
export const CORRECTION_NOTE_EFFECT = 'Puts your note for Gwynn on this correction: she is told now and once more in 14 days unless the correction is completed. One note each time it is sent (the request, then each SFS CORRECTION · SUBMIT), kept as written; the latest shows on the row, the Kanban card and the case.';
export function CorrectionNotePanel({ row, onDone, onCancel }: {
  row: Pick<BoardRow, 'id' | 'first_name' | 'last_name' | 'correction'>;
  onDone: (msg: string) => void;
  onCancel: () => void;
}) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const inFlight = React.useRef(false);
  const name = personName(row);
  const note = text.trim();
  const ready = note.length >= 3 && note.length <= 2000;
  const save = async () => {
    if (inFlight.current || busy || !ready || !row.correction) return;
    inFlight.current = true;
    setBusy(true); setErr(null);
    try {
      const r = await umedApi.addCorrectionNote(row.correction.id, note);
      if (!r.success) { setErr(refusal(r)); return; }
      onDone(r.data?.replayed
        ? `${name}: that note was already on the correction. Nothing changed.`
        : `${name}: the note for Gwynn is on the correction. ${(r.data?.told || 0) > 0
          ? 'Gwynn is told now, and once more in 14 days unless it is completed.'
          : 'Nobody who makes packets for this building was told: tell Gwynn yourself.'}`);
    } catch { setErr('Could not reach the server. Nothing was recorded.'); } finally { inFlight.current = false; setBusy(false); }
  };
  return (
    <div className="um-rowpanel um-confirm" role="group" aria-label={`Add a note for Gwynn for ${name}`}
      onClick={(e) => e.stopPropagation()} onKeyDown={busy ? undefined : escapeCancels(onCancel)}>
      <p className="um-rowpanel-what"><strong>{CORRECTION_NOTE_LABEL}.</strong> {CORRECTION_NOTE_EFFECT}</p>
      <label className="um-rowfield"><span>Note for Gwynn</span>
        <textarea value={text} onChange={(e) => setText(e.target.value)} maxLength={2000} rows={4} disabled={busy}
          placeholder="What Gwynn needs to send to DCF: the months, the amount, anything else" aria-label="The note for Gwynn" />
      </label>
      {err && <p className="um-notice stop" role="alert">{err}</p>}
      <div className="um-rowpanel-acts">
        <button type="button" className="um-open um-primary" disabled={!ready} aria-disabled={busy} aria-describedby={!ready ? `why-gwynn-note-${row.id}` : undefined} onClick={save}>
          {busy ? 'Recording…' : 'Send the note to Gwynn'}
        </button>
        <Why id={`why-gwynn-note-${row.id}`} reason={!ready && firstReason([note.length < 3, 'Write the note for Gwynn (at least 3 characters).'], [true, 'Keep the note to 2000 characters.'])} className="um-text um-why" />
        <button type="button" className="um-link" disabled={busy} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

/** The open correction's note for Gwynn on the row (2026-10-29), as the Kanban card and the case show it. */
function CorrectionNoteLine({ correction }: { correction: BoardRow['correction'] }) {
  if (!correction) return null;
  if (correction.note) {
    return <span className="um-quiet um-wordonly um-cornote" data-testid="row-correction-note" title={correction.note}><strong>Note for Gwynn:</strong> {correction.note}</span>;
  }
  if (correction.note_withheld) return <span className="um-quiet um-wordonly" data-testid="row-correction-note">{CORRECTION_NOTE_WITHHELD}</span>;
  return null;
}

/*
 * "Reported deceased" (SCRUM-128, 2026-11-03; the ruling's default): a death reported outside UMED -- the resident's
 * status reads Deceased, or eMerge holds a death report -- is said on the row and the case. Nothing moves on its own:
 * the case stays where it is (Review included) until a person records the DECEASED disposition, which the chip offers to
 * who may record it. Not drawn once the case already says deceased.
 */
export const DECEASED_REPORTED_LABEL = 'Reported deceased';
export const DECEASED_REPORTED_WHY = 'The resident is reported deceased outside UMED (their status, or a death report in eMerge). The case stays where it is until DECEASED is recorded here.';
export const deceasedReportedShown = (row: Pick<BoardRow, 'deceased_reported' | 'lifecycle' | 'disposition'>): boolean =>
  Boolean(row.deceased_reported) && row.lifecycle !== 'DECEASED' && row.disposition?.word !== 'DECEASED';
export function DeceasedReportedChip({ row, onRecord }: { row: Pick<BoardRow, 'deceased_reported' | 'lifecycle' | 'disposition'>; onRecord?: () => void }) {
  if (!deceasedReportedShown(row)) return null;
  return (
    <span className="um-quiet um-wordonly" data-testid="deceased-reported">
      <span className="um-tag attn" title={DECEASED_REPORTED_WHY}>{DECEASED_REPORTED_LABEL}</span>{' '}
      {onRecord
        ? <button type="button" className="um-link" onClick={(e) => { e.stopPropagation(); onRecord(); }}>Record DECEASED…</button>
        : <span>Record DECEASED under Disposition when confirmed.</span>}
    </span>
  );
}

/** What completing a correction did on the Finance side, in words (the server's answer; shared with the case panel). */
export const correctionCompletedWords = (d: CorrectionCompleted | undefined): string => (
  d?.alreadyCompleted ? 'This correction was already completed.'
    : d?.financeHandoffAmended ? "Correction completed. Finance's handoff carries it as an amendment."
      : d?.financeHandoff === FINANCE_HANDOFF_BLOCKED ? `Correction completed. ${d.financeHandoffBlocked?.message || APPROVED_SECOND_HANDOFF_BLOCKED}`
      : d?.financeHandoff === 'NOT_APPROVED' ? 'Correction completed. Nothing went to Finance: the case is not approved.'
        : d?.financeHandoffCreated || d?.financeHandoffActionId ? 'Correction completed. The Finance handoff is open.'
          : 'Correction completed.');

/**
 * Josie Evans, 2026-09-24: "Where the UMED status is I want to be able to
 * drop a box down that says approval, SFS correction, or noca-no flmmis."
 *
 * On the board that dropdown MOVED the row: you picked a word and the word
 * became true. Here it cannot, and should not. Every tab on this screen is a
 * predicate over the case record, so a status is something the case IS, never
 * something it was told it is.
 *
 * So the three words she named are offered as the three ACTS that produce
 * them — a review that finds FLMMIS reflected, a review that finds the NOCA
 * alone, a correction raised with its reason — and the chip beside them keeps
 * showing the board's own word until the facts move it. She gets her dropdown;
 * nothing gets declared true by being picked from a list.
 *
 * Each choice opens a panel that says what will happen BEFORE it happens.
 * "Approval" opens the Finance handoff, and a select that fires on change is
 * no place to learn that afterwards.
 */
/*
 * UM-R2 (Josie Evans, 2026-10-02): "Please provide me these status updates
 * with this color coding." The menu now offers every board word she listed
 * that a native act can produce, each NAMED by the word it produces -- and,
 * as before, each is the ACT, never the word declared: REVERSAL RESUB records the sending of a DCF resubmittal
 * (REVERSAL / DCF RESUBMITTAL… starts one, 2026-10-19 -- see resubmittalStartOffered),
 * SFS CORRECTION COMPLETED completes the open correction. The server writes the
 * word in the same transaction (statusWords.js), so the chip moves when the
 * board reloads. Offered only where the act can work: a correction is
 * requested when none is open and completed when one is; a reversal is
 * requested, sent, or answered according to where it stands.
 */
type Outcome =
  | 'APPROVAL' | 'NOCA_NO_FLMMIS'
  | 'SFS_CORRECTION' | 'SFS_CORRECTION_FLMMIS_ISSUE' | 'CORRECTION_COMPLETE'
  // Josie Evans, 2026-10-28: the open correction went to DCF and DCF still does not show it -- send it again.
  | 'CORRECTION_RESEND'
  | 'REVERSAL_SUBMITTED' | 'REVERSAL_APPROVED' | 'REVERSAL_NOT_APPROVED'
  // PAR 01-03 (Kaleigh Vazquez & Josie Evans, 2026-10-08): the partial approval, and the APPROVED that completes it.
  | 'PARTIAL_APPROVAL' | 'PARTIAL_COMPLETE';
const OUTCOME_LABEL: Record<Outcome, string> = {
  APPROVAL: 'APPROVED',
  PARTIAL_APPROVAL: PARTIAL_APPROVAL_WORD,
  PARTIAL_COMPLETE: 'APPROVED',
  NOCA_NO_FLMMIS: 'NOCA-NO FLMMIS',
  SFS_CORRECTION: 'SFS CORRECTION',
  SFS_CORRECTION_FLMMIS_ISSUE: 'SFS CORRECTION-FLMMIS ISSUE',
  CORRECTION_COMPLETE: 'SFS CORRECTION COMPLETED',
  // Josie Evans, 2026-11-03: "It simply needs to be submitted under SFS Correction" -- not a "Correction Again".
  CORRECTION_RESEND: 'SFS CORRECTION · SUBMIT',
  REVERSAL_SUBMITTED: 'REVERSAL RESUB',
  REVERSAL_APPROVED: 'REVERSAL COMPLETE',
  REVERSAL_NOT_APPROVED: 'REVERSAL NOT APPROVED',
};
/*
 * A paid-in-full correction completes as Finance's word for it, PAID IN FULL CORRECTION -- but the TILE that completes it
 * is never named by that word alone (Josie Evans, Teams, 2026-10-29). She clicked "PAID IN FULL CORRECTION" to START one
 * for a resident whose correction was already open (and never sent), read the completion's sentence, pressed Record and
 * completed it. The start tile is "Paid in full correction" in the Paid in full group; the completion says COMPLETED.
 */
export const PIF_CORRECTION_WORD = 'PAID IN FULL CORRECTION';
export const PIF_CORRECTION_COMPLETE_LABEL = 'PAID IN FULL CORRECTION · COMPLETED';
const isPifCorrection = (row: Pick<BoardRow, 'correction'>): boolean => row.correction?.correction_type === 'PAID_IN_FULL_CORRECTION';
const outcomeLabel = (o: Outcome, row: BoardRow): string =>
  (o === 'CORRECTION_COMPLETE' && isPifCorrection(row) ? PIF_CORRECTION_COMPLETE_LABEL : OUTCOME_LABEL[o]);
/** The status word an outcome writes, where it is not the tile's own label (the paid in full completion's). */
const outcomeWord = (o: Outcome, row: BoardRow): string =>
  (o === 'CORRECTION_COMPLETE' && isPifCorrection(row) ? PIF_CORRECTION_WORD : outcomeLabel(o, row));

/*
 * Completing a correction says "the corrected UMED went in" -- so it is offered only once it DID go: its corrected UMED
 * went to DCF since it was opened (or last asked to be sent again), the server's `sent_since_opened` (2026-10-29). Before
 * that the row says the correction is open and to send (CorrectionStateChip), and the case panel keeps a completion for
 * one sent outside eMerge, behind its own confirmation. Absent on a payload from before #537: offered, as it was.
 */
export const correctionCompleteOffered = (row: Pick<BoardRow, 'correction'>): boolean =>
  Boolean(row.correction && row.correction.sent_since_opened !== false);

/*
 * "My buttons are gone" (Josie Evans, 2026-10-29): with the completion absent while a correction was open and not sent,
 * the menu said nothing at all. Ruling (Isaiah Cotto, 2026-10-29): "Show them greyed with the reason." So the completion
 * tile stays on the row, DISABLED, saying why it cannot be recorded yet and -- where there is one -- the way to the next
 * step. Picking it records nothing and posts nothing; the server still refuses CORRECTION_NOT_SENT. Read from the row's
 * own facts: the packet's hold (a terms or total hold opens the case's Terms section), a packet ready for Gwynn, else
 * the plain fact. The case panel's sent-outside-eMerge completion is unchanged.
 */
export const CORRECTION_UNSENT_TERMS = 'Record the terms first so Gwynn can send it.';
export const CORRECTION_UNSENT_READY = 'The packet is ready; Gwynn has not sent it yet.';
export const CORRECTION_UNSENT_HELD = 'Its packet is held: open the packet to see what it needs.';
/*
 * Sending it (again) to DCF from eMerge is the packet Gwynn sends; on a case adopted from Monday that packet is held for
 * terms eMerge never had, so the terms step says which terms (Teams, 2026-10-29). Shown with the step, not on the tile.
 */
export const CORRECTION_TERMS_HELP = 'To send it to DCF, enter the terms of the original UMED (monthly amount × months).';
/**
 * `also` (2026-10-29): row acts the explanation opens, each behind its own panel -- "Add a note for Gwynn" while the
 * correction has none (CorrectionNotePanel), and "Already sent to DCF outside eMerge", the attested completion
 * (CorrectionOutsidePanel). Every greyed completion offers the second; that act's own confirmation guards it.
 */
export interface DisabledTile { reason: string; help?: string; next?: { label: string; to: CaseFocus['kind'] }; also?: Array<{ lead: string; label: string; act: RowAct }> }
export const CORRECTION_NOTE_LABEL = 'Add a note for Gwynn';
/**
 * The open correction's CURRENT episode has its note for Gwynn (shown, or withheld from this viewer): it takes no second.
 * Since 2026-11-03 (SCRUM-131) one note per episode -- the request, then each SEND AGAIN -- so the server's `note_current`
 * decides; a payload from before it has one note per correction, as it was.
 */
export const correctionHasNote = (row: Pick<BoardRow, 'correction'>): boolean => {
  const k = row.correction;
  if (!k) return false;
  return typeof k.note_current === 'boolean' ? k.note_current : Boolean(k.note || k.note_withheld);
};
export function correctionCompleteHeld(row: Pick<BoardRow, 'correction' | 'packet_preparation'>): DisabledTile | null {
  if (!row.correction || correctionCompleteOffered(row)) return null;
  const head = (row.correction.resends || 0) > 0 ? 'Not sent to DCF since it was last asked to be sent again.' : 'Not sent to DCF yet.';
  const prep = row.packet_preparation;
  const hold = prep && prep.state === 'ACTION_REQUIRED' && prep.blockers.length ? prep.blockers[0] : null;
  const also: NonNullable<DisabledTile['also']> = [
    ...(correctionHasNote(row) ? [] : [{ lead: 'Tell Gwynn what to send?', label: CORRECTION_NOTE_LABEL, act: 'CORRECTION_NOTE' as RowAct }]),
    { lead: 'Sent to DCF before eMerge carried it, and DCF shows it?', label: CORRECTION_OUTSIDE_LABEL, act: 'CORRECTION_OUTSIDE' },
  ];
  if (hold && TERMS_HOLDS.has(hold.code)) return { reason: `${head} ${CORRECTION_UNSENT_TERMS}`, help: CORRECTION_TERMS_HELP, next: { label: 'Record the terms', to: 'terms' }, also };
  if (hold) return { reason: `${head} ${CORRECTION_UNSENT_HELD}`, next: { label: 'Open the packet', to: 'packet' }, also };
  if (prep && prep.state === 'READY') return { reason: `${head} ${CORRECTION_UNSENT_READY}`, next: { label: 'Open the packet', to: 'packet' }, also };
  return { reason: head, also };
}
/** "Add a note for Gwynn" on the row (2026-10-29): for who may manage corrections, on any open correction; greyed once it has its note. */
export const CORRECTION_NOTE_HAS_ONE = 'This correction has its note for Gwynn already, kept as written. Add anything more as a note on the case, or with SFS CORRECTION · SUBMIT once it has gone to DCF.';
export function correctionNoteTile(row: BoardRow, acts: Pick<BoardActsValue, 'canCorrect'> | null): ActTile | null {
  if (!acts?.canCorrect || !row.correction || row.stage === 'CLOSED') return null;
  return correctionHasNote(row)
    ? { act: 'CORRECTION_NOTE', label: CORRECTION_NOTE_LABEL, hex: null, title: CORRECTION_NOTE_HAS_ONE, disabled: { reason: CORRECTION_NOTE_HAS_ONE } }
    : { act: 'CORRECTION_NOTE', label: CORRECTION_NOTE_LABEL, hex: null, title: CORRECTION_NOTE_EFFECT };
}

/*
 * "Still not reflecting -- send the correction again" (Josie Evans, Teams, 2026-10-28): "I need to submit it again
 * because 9/1 is not reflecting but it's not letting me re-click SFS-correction. How am I supposed to resubmit for Gwynn
 * to fax again?" A correction is open, so no new SFS CORRECTION is offered (the server refuses CORRECTION_OPEN). What she
 * needs is the SAME correction sent again -- offered once its corrected UMED went to DCF (the server's
 * `sent_since_opened`, the very fact resendCorrection checks). Before that there is nothing to send again, and the row
 * says why in words: what holds the packet, with the way to fix it (correctionNotSentNote) -- never a silent absence.
 */
export const CORRECTION_RESEND_LABEL = OUTCOME_LABEL.CORRECTION_RESEND;
export const correctionResendOffered = (row: Pick<BoardRow, 'correction' | 'stage' | 'reversal'>): boolean =>
  Boolean(row.correction && row.correction.sent_since_opened === true && row.stage !== 'CLOSED' && !row.reversal);
/*
 * Why SEND AGAIN cannot be asked now, in words, for a correction that is open (2026-11-03, SCRUM-127): the docked
 * record draws the act disabled with this line rather than leaving it out. The order is the server's (resendCorrection):
 * a closed case, a DCF resubmittal open (OTHER_WORK_OPEN: it is sent first), not sent since it was opened or last asked
 * again (CORRECTION_NOT_SENT, with what holds its packet). Null when it is offered, or there is no open correction.
 */
export const CORRECTION_RESEND_CLOSED = 'This case is closed.';
export const CORRECTION_RESEND_RESUB_OPEN = 'A DCF resubmittal is open on this case and is sent first. Submit the SFS correction once it is done.';
/** The server's own words for a re-send under a fax (nativeWorkflowService RESEND_FAX_IN_FLIGHT_WORDS, 409 FAX_IN_FLIGHT). */
export const CORRECTION_RESEND_FAX_IN_FLIGHT = 'A fax of this case is on its way to DCF (or being recorded). Wait for its result before submitting the SFS correction.';
export function correctionResendHeld(row: Pick<BoardRow, 'correction' | 'stage' | 'reversal' | 'packet_preparation' | 'fax_in_flight'>): string | null {
  if (!row.correction) return null;
  // Verification of #580 (R2): offered, but eMerge's own fax of the case is in flight -- the server refuses it, so it is
  // greyed with the server's words (the row's fax_in_flight, the predicate the guard refuses on), as Set status greys it.
  if (correctionResendOffered(row)) return row.fax_in_flight ? CORRECTION_RESEND_FAX_IN_FLIGHT : null;
  if (row.stage === 'CLOSED') return CORRECTION_RESEND_CLOSED;
  if (row.reversal) return CORRECTION_RESEND_RESUB_OPEN;
  const held = correctionCompleteHeld({ ...row, correction: { ...row.correction, sent_since_opened: false } });
  return `${held ? held.reason : 'Not sent to DCF yet.'} There is nothing more to submit until it goes.`;
}
/** The packet holds that the case's Terms section clears (Record terms / Change terms / Add the original terms). */
const TERMS_HOLDS: ReadonlySet<string> = new Set(['TOTAL_MISSING', 'PAYMENT_TERMS_MISSING', 'CORRECTED_TERMS_MISSING']);
const CORRECTION_NOUN: Record<string, string> = {
  SFS_CORRECTION: 'The SFS correction', SFS_CORRECTION_FLMMIS_ISSUE: 'The SFS correction (FLMMIS issue)', PAID_IN_FULL_CORRECTION: 'The paid in full correction',
};
/**
 * Why no SEND AGAIN is offered on a row with an open correction, for who may manage corrections, where something holds
 * it: it has not gone to DCF (since it was opened or last asked again) because its packet is held, and by what --
 * prod 2026-10-28 (verification of #537): 27 open corrections held, first hold TOTAL_MISSING on 24 and
 * PAYMENT_TERMS_MISSING on 3 -- every one a terms/total hold. `fix` is where the row's link lands: the case's Terms section for a terms or total hold, else the packet
 * section. Null when SEND AGAIN is offered, nothing is open, or nothing holds the packet (it is being prepared or is
 * ready for Gwynn: the stage cell says so).
 */
export function correctionNotSentNote(row: BoardRow, acts: Pick<BoardActsValue, 'canCorrect'> | null): { note: string; fix: 'terms' | 'packet' } | null {
  if (!acts?.canCorrect || !row.correction || row.stage === 'CLOSED' || row.reversal) return null;
  if (row.correction.sent_since_opened !== false) return null;
  const prep = row.packet_preparation;
  const hold = prep && prep.state === 'ACTION_REQUIRED' && prep.blockers.length ? prep.blockers[0] : null;
  if (!hold) return null;
  const who = CORRECTION_NOUN[row.correction.correction_type] || 'The correction';
  const since = (row.correction.resends || 0) > 0 ? 'since it was last asked to be sent again' : 'yet';
  return { note: `${who} has not gone to DCF ${since}: its packet is held. ${hold.message}`, fix: TERMS_HOLDS.has(hold.code) ? 'terms' : 'packet' };
}

/** The acts this caller may record on this row, in the order the menu offers them. */
function outcomesFor(row: BoardRow, acts: BoardActsValue | null): Outcome[] {
  if (!acts) return [];
  const out: Outcome[] = [];
  /*
   * APPROVED is the approval act itself (2026-10-06). Since 2026-10-10 it is offered on every open case not yet
   * approved where both of its steps can work (approvalOffered): with FLMMIS on record it is today's confirmation,
   * without it the panel records FLMMIS first -- never a menu entry that opens only to say no (P2-6). NOCA-NO FLMMIS
   * rewrites the evidence, so an approved case is not offered it (P1-A; the server refuses it too).
   */
  if (acts.canReview) {
    // PAR 03: on an APPROVED - SFS CORRECTION case, APPROVED is the act that completes it (FLMMIS shows the months).
    if (partialApprovalOpen(row)) out.push('PARTIAL_COMPLETE');
    if (approvalOffered(row, acts.canReview)) out.push('APPROVAL');
    // PAR 01: beside APPROVED, where FLMMIS is reflected and only some months are.
    if (partialApprovalOffered(row, acts.canReview)) out.push('PARTIAL_APPROVAL');
    if (!isApproved(row)) out.push('NOCA_NO_FLMMIS');
  }
  if (acts.canCorrect) {
    // A partial approval's correction completes by APPROVED above, an open PL correction's by PL CORRECTION OUTCOME --
    // never by SFS CORRECTION COMPLETED (the server refuses both).
    if (row.correction) {
      // Recordable only once its corrected UMED went to DCF (correctionCompleteOffered, 2026-10-29); before that the tile
      // is still drawn, disabled, with the reason (correctionCompleteHeld; actTilesFor).
      if (!partialApprovalOpen(row) && !plCorrectionHoldsCorrection(row)) out.push('CORRECTION_COMPLETE');
      // 2026-10-28: sent, and DCF still does not show it -- the SFS CORRECTION tile she looked for, as "send again".
      if (correctionResendOffered(row)) out.push('CORRECTION_RESEND');
    }
    // No NEW correction while a DCF resubmittal is open, handed to UMED or not (newCorrectionOffered, 2026-10-21).
    else if (newCorrectionOffered(row)) {
      if (acts.sfs) out.push('SFS_CORRECTION');
      if (acts.flmmisIssue) out.push('SFS_CORRECTION_FLMMIS_ISSUE');
    }
  }
  if (acts.canReverse && row.stage !== 'CLOSED') {
    const v = row.reversal;
    // Starting one is its own tile, REVERSAL / DCF RESUBMITTAL… (2026-10-19; resubmittalStartOffered), not an outcome. The sending is
    // offered for one handed to UMED that states its kind (a row still with Finance has no packet), or after a decline.
    if (!v) { /* nothing open: nothing to record here */ }
    else if ((v.state === 'REQUESTED' && v.handed !== false && v.kind !== null) || v.state === 'NOT_APPROVED') out.push('REVERSAL_SUBMITTED');
    else if (v.state === 'SUBMITTED') out.push('REVERSAL_APPROVED', 'REVERSAL_NOT_APPROVED');
  }
  return out;
}

const OUTCOME_EFFECT: Partial<Record<Outcome, string>> = {
  SFS_CORRECTION: 'Raises an SFS correction: a new UMED must be produced, and whoever makes packets is told. The reason is kept with the correction for good. The case moves to Corrections and out of Review for approvals until the correction is complete.',
  SFS_CORRECTION_FLMMIS_ISSUE: 'Raises a correction because FLMMIS rejected it: a new UMED must be produced, and whoever makes packets is told. The reason is kept for good. The case moves to Corrections and New approvals — NOCA, and out of Review for approvals.',
  // Said per case: see correctionCompleteEffect (an approved case stays approved).
  REVERSAL_SUBMITTED: 'Records that the DCF resubmittal packet went to DCF. It stays under DCF RESUBMIT until DCF answers.',
  // Ruling A (2026-10-30): a corrected one on an approved claim reaches Finance whether or not Finance had touched the case.
  // PR #572 review: qualified for ruling B -- a PORTFOLIO claim beside the resident's live handoff opens none.
  REVERSAL_APPROVED: 'Records that DCF approved the resubmittal: it is complete. A corrected one on an approved claim reaches Finance: the handoff Finance holds is amended, or one opens when there is none. A full one opens the handoff only if Finance had anything to unwind. No handoff opens for an SFS PORTFOLIO claim when Finance already holds one for this resident: Finance is flagged instead, and the answer says so.',
  REVERSAL_NOT_APPROVED: 'Records that DCF did not approve the resubmittal. It stays under DCF RESUBMIT until it is sent again or left.',
};

/**
 * SFS CORRECTION COMPLETED, in the words that are true for THIS case
 * (verification of 2026-10-06, P2-5). Completing a correction opens its own
 * Finance handoff either way; what happens to the case depends on whether it
 * is approved in eMerge -- an approved case stays approved and never goes
 * back to Review for approvals, which holds only cases not yet approved.
 */
export const correctionCompleteEffect = (approved: boolean) => (approved
  ? 'Records that the corrected UMED went in and the correction is complete, and opens the Finance handoff for the correction. The case is approved in eMerge and stays approved: it does not go back to Review for approvals.'
  : 'Records that the corrected UMED went in and the correction is complete, and opens the Finance handoff for the correction. The case is not approved yet, so it goes back to Review for approvals while DCF answers.');
/*
 * PAID IN FULL CORRECTION · COMPLETED, in the words that are true of it (Josie Evans, CORRECTIONS R3/R5, 2026-10-28:
 * the account stays paid in full on the Finance side; Gwynn sends the correction; Josie watches DCF, then closes it on the
 * UMED side). completeCorrection decides Finance over the resident's whole EPISODE (handoffAmendments.episodeApproved and
 * the episode's one handoff), which the row does not carry -- so the sentence says the rule, not this case's approval
 * (verification of #543, D2). The answer after Record says what actually happened (financeHandoff, from the server).
 */
export const PIF_CORRECTION_COMPLETE_EFFECT = "Records that DCF now shows the paid in full correction Gwynn sent, and closes it on the UMED side. The account stays paid in full. Finance is told the correction is complete only if this resident's UMED has an approved handoff (a new handoff, or a note on the one Finance holds); otherwise nothing goes to Finance.";

function OutcomePanel({ row, outcome, onClose }: { row: BoardRow; outcome: Outcome; onClose: () => void }) {
  const acts = React.useContext(BoardActs);
  const [on, setOn] = useState(todayLocal());
  const [reason, setReason] = useState('');
  const [gwynnNote, setGwynnNote] = useState('');
  // CORRECTIONS R2 (2026-10-28): the months not reflecting, which Finance reads as "Correction" (optional).
  const [months, setMonths] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const label = outcomeLabel(outcome, row);
  const word = outcomeWord(outcome, row);
  /*
   * The server does not write NOCA-NO FLMMIS over an open reversal, correction
   * or problem account (statusWords.reviewWord), so the panel does not promise
   * it. Every other act writes its own word. APPROVED is not recorded here: it
   * is the Approve act's own confirmation (ApproveAct), one sentence for both
   * ways in (P2-6).
   */
  const exceptionWork = Boolean(row.reversal) || Boolean(row.correction)
    || (['REVERSAL', 'CORRECTION', 'PROBLEM'].includes(row.exception) && row.exception_state !== 'COMPLETE');
  const wordStays = outcome === 'NOCA_NO_FLMMIS' && exceptionWork;
  const isReview = outcome === 'NOCA_NO_FLMMIS';
  const needsReason = outcome === 'SFS_CORRECTION' || outcome === 'SFS_CORRECTION_FLMMIS_ISSUE';
  const ready = !needsReason || reason.trim().length >= 3;

  const save = async () => {
    setBusy(true); setErr(null);
    try {
      let r: { success: boolean; status?: number; code?: string; message?: string; error?: string };
      if (outcome === 'SFS_CORRECTION' || outcome === 'SFS_CORRECTION_FLMMIS_ISSUE') {
        r = await umedApi.requestCorrection(row.id, {
          correction_type: outcome as CorrectionType, reason: reason.trim(),
          ...(outcome === 'SFS_CORRECTION_FLMMIS_ISSUE' && gwynnNote.trim() ? { note_to_gwynn: gwynnNote.trim() } : {}),
          ...(months.length ? { months } : {}),
        });
      } else if (outcome === 'CORRECTION_COMPLETE') {
        if (!row.correction) return;
        r = await umedApi.completeCorrection(row.correction.id);
      } else if (outcome === 'REVERSAL_SUBMITTED') {
        if (!row.reversal) return;
        r = await umedApi.reversalSubmitted(row.reversal.id);
      } else if (outcome === 'REVERSAL_APPROVED' || outcome === 'REVERSAL_NOT_APPROVED') {
        if (!row.reversal) return;
        r = await umedApi.reversalOutcome(row.reversal.id, outcome === 'REVERSAL_APPROVED' ? 'APPROVED' : 'NOT_APPROVED');
      } else {
        r = await umedApi.review(row.id, { noca: 'RECEIVED' as NocaResult, flmmis: 'NOT_REFLECTED' as FlmmisResult, decision: 'CONTINUE', follow_up_on: on });
      }
      if (!r.success) { setErr(refusal(r)); return; }
      onClose();
      // Ruling B (PR #572 review): when DCF's answer opened no second handoff, the server's own words say so.
      acts?.announce(`${label} recorded for ${personName(row)}.${blockedWords(r)}`, row.id);
    } catch { setErr('Could not reach the server. Nothing was recorded.'); } finally { setBusy(false); }
  };

  return (
    <div className="um-rowpanel" role="group" aria-label={`Record ${label} for ${personName(row)}`}
      onClick={(e) => e.stopPropagation()} onKeyDown={busy ? undefined : escapeCancels(onClose)}>
      <p className="um-rowpanel-what">
        {outcome === 'NOCA_NO_FLMMIS' && `Records the NOCA as received and FLMMIS as not reflected. The case is not approved, and the next check falls due ${acts?.recheckDays ?? 10} days after the date below.`}
        {outcome === 'CORRECTION_COMPLETE'
          ? (isPifCorrection(row) ? PIF_CORRECTION_COMPLETE_EFFECT : correctionCompleteEffect(isApproved(row)))
          : OUTCOME_EFFECT[outcome]}
        {' '}{wordStays
          ? <>The status stays {row.source_status_raw || 'as it is'}: an open reversal, correction or problem account keeps its own word.</>
          : <>The status becomes {word}.</>}
      </p>
      {needsReason && (
        <label className="um-rowfield"><span>Reason</span>
          <input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={2000} disabled={busy}
            placeholder="Why it must be corrected" aria-label="Why the correction is needed" />
        </label>
      )}
      {needsReason && <MonthsNotReflecting months={months} onChange={setMonths} disabled={busy} />}
      {outcome === 'SFS_CORRECTION_FLMMIS_ISSUE' && (
        <label className="um-rowfield"><span>Instructions for Gwynn</span>
          <textarea value={gwynnNote} onChange={(e) => setGwynnNote(e.target.value)} maxLength={2000} rows={4} disabled={busy}
            placeholder="What Gwynn needs to do to complete the correction" aria-label="Instructions for Gwynn" aria-describedby="um-gwynn-note-help" />
          <small id="um-gwynn-note-help">Gwynn receives this now and once more in 14 days unless the correction is completed.</small>
        </label>
      )}
      {isReview && (
        <label className="um-rowfield"><span>The day you looked</span>
          <input type="date" value={on} max={todayLocal()} disabled={busy}
            onChange={(e) => setOn(e.target.value)} aria-label="The day you checked for an approval" />
        </label>
      )}
      {err && <p className="um-notice stop" role="alert">{err}</p>}
      <div className="um-rowpanel-acts">
        <button type="button" className="um-open um-primary" disabled={busy || !ready} aria-describedby={(!ready && 'Say why (at least 3 characters) first.') ? `why-outcome-${row.id}` : undefined}
          onClick={save}>{busy ? 'Recording…' : 'Record'}</button><Why id={`why-outcome-${row.id}`} reason={!ready && 'Say why (at least 3 characters) first.'} className="um-text um-why" />
        <button type="button" className="um-link" disabled={busy} onClick={onClose}>Cancel</button>
      </div>
    </div>
  );
}

/**
 * A disposition, from the row (2026-10-02).
 *
 * The same discipline as OutcomePanel: the panel says what will happen before
 * it happens, a reason is required for every one (the server refuses without
 * it, so the button does too), and the effective date defaults to today.
 * After saving the board reloads, and the row leaves this tab when the tab's
 * predicate no longer holds -- which is the point: a resident who no longer
 * qualifies is not in Review for approval any more.
 *
 * REACTIVATE is offered only where the server will accept it (the row's
 * `disposition.reactivatable`), and a closed case is offered nothing else.
 */
const DISPOSITION_EFFECT: Record<Disposition, string> = {
  NO_LONGER_QUALIFIES: 'Suspends the approval chase. The case keeps its stage, leaves every approval, creation and submit queue, and is found under Not qualifying / recert and in All cases. The Patient Advocate who owns this resident is notified, with your reason, to tell the POA and the business office manager (BOM).',
  NEEDS_MEDICAID_RECERT: 'Suspends the case until Medicaid is recertified. It leaves every approval, creation and submit queue and is found under Not qualifying / recert and in All cases. The Patient Advocate who owns this resident is notified, with your reason, to tell the POA and the business office manager (BOM).',
  DECEASED: 'Closes the case on the date below. It leaves every queue and stays in All cases. The Patient Advocate’s case is not changed. The UMED submitter is told so DCF can be notified to stop approving.',
  // Recorded from the "Paid in full" group (2026-10-20), never from this panel: the group's own words, kept here so the map is whole.
  PAID_IN_FULL: paidInFullOption('PAID_IN_FULL').effect,
  DCF_RESUBMISSION_NEEDED: 'Opens a DCF resubmission and tells Gwynn to send it again. The case moves to the DCF RESUB NEEDED section and stays in Review for Approvals (Josie Evans, 2026-10-06).',
  // UM-R1/UM-R2 (Josie Evans, 2026-09-28 and 2026-10-02).
  PROBLEM_ACCOUNT: 'Marks a problem account: the Patient Advocate who owns this resident is notified, with your reason, to tell the POA and the business office manager (BOM). The case leaves Review for approvals and is found under Problem Accounts and in All cases. Reactivate puts it back.',
  REACTIVATE: 'Undoes the disposition: what it changed goes back to what it replaced, so the case returns to the queues it was in. Anything that has moved since stays as it is now, and an approval recorded since keeps the case approved.',
};
/** The board word each disposition writes (UM-R2), said in the panel so the chip's change is not a surprise. */
const DISPOSITION_STATUS_WORD: Partial<Record<Disposition, string>> = {
  NO_LONGER_QUALIFIES: 'NO LONGER QUALIFIES', NEEDS_MEDICAID_RECERT: 'NEEDS MCD RECERT', DECEASED: 'DECEASED',
  PAID_IN_FULL: 'PAID IN FULL', DCF_RESUBMISSION_NEEDED: 'DCF RESUB NEEDED', PROBLEM_ACCOUNT: 'PROBLEM ACCOUNT',
};

/**
 * The dispositions an approved case refuses (409 ALREADY_APPROVED,
 * nativeWorkflowService CONTRADICTS_APPROVAL, verification of 2026-10-06
 * P2-2): each says the case is not, or not yet, approvable. Not offered on an
 * approved row -- undoing an approval is a reversal or a correction.
 */
export const CONTRADICTS_APPROVAL: readonly Disposition[] = ['DCF_RESUBMISSION_NEEDED', 'NO_LONGER_QUALIFIES', 'NEEDS_MEDICAID_RECERT', 'PROBLEM_ACCOUNT'];

function DispositionPanel({ row, onClose, initial = null }: { row: BoardRow; onClose: () => void; initial?: Disposition | null }) {
  const acts = React.useContext(BoardActs);
  const closed = row.stage === 'CLOSED';
  const approved = isApproved(row);
  // Paid in full is not listed here (2026-10-20): it is the first act of the "Paid in full" group, beside the other two
  // paid-in-full acts, so the menu offers it in one place, under one name. Reactivate (below) still undoes it.
  const choices: Disposition[] = [
    ...(closed ? [] : DISPOSITION_WORDS.filter((w) => w !== 'PAID_IN_FULL' && w !== row.disposition?.word && !(approved && CONTRADICTS_APPROVAL.includes(w)))),
    ...(row.disposition?.reactivatable ? ['REACTIVATE' as Disposition] : []),
  ];
  const [choice, setChoice] = useState<Disposition | ''>(
    initial && choices.includes(initial) ? initial : (choices.length === 1 ? choices[0] : ''),
  );
  const [reason, setReason] = useState('');
  const [on, setOn] = useState(todayLocal());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // One key per opening of the panel: a retry of this click is a replay, not a second act.
  const [key] = useState(() => freshKey());

  const save = async () => {
    if (!choice) return;
    setBusy(true); setErr(null);
    try {
      const r = await umedApi.recordDisposition(row.id, { disposition: choice, reason: reason.trim(), effective_on: on, idempotency_key: key });
      if (!r.success) { setErr(refusal(r)); return; }
      onClose();
      acts?.announce(`${DISPOSITION_LABEL[choice]} recorded for ${personName(row)}.`, row.id);
    } catch { setErr('Could not reach the server. Nothing was recorded.'); } finally { setBusy(false); }
  };

  const boardMoves = (row.forward_authority || 'MONDAY') === 'MONDAY';
  return (
    <div className="um-rowpanel" role="group" aria-label={`Record a disposition for ${personName(row)}`}
      onClick={(e) => e.stopPropagation()} onKeyDown={busy ? undefined : escapeCancels(onClose)}>
      {choices.length === 0 ? (
        <p className="um-rowpanel-what">This case is closed, and its closing cannot be undone here.</p>
      ) : (
        <>
          <label className="um-rowfield"><span>Disposition</span>
            <select value={choice} disabled={busy} onChange={(e) => setChoice(e.target.value as Disposition)} aria-label="Disposition">
              <option value="">Choose…</option>
              {choices.map((d) => <option key={d} value={d}>{DISPOSITION_LABEL[d]}</option>)}
            </select>
          </label>
          {choice && (
            <p className="um-rowpanel-what">
              {DISPOSITION_EFFECT[choice]}
              {DISPOSITION_STATUS_WORD[choice] && ` The status becomes ${DISPOSITION_STATUS_WORD[choice]}.`}
              {choice === 'REACTIVATE' && boardMoves && ' This case still follows the Monday board: if the board still carries the word, the next sync applies it again.'}
            </p>
          )}
          <label className="um-rowfield"><span>Reason</span>
            <input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={2000} disabled={busy}
              placeholder={choice === 'PROBLEM_ACCOUNT' ? 'The note for the Patient Advocate — kept with the case' : 'Why — kept with the case'} aria-label="Why this disposition" />
          </label>
          <label className="um-rowfield"><span>Effective</span>
            <input type="date" value={on} max={todayLocal()} disabled={busy}
              onChange={(e) => setOn(e.target.value)} aria-label="The day it took effect" />
          </label>
        </>
      )}
      {err && <p className="um-notice stop" role="alert">{err}</p>}
      <div className="um-rowpanel-acts">
        {choices.length > 0 && (<>
          <button type="button" className="um-open um-primary" disabled={busy || !choice || reason.trim().length < 3} aria-describedby={(firstReason([!choice, 'Choose what to record first.'], [reason.trim().length < 3, 'Say why (at least 3 characters) first.'])) ? `why-disposition-${row.id}` : undefined}
            onClick={save}>{busy ? 'Recording…' : 'Record'}</button><Why id={`why-disposition-${row.id}`} reason={firstReason([!choice, 'Choose what to record first.'], [reason.trim().length < 3, 'Say why (at least 3 characters) first.'])} className="um-text um-why" />
        </>)}
        <button type="button" className="um-link" disabled={busy} onClick={onClose}>Cancel</button>
      </div>
    </div>
  );
}

/*
 * "Paid in full" (Dominique Genua, Finance, 2026-10-20: "We need the option paid in full, not every member will need a
 * UMED submitted, that's a rare case" -- (1) Paid in full, (2) Paid in full correction, (3) Paid in full needs UMED).
 *
 * The three paid-in-full acts sit in ONE group on the row's status menu, each named and explained by
 * PAID_IN_FULL_OPTIONS (umedApi), each opening a panel that says what it does BEFORE anything is saved, and each
 * offered only where the server would take it from this viewer (paidInFullTilesFor). No new act: (1) is the
 * PAID_IN_FULL disposition, (2) the "Paid in full, UMED pending" mark (#427), (3) requestCorrection with
 * PAID_IN_FULL_CORRECTION. Each panel takes focus on its first field, Escape and Cancel give it back to the chip, and a
 * refusal is said in the server's words.
 */
/** What the paid-in-full disposition does beyond its one sentence (nativeWorkflowService.dispositionChange). */
export const PIF_DISPOSITION_DETAIL = 'Finance’s own payment records are not touched. Reactivate, under Disposition…, undoes it.';
/** What the mark does beyond its one sentence (umed/paidInFull.js; Finance reads it on the board row and the dossier). */
export const PIF_MARK_DETAIL = 'Nothing else changes: the case keeps its status and its lists. Finance sees the mark on the resident now, and once DCF approves, Finance’s handoff says “Resident paid in full: reimbursement owed”.';
/*
 * What REQUESTING a paid in full correction does, and where it goes (Josie Evans, Teams, 2026-10-29: "not sure where it's
 * going to go if I put it in"; April Torino: "A paid in full correction should go to UMEDs needed for Gwynn to send in a
 * paid in full correction"). CORRECTIONS R3/R5 (2026-10-28): the account stays paid in full on the Finance side and the
 * Finance board is not touched; Gwynn sends it; Josie watches DCF, then closes it on the UMED side.
 */
export const PIF_CORRECTION_REQUEST_EFFECT = 'The account is paid in full, and DCF must still be sent a corrected UMED. This goes to Gwynn: she is told now, with your note, and the corrected UMED is prepared for her to send to DCF. The resident is in UMEDs Needed until Gwynn sends it, and on the Kanban under Corrections & resubmittals until it is complete. Finance is not changed: the account stays paid in full there.';
/** What the correction does beyond that (requestCorrection, completeCorrection, the packet's cover). */
export const PIF_CORRECTION_DETAIL = 'One correction is open per case. Its packet carries the terms recorded on the case and its cover ticks UMED CORRECTION. The status word stays as it is while it is open: the row reads "Paid in full correction - open, to send", then "sent, awaiting review" once Gwynn records that it went to DCF. When DCF shows it, complete it with PAID IN FULL CORRECTION · COMPLETED.';
/** The note Gwynn sends from: required on a paid in full correction from UMED (the server refuses NOTE_REQUIRED). */
export const PIF_NOTE_HELP = 'Gwynn receives this now and once more in 14 days unless the correction is completed. It is kept on the case with the correction; it is not printed on the DCF fax cover.';
export const PIF_NOTE_PLACEHOLDER = 'What DCF must reflect, e.g. please only reflect our UMED terms for 1/1/26-6/1/26 (6 months at $500.00 = $3,000); full PL goes to the facility starting 7/1';
/** The confirmation once it is requested: where it went, who was told, and when she sees it again. */
export function pifCorrectionRequestedWords(name: string, told: number | null | undefined): string {
  const who = told === 0
    ? 'Nobody who makes packets for this building was told: tell Gwynn yourself, and ask for the umed.packet.generate grant to be checked.'
    : 'It goes to Gwynn with your note: she is told now, and once more in 14 days unless the correction is completed.';
  return `Paid in full correction requested for ${name}. ${who} Find it on the Kanban under Corrections & resubmittals (Paid in full correction · to send). Once Gwynn records that it went to DCF it reads "sent, awaiting review" and a 10-day review opens; when DCF shows it, complete it with ${PIF_CORRECTION_COMPLETE_LABEL}. Finance is not changed.`;
}

function PaidInFullDispositionPanel({ row, onClose }: { row: BoardRow; onClose: () => void }) {
  const acts = React.useContext(BoardActs);
  const option = paidInFullOption('PAID_IN_FULL');
  const [reason, setReason] = useState('');
  const [on, setOn] = useState(todayLocal());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // One key per opening of the panel: a retry of this click is a replay, not a second act.
  const [key] = useState(() => freshKey());
  const first = React.useRef<HTMLInputElement | null>(null);
  useEffect(() => { first.current?.focus({ preventScroll: true }); }, []);
  const save = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await umedApi.recordDisposition(row.id, { disposition: 'PAID_IN_FULL', reason: reason.trim(), effective_on: on, idempotency_key: key });
      if (!r.success) { setErr(refusal(r)); return; }
      onClose();
      acts?.announce(`${option.label} recorded for ${personName(row)}.`, row.id);
    } catch { setErr('Could not reach the server. Nothing was recorded.'); } finally { setBusy(false); }
  };
  return (
    <div className="um-rowpanel um-pifpanel" role="group" aria-label={`${PAID_IN_FULL_GROUP_LABEL}: ${option.label} for ${personName(row)}`}
      onClick={(e) => e.stopPropagation()} onKeyDown={busy ? undefined : escapeCancels(onClose)}>
      <p className="um-rowpanel-what" data-testid="pif-effect"><strong>{option.label}.</strong> {option.effect}</p>
      <p className="um-rowpanel-what um-quiet">{PIF_DISPOSITION_DETAIL}</p>
      <label className="um-rowfield"><span>Reason</span>
        <input ref={first} value={reason} onChange={(e) => setReason(e.target.value)} maxLength={2000} disabled={busy}
          placeholder="Why — kept with the case" aria-label="Why the resident is recorded paid in full" />
      </label>
      <label className="um-rowfield"><span>Effective</span>
        <input type="date" value={on} max={todayLocal()} disabled={busy}
          onChange={(e) => setOn(e.target.value)} aria-label="The day it took effect" />
      </label>
      {err && <p className="um-notice stop" role="alert">{err}</p>}
      <div className="um-rowpanel-acts">
        <button type="button" className="um-open um-primary" disabled={busy || reason.trim().length < 3} aria-describedby={(reason.trim().length < 3 && 'Say why (at least 3 characters) first.') ? `why-pif-${row.id}` : undefined} onClick={save}>
          {busy ? 'Recording…' : 'Record paid in full'}
        </button><Why id={`why-pif-${row.id}`} reason={reason.trim().length < 3 && 'Say why (at least 3 characters) first.'} className="um-text um-why" />
        <button type="button" className="um-link" disabled={busy} onClick={onClose}>Cancel</button>
      </div>
    </div>
  );
}

function PaidInFullCorrectionPanel({ row, onClose }: { row: BoardRow; onClose: () => void }) {
  const acts = React.useContext(BoardActs);
  const option = paidInFullOption('PAID_IN_FULL_CORRECTION');
  const [reason, setReason] = useState('');
  // Josie Evans, 2026-10-29: the note Gwynn sends from. Required here; the server refuses without it (NOTE_REQUIRED).
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // One key per opening of the panel: a retry after a lost answer is a replay of this request, not "already open".
  const [key] = useState(() => freshKey());
  const first = React.useRef<HTMLInputElement | null>(null);
  useEffect(() => { first.current?.focus({ preventScroll: true }); }, []);
  const ready = reason.trim().length >= 3 && note.trim().length >= 3;
  const save = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await umedApi.requestCorrection(row.id, { correction_type: 'PAID_IN_FULL_CORRECTION', reason: reason.trim(), note_to_gwynn: note.trim(), idempotency_key: key });
      if (!r.success) { setErr(refusal(r)); return; }
      onClose();
      acts?.announce(pifCorrectionRequestedWords(personName(row), r.data?.told), row.id);
    } catch { setErr('Could not reach the server. Nothing was recorded.'); } finally { setBusy(false); }
  };
  return (
    <div className="um-rowpanel um-pifpanel" role="group" aria-label={`${PAID_IN_FULL_GROUP_LABEL}: ${option.label} for ${personName(row)}`}
      onClick={(e) => e.stopPropagation()} onKeyDown={busy ? undefined : escapeCancels(onClose)}>
      <p className="um-rowpanel-what" data-testid="pif-effect"><strong>{option.label}.</strong> {PIF_CORRECTION_REQUEST_EFFECT}</p>
      <p className="um-rowpanel-what um-quiet">{PIF_CORRECTION_DETAIL}</p>
      <label className="um-rowfield"><span>Why (required)</span>
        <input ref={first} value={reason} onChange={(e) => setReason(e.target.value)} maxLength={2000} disabled={busy}
          placeholder="Why it must be corrected — kept with the correction for good" aria-label="Why the correction is needed" />
      </label>
      <label className="um-rowfield"><span>Note for Gwynn (required)</span>
        <textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={2000} rows={4} disabled={busy}
          placeholder={PIF_NOTE_PLACEHOLDER} aria-label="Note for Gwynn" aria-describedby={`um-pif-note-help-${row.id}`} />
        <small id={`um-pif-note-help-${row.id}`}>{PIF_NOTE_HELP}</small>
      </label>
      {err && <p className="um-notice stop" role="alert">{err}</p>}
      <div className="um-rowpanel-acts">
        <button type="button" className="um-open um-primary" disabled={busy || !ready} aria-describedby={(!ready && firstReason([reason.trim().length < 3, 'Say why (at least 3 characters) first.'], [note.trim().length < 3, 'Write the note to Gwynn (at least 3 characters) first.'])) ? `why-pif-correction-${row.id}` : undefined} onClick={save}>
          {busy ? 'Recording…' : 'Request the correction'}
        </button><Why id={`why-pif-correction-${row.id}`} reason={!ready && firstReason([reason.trim().length < 3, 'Say why (at least 3 characters) first.'], [note.trim().length < 3, 'Write the note to Gwynn (at least 3 characters) first.'])} className="um-text um-why" />
        <button type="button" className="um-link" disabled={busy} onClick={onClose}>Cancel</button>
      </div>
    </div>
  );
}

/** Each refusal is a different thing to do about it. */
const refusal = (r:{ status?: number; code?: string; message?: string; error?: string }): string => {
  if (r.status === 404) return 'Not switched on here.';
  if (r.code === 'DENY_NO_CAPABILITY' || r.status === 403) return 'Not permitted.';
  if (r.code === 'CORRECTION_OPEN') return 'A correction is already open on this case.';
  if (r.code === 'REVERSAL_OPEN') return 'A reversal is already open on this case.';
  if (r.code === 'EXCEPTION_ALREADY_OPEN') return r.message || r.error || 'Another exception is open on this case.';
  return r.message || r.error || 'That was not recorded.';
};

/**
 * The one FLMMIS writer on the board: a review that carries the NOCA as it stands and FLMMIS as found
 * (POST /umed/cases/:id/reviews, decision CONTINUE). FlmmisCell and the APPROVED tile's first step both record
 * through it, so FLMMIS reflected means one thing whichever control recorded it. Null when recorded; otherwise the
 * refusal in words. Throws when the server cannot be reached.
 */
async function writeFlmmis(row: Pick<BoardRow, 'id' | 'approval'>, next: FlmmisResult): Promise<string | null> {
  const blocked = flmmisBlocked(row);
  if (blocked) return blocked;
  const r = await umedApi.review(row.id, { noca: nocaForward(row.approval.noca) as NocaResult, flmmis: next, decision: 'CONTINUE' });
  return r.success ? null : refusal(r);
}

/**
 * The approval's evidence, both facts in ONE review (2026-10-13): NOCA received AND FLMMIS reflected
 * (POST /umed/cases/:id/reviews, decision CONTINUE) -- the two facts the approval act requires (Process 2 Step 3,
 * APR 03). The same review FlmmisCell writes, stating the NOCA as received instead of carrying it forward. Never
 * written over a NOCA a review has no word for (REJECTED) or one this viewer may not see (flmmisBlocked). Null when
 * recorded; otherwise the refusal in words. Throws when the server cannot be reached. Approves nothing.
 */
async function writeApprovalEvidence(row: Pick<BoardRow, 'id' | 'approval'>): Promise<string | null> {
  if (nocaForward(row.approval.noca) === null) return `NOCA is ${NOCA_LABEL[row.approval.noca as keyof typeof NOCA_LABEL] || row.approval.noca}, which a review has no word for. Open the case to record the approval evidence.`;
  const r = await umedApi.review(row.id, { noca: 'RECEIVED', flmmis: 'REFLECTED', decision: 'CONTINUE' });
  return r.success ? null : refusal(r);
}

/*
 * NEEDS MCD RECERT and NO LONGER QUALIFIES (Isaiah Cotto, 2026-10-10: "'NEEDS MCD RECERT' – DARK GRAY; 'NO LONGER
 * QUALIFIES' LIGHT PINK"), and DECEASED (Josie Evans, 2026-10-21): each a tile of its own, opening the disposition panel on that disposition -- the reason,
 * the date and the consequence exactly as "Disposition…" asks for them. In this order, after APPROVED.
 */
type DirectDisposition = 'NEEDS_MEDICAID_RECERT' | 'NO_LONGER_QUALIFIES' | 'DECEASED';
const DIRECT_DISPOSITIONS: readonly DirectDisposition[] = ['NEEDS_MEDICAID_RECERT', 'NO_LONGER_QUALIFIES', 'DECEASED'];
const isDirectDisposition = (a: string): a is DirectDisposition => (DIRECT_DISPOSITIONS as readonly string[]).includes(a);

/*
 * Gwynn, 2026-10-12. UPLOADED: "UMED uploaded to DCF" -- NOT a second sending act: it opens the packet's own Confirm DCF
 * upload (the one source of truth, which writes UMED SUBMITTED TO DCF). DCF_EMAIL_CREATED: the DCF account's record form.
 * BENEFITS_STUCK / BENEFITS_LINKED: "(STUCK) Can't link Benefits" and its clearing, recorded from the row.
 */
// OPEN_PACKET (2026-11-03, SCRUM-126): a DCF resubmittal's packet held -- the row opens the case's packet panel, where its hold is fixed.
type GwynnAct = 'SUBMITTED_DCF' | 'FAX_DCF' | 'UPLOADED' | 'DCF_EMAIL_CREATED' | 'BENEFITS_STUCK' | 'BENEFITS_LINKED' | 'OPEN_PACKET';
// CORRECTION_OUTSIDE (2026-10-29): the attested completion, opened from a greyed completion's explanation, never a tile.
// CORRECTION_NOTE (2026-10-29): "Add a note for Gwynn" on an open correction (a tile, and from that explanation).
type RowAct = Outcome | 'DISPOSITION' | 'PROBLEM_ACCOUNT' | DirectDisposition | 'DCF_RESUB' | 'SUBMISSION' | GwynnAct | AfterApprovalAct | PaidInFullAct | 'RESUB_START' | 'CORRECTION_OUTSIDE' | 'CORRECTION_NOTE';
/** The "Paid in full" group's acts (2026-10-20): the disposition, the mark and its clearing, the correction. */
type PaidInFullAct = 'PIF_DISPOSITION' | 'PIF_MARK' | 'PIF_CLEAR' | 'PIF_CORRECTION';
/** The row acts that are not an OutcomePanel's: each has its own panel, or continues in the case panel. */
const OWN_PANEL_ACTS: ReadonlySet<string> = new Set(['APPROVAL', 'PARTIAL_APPROVAL', 'PARTIAL_COMPLETE', 'CORRECTION_RESEND', 'CORRECTION_OUTSIDE', 'CORRECTION_NOTE', 'DISPOSITION', 'PROBLEM_ACCOUNT', 'DCF_RESUB', 'SUBMISSION', 'RESUB_START',
  'SUBMITTED_DCF', 'FAX_DCF', 'UPLOADED', 'DCF_EMAIL_CREATED', 'BENEFITS_STUCK', 'BENEFITS_LINKED', 'OPEN_PACKET', 'PL_CORRECTION', 'BENEFITS_LOST', 'PL_OUTCOME',
  'PIF_DISPOSITION', 'PIF_MARK', 'PIF_CLEAR', 'PIF_CORRECTION', ...DIRECT_DISPOSITIONS]);

/** Stages a sending can still be recorded from (recordSubmission's own list). */
const SENDABLE_STAGES = ['CREATED', 'NEEDS_DCF_ACCOUNT', 'READY'];
const SUSPENDED_WORDS = ['NO_LONGER_QUALIFIES', 'NEEDS_RECERT', 'DECEASED'];

/**
 * "(STUCK) Can't link benefits" may be recorded from the row: who manages DCF accounts, an open case not approved -- in
 * eMerge or at its stage (APPROVED / HANDED_TO_FINANCE) -- and not already stuck.
 */
export const stuckOffered = (row: Pick<BoardRow, 'stage' | 'dcf_benefits' | 'approval'>, acts: Pick<BoardActsValue, 'canDcf'> | null): boolean =>
  Boolean(acts?.canDcf) && !['CLOSED', 'APPROVED', 'HANDED_TO_FINANCE'].includes(row.stage) && row.dcf_benefits?.state !== 'STUCK' && !isApproved(row);
/** A stuck mark in force may be cleared ("Benefits linked") by who manages DCF accounts, on any open case. */
export const stuckClearable = (row: Pick<BoardRow, 'stage' | 'dcf_benefits'>, acts: Pick<BoardActsValue, 'canDcf'> | null): boolean =>
  Boolean(acts?.canDcf) && row.stage !== 'CLOSED' && row.dcf_benefits?.state === 'STUCK';

/**
 * Gwynn's acts on the row, first in the grid (2026-10-12), each where the server would take it:
 *   UMED UPLOADED TO DCF  a case with its packet prepared (READY) and no sending yet, to who may prepare a submission.
 *                         It opens Confirm DCF upload in the case panel, which says what still holds it (download,
 *                         review, the DCF account, stuck benefits) -- it never records anything by being picked.
 *   DCF EMAIL CREATED     a case whose DCF account is needed and not yet recorded as made, to who manages DCF accounts.
 *   (STUCK) CAN'T LINK BENEFITS   an open case not yet sent and not approved, to who manages DCF accounts;
 *   BENEFITS LINKED               any open case whose stuck mark is in force.
 */
/*
 * SCRUM-126 (2026-11-03): Gwynn faxes from UMEDs to Fax, and a DCF resubmittal Finance handed to UMED never had a FAX
 * TO DCF there (the sendable tiles test the case's own episode: not approved, no reversal open). Its row now offers the
 * way to its packet: FAX TO DCF when the resubmittal's packet (its REVERSAL episode, the case's current one) is READY,
 * "Open packet" with what holds it when it is ACTION_REQUIRED -- most often "Enter previously submitted terms". Neither
 * faxes: each opens the case's packet panel, whose review and Fax to DCF steps stay (Fax to DCF opens its own form and
 * asks before anything is sent). Who may prepare submissions only; never on a closed case or one still with Finance.
 */
export const RESUB_PACKET_HELD_WORDS: Record<string, string> = { REVERSAL_TERMS_UNKNOWN: 'Enter previously submitted terms' };
export function resubmittalFaxTile(row: BoardRow, acts: Pick<BoardActsValue, 'canMarkSubmitted'> | null): ActTile | null {
  const v = row.reversal;
  const prep = row.packet_preparation;
  // Handed to UMED, said by the server (`handed: true`): a payload that does not say it is never taken as handed.
  if (!acts?.canMarkSubmitted || row.stage === 'CLOSED' || !v || v.handed !== true || !prep) return null;
  if (!['REQUESTED', 'NOT_APPROVED'].includes(v.state)) return null;
  if (prep.state === 'READY') return { act: 'FAX_DCF', label: 'FAX TO DCF', hex: null, title: 'Opens this DCF resubmittal\'s packet: review it, then Fax to DCF asks before anything is sent.' };
  if (prep.state === 'ACTION_REQUIRED' && prep.blockers.length) {
    const hold = prep.blockers[0];
    return { act: 'OPEN_PACKET', label: 'Open packet', hex: null, title: `${RESUB_PACKET_HELD_WORDS[hold.code] || 'Its packet is held'}: ${hold.message}` };
  }
  return null;
}
/** What holds a DCF resubmittal's packet, in the row's words (UMEDs to Fax), or null. */
export function resubmittalHeldLine(row: Pick<BoardRow, 'reversal' | 'packet_preparation' | 'stage'>): string | null {
  const prep = row.packet_preparation;
  if (!row.reversal || row.reversal.handed !== true || row.stage === 'CLOSED' || !prep || prep.state !== 'ACTION_REQUIRED' || !prep.blockers.length) return null;
  const hold = prep.blockers[0];
  return `DCF resubmittal packet held: ${RESUB_PACKET_HELD_WORDS[hold.code] ? `${RESUB_PACKET_HELD_WORDS[hold.code]}. ` : ''}${hold.message}`;
}

export function gwynnTilesFor(row: BoardRow, acts: BoardActsValue | null): ActTile[] {
  if (!acts) return [];
  const out: ActTile[] = [];
  const open = row.stage !== 'CLOSED';
  const unsent = Boolean(acts.canMarkSubmitted) && open && !row.submission_recorded && !isApproved(row)
    && !SUSPENDED_WORDS.includes(row.lifecycle || '');
  /*
   * A DCF resubmission (Gwynn, 2026-10-15: "I don't have a UMED submitted button"): the case stays SUBMITTED while the
   * UMED goes to DCF again, so the sendable stages never matched it and its resend could not be recorded from the row.
   * Its sending is the same act (purpose DCF_RESUB, which completes the resubmission); `submission_recorded` is per
   * episode, so it is false until the RESEND is recorded. The server keeps every precondition (a DCF_RESUB packet,
   * reviewed, the DCF account for an upload).
   */
  // Only the resend itself (verification 2026-10-15, F1): not while a correction or reversal is open -- that episode's
  // sending is its own act, and recording it here would close DCF_RESUB under the wrong word -- and only on an
  // eMerge-owned case, where the packet can be prepared here.
  const resending = unsent && row.stage === 'SUBMITTED' && row.creation_reason === 'DCF_RESUB'
    && !row.correction && !row.reversal && row.forward_authority !== 'MONDAY';
  const uploadable = unsent && (SENDABLE_STAGES.includes(row.stage) || resending) && row.packet_preparation?.state === 'READY';
  /*
   * IF THIS THEN THAT, SUB 05/06 (2026-10-13 ruling): the business's own word, UMED SUBMITTED TO DCF, opens a chooser in
   * the case panel -- fax from eMerge, I faxed it outside, I uploaded it -- each going to its existing act. FAX TO DCF
   * focuses the packet panel's Fax to DCF. Neither records anything by being picked; where fax from eMerge is not on, the
   * panel says so. Beside them UMED UPLOADED TO DCF stays (Gwynn, 2026-10-12).
   */
  if (uploadable) {
    out.push({ act: 'SUBMITTED_DCF', label: 'UMED SUBMITTED TO DCF', hex: mondayHex(UMED_STATUS, 'UMED SUBMITTED TO DCF') });
    out.push({ act: 'FAX_DCF', label: 'FAX TO DCF', hex: null });
  } else if (resending) {
    // The resend's packet is not prepared yet: the tile still opens the case at its sending, which says to prepare it first.
    out.push({ act: 'SUBMITTED_DCF', label: 'UMED SUBMITTED TO DCF', hex: mondayHex(UMED_STATUS, 'UMED SUBMITTED TO DCF') });
  }
  if (uploadable) out.push({ act: 'UPLOADED', label: 'UMED UPLOADED TO DCF', hex: mondayHex(UMED_STATUS, 'UMED SUBMITTED TO DCF') });
  // SCRUM-126 (2026-11-03): a DCF resubmittal handed to UMED, as UMEDs to Fax lists it (resubmittalFaxTile).
  const resubFax = resubmittalFaxTile(row, acts);
  if (resubFax) out.push(resubFax);
  const dcf = row.dcf_account;
  if (acts.canDcf && open && dcf && dcf.state !== 'ACCOUNT_CREATED' && (dcf.need === 'NEEDED' || dcf.need === 'ISSUE')) {
    out.push({ act: 'DCF_EMAIL_CREATED', label: 'DCF EMAIL CREATED', hex: null });
  }
  // Stuck is offered on any open case not approved (Josie, 2026-10-15: a submitted case in Review for Approvals whose
  // benefits she cannot reach in the DCF account), as the case panel's DCF account section and the board's DCF account
  // cell offer it -- the server takes it on any open case. A stuck mark in force can always be cleared from here.
  if (stuckClearable(row, acts)) out.push({ act: 'BENEFITS_LINKED', label: 'BENEFITS LINKED', hex: null });
  else if (stuckOffered(row, acts)) out.push({ act: 'BENEFITS_STUCK', label: DCF_BENEFITS_STUCK_LABEL.toUpperCase(), hex: null });
  return out;
}

/**
 * The Sent to DCF cell (ruling, Isaiah Cotto 2026-10-13: "First moves, other tracked"): the date of the first sending,
 * and a small "Fax owed" / "Upload owed" marker while the other step of that sending is still owed (the server's
 * second_channel_owed). A marker only: it moves no case and changes no tab.
 *
 * Josie Evans, R6 (2026-10-28): once a DCF resubmittal has been re-sent, the date must be the LAST submission to DCF, and
 * the row must visibly be a RESUB. The server's last_sending_day (the newest NEW / DCF_RESUB sending, a corrected day
 * honoured) is the main date, tagged "RESUB sent"; the first sending's day (sent_to_dcf_on, which "Change" corrects) is
 * kept beside it in small text, and the tag says how many times and on which days. A case with no re-send reads as before.
 */
export function SentToDcfCell({ row }: { row: BoardRow & { second_channel_owed?: string | null } }) {
  const owed = secondChannelMarker(row.second_channel_owed);
  // Josie Evans, 2026-10-21: how many times a DCF resubmittal has been re-faxed; the days are in the tooltip. Only when it has been.
  const resent = row.dcf_resend_count || 0;
  const resentDays = (row.dcf_resend_dates || []).map((d) => shortDate(d)).join(', ');
  const lastDay = resent > 0 ? row.last_sending_day : null;
  // A corrected re-send after a wrong document is the last sending but not a counted resubmittal: the tooltip names it.
  const correctedLast = lastDay && !(row.dcf_resend_dates || []).some((d) => String(d).slice(0, 10) === String(lastDay).slice(0, 10)) ? shortDate(lastDay) : null;
  const resentTitle = `Re-sent to DCF on ${resentDays}${resent > (row.dcf_resend_dates || []).length ? ' (the latest ten shown)' : ''}${correctedLast ? `; last sent ${correctedLast} (a corrected re-send)` : ''}`;
  return (
    <span>
      {lastDay
        ? <>
            <span data-testid="dcf-last-sent">Sent {shortDate(lastDay)}</span>{' '}
            <span className="um-tag" data-testid="dcf-resub-sent" title={`Resubmitted to DCF. ${resentTitle}.`}>RESUB sent</span>{' '}
            {row.sent_to_dcf_on && <span className="um-sub" data-testid="dcf-first-sent" title="The day of the first sending to DCF, the day Change corrects.">first sent {shortDate(row.sent_to_dcf_on)}</span>}
          </>
        : row.sent_to_dcf_on ? <span>{shortDate(row.sent_to_dcf_on)}</span> : <NotRecorded />}
      {resent > 0 && <>{' '}<span className="um-tag" data-testid="dcf-resend-count" title={resentTitle}>Re-sent {resent}×</span></>}
      {owed && <>{' '}<span className="um-tag" data-testid="second-channel-owed" title="DCF needs both a fax and an upload of the same packet; this one is still owed.">{owed}</span></>}
      <SentDateChange row={row} />
    </span>
  );
}

/**
 * The NOCA / FLMMIS column (Josie Evans, 2026-10-21; Monday's single "NOCA/FLM…" column): two small file icons in one row,
 * so she sees at a glance whether each document is attached, and a click opens it. Filled and a button where the file is
 * on file and she may open documents (the audited URL, exactly as the case's Documents list opens one); hollow, said in
 * words, where it is not on file; muted where eMerge cannot say. The NOCA and FLMMIS columns beside it keep their selects.
 */
function FileGlyph() {
  return (
    <svg viewBox="0 0 16 16" width="11" height="11" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round">
      <path d="M3.5 1.5h6l3 3v10h-9z" /><path d="M9.5 1.5v3h3" />
    </svg>
  );
}
type FileFact = boolean | null | undefined;
export function FileIconsCell({ row }: { row: BoardRow }) {
  const acts = React.useContext(BoardActs);
  const [busy, setBusy] = useState<'NOCA' | 'FLMMIS' | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => { setErr(null); }, [row.id]);
  const name = personName(row);
  const open = async (label: string, docId: string) => {
    setErr(null); setBusy(label as 'NOCA' | 'FLMMIS');
    try {
      const r = await umedApi.documentUrl(row.id, docId);
      if (r.code === 'DOCUMENT_GONE' || r.status === 410) { setErr('eMerge has a record of this document, but the file is not in storage. Tell your manager; it was not deleted by you.'); return; }
      if (r.status === 403 || r.code === 'DENY_NO_CAPABILITY') { setErr('Not permitted to open documents.'); return; }
      if (!r.success || !r.data) { setErr(refusal(r)); return; }
      window.open(r.data.url, '_blank', 'noopener,noreferrer');
    } catch { setErr('Could not reach the server.'); } finally { setBusy(null); }
  };
  const icon = (label: 'NOCA' | 'FLMMIS', fact: FileFact, docId: string | null | undefined) => {
    const state = fact === true ? 'on' : fact === false ? 'off' : 'unk';
    const words = fact === true ? 'on file' : fact === false ? 'not on file' : 'not verified';
    const canOpen = fact === true && Boolean(docId) && Boolean(acts?.canViewDocuments);
    const cls = `um-fileicon ${state}${canOpen ? ' open' : ''}`;
    if (canOpen) {
      return (
        <button type="button" className={cls} disabled={busy !== null} title={`Open the ${label} (${words})`}
          aria-label={`Open the ${label} for ${name}`}
          onClick={(e) => { e.stopPropagation(); void open(label, docId as string); }}>
          <FileGlyph /><span aria-hidden="true">{label === 'NOCA' ? 'N' : 'F'}</span>
        </button>
      );
    }
    return (
      <span className={cls} title={`${label} ${words}`}>
        <FileGlyph /><span aria-hidden="true">{label === 'NOCA' ? 'N' : 'F'}</span>
        <span className="um-sr-only">{label} {words}.</span>
      </span>
    );
  };
  return (
    <span className="um-docs um-fileicons">
      {icon('NOCA', row.documents?.noca, row.noca_document_id)}
      {icon('FLMMIS', row.documents?.flmmis, row.flmmis_document_id)}
      {err && <span className="um-notice stop um-doc-err" role="alert">{err}</span>}
    </span>
  );
}

export const SENT_DATE_EFFECT = 'Corrects the day this UMED went to DCF. The sending itself is kept as recorded; the correction, your reason and your name are kept beside it, and the ten-day approval check is due ten days from the corrected day. Not before the packet was dated, never in the future.';

/**
 * Change the Sent to DCF day (Gwynn, 2026-10-15: "I can't even edit the sent to DCF date"; Isaiah Cotto's ruling the
 * same day): a UMED operator who prepares submissions, or the approval reviewer, with a reason. The case's FIRST
 * sending is the day this column shows; the server keeps every rule (umed/sendingDate.correctSendingDate).
 */
export function SentDateChange({ row }: { row: BoardRow }) {
  const acts = React.useContext(BoardActs);
  const [open, setOpen] = useState(false);
  const [day, setDay] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const key = React.useRef(freshKey());
  const back = useReturnFocus<HTMLButtonElement>(open);
  useOnRowChange(row.id, () => { setOpen(false); setErr(null); });
  if (!(acts?.canMarkSubmitted || acts?.canDispose) || row.stage === 'CLOSED' || !row.first_submission_id || !row.sent_to_dcf_on) return null;
  const name = personName(row);
  const close = () => { back.restore(); setOpen(false); setErr(null); };
  if (!open) {
    return (
      <>{' '}<button type="button" className="um-link" ref={back.opener} data-testid="sent-date-change" aria-label={`Change the first sending's day for ${name}`}
        onClick={(e) => { e.stopPropagation(); setDay(isoDay(row.sent_to_dcf_on)); setReason(''); setOpen(true); }}>Change</button></>
    );
  }
  return (
    <div className="um-rowpanel" role="group" aria-label={`Change the first sending's day for ${name}`} onClick={(e) => e.stopPropagation()}
      onKeyDown={busy ? undefined : escapeCancels(close)}>
      <p className="um-rowpanel-what">{SENT_DATE_EFFECT}</p>
      <label className="um-rowfield"><span>Day it went to DCF</span>
        <input type="date" value={day} disabled={busy} autoFocus onChange={(e) => setDay(e.target.value)} aria-label="Day it went to DCF" /></label>
      <label className="um-rowfield"><span>Why the recorded day is wrong (required)</span>
        <textarea rows={2} maxLength={500} value={reason} disabled={busy} onChange={(e) => setReason(e.target.value)} aria-label="Why the recorded day is wrong" /></label>
      {err && <p className="um-notice stop" role="alert">{err}</p>}
      <div className="um-rowpanel-acts">
        <button type="button" className="um-open um-primary" disabled={busy || !day || reason.trim().length < 3} aria-describedby={(firstReason([!day, 'Choose the day it was sent first.'], [reason.trim().length < 3, 'Say why (at least 3 characters) first.'])) ? `why-sent-date-${row.id}` : undefined} onClick={async () => {
          setBusy(true); setErr(null);
          try {
            const r = await umedApi.correctSendingDate(row.id, row.first_submission_id as string, { corrected_on: day, reason: reason.trim(), idempotency_key: key.current });
            if (!r.success) { setErr(refusal(r)); return; }
            key.current = freshKey();
            setOpen(false);
            acts?.announce(`First sending for ${name} corrected to ${shortDate(day)}.`, row.id, { focusTo: () => back.opener.current });
          } catch { setErr('Could not reach the server. Nothing was recorded.'); } finally { setBusy(false); }
        }}>{busy ? 'Recording…' : 'Record the corrected day'}</button><Why id={`why-sent-date-${row.id}`} reason={firstReason([!day, 'Choose the day it was sent first.'], [reason.trim().length < 3, 'Say why (at least 3 characters) first.'])} className="um-text um-why" />
        <button type="button" className="um-link" disabled={busy} onClick={close}>Cancel</button>
      </div>
    </div>
  );
}

/* ── Approval is an explicit act (Josie Evans, 2026-10-06) ───────────── */

/**
 * "I want to be able to click down on FLMMIS reflecting, but I do not want it
 * to move to approval unless I click UMED status and then move it to
 * approval."
 *
 * So FLMMIS reflected is recorded as evidence (FlmmisCell, with its own
 * in-row confirmation), and the approval is THIS: one clear button, offered
 * only when FLMMIS is on record and nobody has approved the case yet, that
 * says what it will do before it does it. No browser dialog -- a confirmation
 * in the row, where her eyes already are, with the consequence written out.
 *
 * APPROVED chosen from the status menu opens this same confirmation
 * (`startConfirming`), so both ways in say one sentence and do one thing
 * (design verification 2026-10-06, P2-6).
 *
 * Exported so the case panel offers the same act with the same words.
 */
export const APPROVE_EFFECT = (name: string) => `Approves ${name}: the status becomes APPROVED and the Finance handoff opens.`;
export const FLMMIS_EFFECT = 'Records FLMMIS as reflected. The case is not approved until you move it to Approved.';
/** What the approval act requires on record (2026-10-13: NOCA received AND FLMMIS reflected), said where Approve is not offered yet. */
export const APPROVE_NEEDS_EVIDENCE = 'Record NOCA received and FLMMIS reflected first.';
/** The server's two gate refusals, in words. */
const approvalGateWords = (code?: string): string | null => (code === 'FLMMIS_REQUIRED' ? 'Record FLMMIS reflected first.'
  : code === 'NOCA_REQUIRED' ? 'Record NOCA received first.' : null);
/** Where the next act is, said in the FLMMIS confirmation on the row (P1-a). */
export const FLMMIS_NEXT = 'Approve it in UMED status when ready.';

export function ApproveAct({ row, onDone, compact = false, startConfirming = false, onCancel, focusCommit = true }: {
  row: Pick<BoardRow, 'id' | 'first_name' | 'last_name' | 'approval' | 'stage'>;
  onDone: (msg: string, outcome?: ApprovedOutcome) => void;
  compact?: boolean;
  /** Open straight on the confirmation (the status menu's APPROVED). */
  startConfirming?: boolean;
  /** Who takes focus back on Cancel or Escape, when the opener is not this button. */
  onCancel?: () => void;
  /**
   * Whether the confirmation's Approve button takes focus when it opens (default, as before). False when the board's
   * Kanban opened it (2026-10-14): focus then lands on the sentence, never on the button that commits, so a key
   * repeat after a keyboard drop cannot approve; caseAnchor moves it there only if the person has not moved on.
   */
  focusCommit?: boolean;
}) {
  const [confirming, setConfirming] = useState(startConfirming);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const opener = React.useRef<HTMLButtonElement | null>(null);
  const refocus = React.useRef(false);
  useEffect(() => { setConfirming(startConfirming); setErr(null); }, [row.id, startConfirming]);
  // Cancel puts focus back on the button that opened the confirmation, never on the page body (P2-1).
  useEffect(() => {
    if (!confirming && refocus.current) { refocus.current = false; opener.current?.focus(); }
  }, [confirming]);
  const name = personName(row);
  if (row.approval.canonical === 'PROVEN' || row.stage === 'CLOSED') return null;
  const ready = Boolean(row.approval.ready_to_approve);
  const cancel = () => {
    setErr(null);
    if (onCancel) { onCancel(); return; }
    refocus.current = true;
    setConfirming(false);
  };

  if (!confirming) {
    return (
      <span className={`um-approve${compact ? ' compact' : ''}`} onClick={(e) => e.stopPropagation()}>
        <button type="button" className="um-approve-btn" disabled={!ready} ref={opener}
          aria-label={`Approve ${name}`}
          title={ready ? 'Move this case to Approved.' : APPROVE_NEEDS_EVIDENCE}
          onClick={() => { setErr(null); setConfirming(true); }}>
          Approve
        </button>
        {!ready && !compact && <span className="um-sub">{APPROVE_NEEDS_EVIDENCE}</span>}
      </span>
    );
  }
  return (
    <div className="um-rowpanel um-confirm" role="group" aria-label={`Approve ${name}`}
      onClick={(e) => e.stopPropagation()} onKeyDown={busy ? undefined : escapeCancels(cancel)}>
      <p className="um-rowpanel-what" tabIndex={-1} data-act-focus="approve">{APPROVE_EFFECT(name)}</p>
      {err && <p className="um-notice stop" role="alert">{err}</p>}
      <div className="um-rowpanel-acts">
        <Why id={`why-approve-${row.id}`} reason={!ready && APPROVE_NEEDS_EVIDENCE} className="um-text um-why" />
        <button type="button" className="um-open um-primary" disabled={!ready} aria-disabled={busy} autoFocus={focusCommit} aria-describedby={!ready ? `why-approve-${row.id}` : undefined}
          onClick={async () => {
            // aria-disabled, not disabled, while busy: a disabled button drops focus to the page body, and a refusal then leaves her nowhere.
            if (busy) return;
            setBusy(true); setErr(null);
            try {
              const r = await umedApi.approve(row.id);
              if (!r.success) { setErr(approvalGateWords(r.code) || refusal(r)); return; }
              setConfirming(false);
              const o = approvedOutcomeOf(r.data);
              onDone(approvedSentence(name, o), o);
            } catch { setErr('Could not reach the server. Nothing was recorded.'); } finally { setBusy(false); }
          }}>{busy ? 'Approving…' : 'Approve'}</button>
        <button type="button" className="um-link" disabled={busy} onClick={cancel}>Cancel</button>
      </div>
    </div>
  );
}

/**
 * APPROVED where the approval's evidence is not on record yet (Josie Evans, 2026-10-09; Isaiah Cotto, 2026-10-10).
 *
 * 2026-10-13 (Kaleigh Vazquez & Josie Evans, "PA Sale to Approval - IF THIS THEN THAT", 2026-10-08, Process 2 Step 3:
 * "IF NOCA is Received - but Josie checks FLMMIS and there IS a reflection of update PL THEN - Josie would Mark the Lead
 * Status as 'APPROVED'"; Isaiah Cotto's ruling the same day): approval rests on NOCA received AND FLMMIS reflected,
 * and approval is her act. So the tile opens the two acts it takes, in order, in one panel, each with its consequence
 * written above its own button:
 *   1. Record NOCA received and FLMMIS reflected -- BOTH facts in ONE review (writeApprovalEvidence), whichever is
 *      missing. This alone approves nothing.
 *   2. Approve -- the SAME call as ApproveAct (POST /approve), enabled only once step 1 is recorded, and only on her
 *      second click. Nothing chains: recording the evidence never approves, and the server refuses an approval without
 *      both on record (NOCA_REQUIRED / FLMMIS_REQUIRED), which puts her back on step 1.
 * Cancel after step 1 leaves the evidence recorded and the case not approved, and says so.
 */
export const TWO_STEP_STEP1 = 'Record NOCA received and FLMMIS reflected';
/** The panel's first line: what is not on record, in words ("FLMMIS reflected", "NOCA received", or both). */
export const twoStepIntro = (missing: string[]) => `${missing.length ? missing.join(' and ') : 'The approval evidence'} ${missing.length > 1 ? 'are' : 'is'} not on record, and approval needs NOCA received and FLMMIS reflected. Two steps, each confirmed on its own:`;
export const EVIDENCE_EFFECT = 'Records NOCA as received and FLMMIS as reflected, in one review. The case is not approved until you move it to Approved.';
export const TWO_STEP_EVIDENCE_DONE = 'NOCA received and FLMMIS reflected are recorded. The case is not approved yet.';
export const TWO_STEP_CANCEL_AFTER = 'Closing now leaves NOCA received and FLMMIS reflected recorded and the case not approved.';
/** Said when the server refuses the approval for want of either fact: nothing approved, back to step 1. */
export const TWO_STEP_NOT_ON_RECORD = 'NOCA received and FLMMIS reflected are not both on record. Record them first; nothing was approved.';
/** Said in step 1 when FLMMIS is on record as an exception (#373, P3-3): the step replaces it. */
export const TWO_STEP_REPLACES_EXCEPTION = 'FLMMIS is recorded as an exception. Recording it as reflected replaces that.';

export function ApproveInTwoSteps({ row, onDone, onCancel }: {
  row: Pick<BoardRow, 'id' | 'first_name' | 'last_name' | 'approval' | 'stage'>;
  onDone: (msg: string, outcome?: ApprovedOutcome) => void;
  onCancel: () => void;
}) {
  const acts = React.useContext(BoardActs);
  const [evidenceDone, setEvidenceDone] = useState(false);
  const [busy, setBusy] = useState<'EVIDENCE' | 'APPROVE' | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const evidenceBtn = React.useRef<HTMLButtonElement | null>(null);
  const approveBtn = React.useRef<HTMLButtonElement | null>(null);
  const moved = React.useRef(false);
  // A second click in the same tick, before `busy` has rendered, is not a second act (#373, P3-4).
  const inFlight = React.useRef(false);
  const name = personName(row);
  // What is missing, read once as the panel opens: the re-read board row must not rewrite the panel under her.
  const [missing] = useState(() => approvalEvidenceMissing(row));
  // Focus follows the step: to Approve once the evidence is recorded, back to step 1 if the server says it is not on record.
  useEffect(() => {
    if (evidenceDone) { moved.current = true; approveBtn.current?.focus(); } else if (moved.current) evidenceBtn.current?.focus();
  }, [evidenceDone]);

  const recordEvidence = async () => {
    if (inFlight.current || busy || evidenceDone) return;
    inFlight.current = true;
    setBusy('EVIDENCE'); setErr(null);
    try {
      const refused = await writeApprovalEvidence(row);
      if (refused) { setErr(refused); return; }
      setEvidenceDone(true);
      // The board re-reads so the NOCA and FLMMIS columns say what was recorded; focus stays in this panel.
      acts?.announce(`NOCA received and FLMMIS reflected are recorded for ${name}. It is not approved yet.`, row.id, { keepFocus: true });
    } catch { setErr('Could not reach the server. Nothing was recorded.'); } finally { inFlight.current = false; setBusy(null); }
  };
  const approve = async () => {
    if (inFlight.current || busy || !evidenceDone) return;
    inFlight.current = true;
    setBusy('APPROVE'); setErr(null);
    try {
      const r = await umedApi.approve(row.id);
      if (!r.success) {
        if (r.code === 'FLMMIS_REQUIRED' || r.code === 'NOCA_REQUIRED') { setEvidenceDone(false); setErr(TWO_STEP_NOT_ON_RECORD); return; }
        setErr(refusal(r)); return;
      }
      const o = approvedOutcomeOf(r.data);
      onDone(approvedSentence(name, o), o);
    } catch { setErr('Could not reach the server. Nothing was approved.'); } finally { inFlight.current = false; setBusy(null); }
  };
  const stepHint = `um-twostep-hint-${row.id}`;

  return (
    <div className="um-rowpanel um-confirm um-twostep" role="group" aria-label={`Approve ${name} in two steps`}
      onClick={(e) => e.stopPropagation()} onKeyDown={busy ? undefined : escapeCancels(onCancel)}>
      <p className="um-rowpanel-what">{twoStepIntro(missing)}</p>
      <ol className="um-steps">
        <li className={`um-step${evidenceDone ? ' done' : ''}`}>
          <span className="um-step-name">1. {TWO_STEP_STEP1}</span>
          {evidenceDone ? (
            <p className="um-step-what">{TWO_STEP_EVIDENCE_DONE}</p>
          ) : (
            <>
              <p className="um-step-what">
                {row.approval.flmmis === 'EXCEPTION' && <>{TWO_STEP_REPLACES_EXCEPTION} </>}{EVIDENCE_EFFECT}
              </p>
              <button type="button" className="um-open um-primary" ref={evidenceBtn} autoFocus aria-disabled={busy !== null}
                onClick={recordEvidence}>{busy === 'EVIDENCE' ? 'Recording…' : TWO_STEP_STEP1}</button>
            </>
          )}
        </li>
        <li className={`um-step${evidenceDone ? '' : ' waiting'}`}>
          <span className="um-step-name">2. Approve <FilledStatusChip word="APPROVED" column={UMED_STATUS} size="sm" /></span>
          <p className="um-step-what">{APPROVE_EFFECT(name)}</p>
          {!evidenceDone && <p className="um-step-hint" id={stepHint}>{APPROVE_NEEDS_EVIDENCE}</p>}
          {/* aria-disabled while busy (a refusal must leave focus here); disabled until step 1 is on record. */}
          <button type="button" className="um-open um-primary" ref={approveBtn} disabled={!evidenceDone}
            aria-disabled={busy !== null} aria-describedby={evidenceDone ? undefined : stepHint}
            onClick={approve}>{busy === 'APPROVE' ? 'Approving…' : 'Approve'}</button>
        </li>
      </ol>
      {err && <p className="um-notice stop" role="alert">{err}</p>}
      {evidenceDone && <p className="um-step-hint">{TWO_STEP_CANCEL_AFTER}</p>}
      <div className="um-rowpanel-acts">
        <button type="button" className="um-link" disabled={busy !== null} onClick={onCancel}>{evidenceDone ? 'Close' : 'Cancel'}</button>
      </div>
    </div>
  );
}

/**
 * Monday says a later stage than eMerge (Josie Evans, 2026-10-06: "He was under
 * Ready to submit in eMerge but under Review for Approvals in Monday").
 *
 * Monday no longer moves a stage eMerge owns, so a sending recorded only on the
 * board never reaches the case. The marker says so, in words; the one-click act
 * beside it records the sending as HER act, under her name -- never by itself.
 * The server reads Monday's word itself and refuses when the board does not say
 * it went to DCF (verification P2-B); nothing here sends a word.
 */
export const MONDAY_EXPLAIN = 'Monday\'s word for this UMED is further along than eMerge\'s stage. eMerge does not take stages from Monday; record it here if it is true.';

/** When only the board's sending date makes the item this work's (final verification P2-3). */
export const MONDAY_ITEM_FIRST_SEEN_BEFORE = 'This Monday item was first seen before this case opened. Make sure it is this UMED.';

/**
 * The board item "Monday says" is read from, named in the confirm panel (P2-3): its id, the board's own
 * sending date, and the day eMerge first saw it -- so she can check it is THIS UMED before recording it.
 */
export function mondayItemLine(item: MondayItem): string {
  const sent = item.sent_to_dcf_date ? `sent to DCF ${shortDate(item.sent_to_dcf_date)}`
    : item.submitted_date ? `submitted ${shortDate(item.submitted_date)}` : 'no sending date on the board';
  const seen = item.first_seen_on || item.first_seen_at;
  return `Monday item ${item.monday_item_id || 'not recorded'} · ${sent} · first seen ${seen ? shortDate(seen) : 'date not recorded'}`;
}

/**
 * Accept Monday's word (ruling #10, Isaiah Cotto, 2026-10-07; Josie's point 10: SUBMITTED in eMerge while Monday
 * says REVERSAL). One click on the marker opens the native act eMerge records that word with -- the SAME act, panel
 * and server call as the status tile (a reversal asks who and why, as it always does) -- under her name. Nothing is
 * written until she records it in that panel, and eMerge never applies the word on its own.
 */
export const ACCEPT_MONDAY_EXPLAIN = 'Opens the eMerge act that records this word, under your name. Nothing changes until you record it.';
function AcceptMondayWord({ word, name, onAccept }: { word: string; name: string; onAccept: () => void }) {
  return (
    <button type="button" className="um-link um-acceptmonday" title={ACCEPT_MONDAY_EXPLAIN}
      aria-label={`Accept Monday's word ${word} for ${name}: opens the eMerge act`}
      onClick={(e) => { e.stopPropagation(); onAccept(); }}>
      Accept Monday&rsquo;s word
    </button>
  );
}

/** What the word-only marker means (director's ruling, 2026-10-06): Monday moved on; nothing here follows it. */
export const MONDAY_WORD_EXPLAIN = 'Monday now says this word for the same work. eMerge has not recorded it, and nothing moves on its own: record the act here if it is right.';

export function MondayDiffers({ row, canMark, onDone, explain = false, compact = false, accept = null }: {
  row: Pick<BoardRow, 'id' | 'first_name' | 'last_name' | 'monday_differs'> & { monday_word_differs?: BoardRow['monday_word_differs']; packet_preparation?: BoardRow['packet_preparation'] };
  canMark: boolean;
  onDone: (msg: string) => void;
  /** Say why in visible words (the case panel has the room; the row keeps it short). P2-9. */
  explain?: boolean;
  /** In the row's status cell, beside the UMED status: one line, the act beside the chip (2026-10-06, P2-6). */
  compact?: boolean;
  /**
   * Ruling #10 (Isaiah Cotto, 2026-10-07): "Accept Monday's word" -- opens the SAME native act a status tile opens
   * for that word (acceptActFor). Absent where no native act maps to the word or the act is not allowed here.
   */
  accept?: { onAccept: () => void } | null;
}) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const opener = React.useRef<HTMLButtonElement | null>(null);
  const refocus = React.useRef(false);
  useEffect(() => { setConfirming(false); setErr(null); }, [row.id]);
  useEffect(() => {
    if (!confirming && refocus.current) { refocus.current = false; opener.current?.focus(); }
  }, [confirming]);
  const d = row.monday_differs;
  if (!d) {
    // Monday says another word for the same work (not a later stage): shown beside eMerge's word, never applied.
    const w = row.monday_word_differs;
    if (!w) return null;
    return (
      <span className={`um-mondaydiff${compact ? ' compact' : ''}`} onClick={(e) => e.stopPropagation()}>
        <span className="um-tag attn" title={MONDAY_WORD_EXPLAIN}>Monday says: {w.word}</span>
        {explain && <span className="um-mondaydiff-why">{MONDAY_WORD_EXPLAIN}</span>}
        {accept && <AcceptMondayWord word={w.word} name={personName(row)} onAccept={accept.onAccept} />}
      </span>
    );
  }
  const name = personName(row);
  const cancel = () => { setErr(null); refocus.current = true; setConfirming(false); };
  const markable = canMark && d.can_mark_submitted;
  return (
    <span className={`um-mondaydiff${compact ? ' compact' : ''}`} onClick={(e) => e.stopPropagation()}>
      <span className="um-tag attn" title={MONDAY_EXPLAIN}>
        Monday says: {d.word}
      </span>
      {explain && <span className="um-mondaydiff-why">{MONDAY_EXPLAIN}</span>}
      {markable && !confirming && (
        <button type="button" className="um-link" ref={opener} onClick={() => { setErr(null); setConfirming(true); }}
          aria-label={`Mark ${name} submitted to DCF`}>Mark submitted to DCF</button>
      )}
      {/* A sending word has its own act above (Mark submitted); any other word Monday is further along with may be accepted. */}
      {accept && !(canMark && d.can_mark_submitted) && !confirming && <AcceptMondayWord word={d.word} name={name} onAccept={accept.onAccept} />}
      {confirming && (
        <div className="um-rowpanel um-confirm" role="group" aria-label={`Mark ${name} submitted to DCF`}
          onKeyDown={busy ? undefined : escapeCancels(cancel)}>
          <p className="um-rowpanel-what">
            Moves {name} to UMED submitted under your name and starts the ten-day check. Nothing is sent.
          </p>
          {d.item && <p className="um-rowpanel-item">{mondayItemLine(d.item)}</p>}
          {d.item?.matched_by === 'SENDING_DATE' && <p className="um-rowpanel-warn" role="note">{MONDAY_ITEM_FIRST_SEEN_BEFORE}</p>}
          {err && <p className="um-notice stop" role="alert">{err}</p>}
          <div className="um-rowpanel-acts">
            <button type="button" className="um-open um-primary" aria-disabled={busy} autoFocus onClick={async () => {
              if (busy) return;
              setBusy(true); setErr(null);
              try {
                const r = await umedApi.markSubmitted(row.id);
                if (!r.success) { setErr(refusal(r)); return; }
                setConfirming(false);
                onDone(`${name} is recorded as submitted to DCF.`);
              } catch { setErr('Could not reach the server. Nothing was recorded.'); } finally { setBusy(false); }
            }}>{busy ? 'Recording…' : 'Mark submitted'}</button>
            <button type="button" className="um-link" disabled={busy} onClick={cancel}>Cancel</button>
          </div>
        </div>
      )}
    </span>
  );
}

/**
 * DCF RESUB NEEDED, the board's lime-green move (Josie Evans, 2026-10-06):
 * "after 10-14 days I leave Gwynn a note and click DCF RESUB NEEDED". One
 * click and an optional note; Gwynn (whoever submits) is told in-app with the
 * note; the case moves to the DCF Resub section and STAYS in Review for
 * Approvals. Offered only where the server will take it: not approved, not
 * closed, FLMMIS not reflected, no reversal or correction open, and not
 * already DCF RESUB NEEDED.
 */
/*
 * Who is told (verification of 676fdb9, P2-4): the case's submitter AND every holder of umed.submission.prepare in
 * the building, so the panel names the role, not one person.
 */
export const DCF_RESUB_EFFECT = 'The submitter, and everyone who prepares submissions for this building, is told in eMerge, with your note, to send the UMED to DCF again. The case moves to the DCF RESUB NEEDED section and stays in Review for Approvals. Reactivate undoes it.';
/** The word is on the case but no move was made here (Monday's word, or an older review's): nobody was told in eMerge. */
export const dcfResubWordOnly = (row: Pick<BoardRow, 'source_status_raw' | 'dcf_resub_moved'>): boolean =>
  normalizeMondayWord(row.source_status_raw) === 'DCF RESUB NEEDED' && !row.dcf_resub_moved;
export function dcfResubOffered(row: BoardRow, canDispose: boolean): boolean {
  if (!canDispose || row.stage === 'CLOSED' || isApproved(row)) return false;
  if (row.reversal || row.correction) return false;
  // The move already made is not offered twice; a word nobody acted on here IS offered the move (P2-1).
  if (row.dcf_resub_moved) return false;
  // The server's own answer, and the only one: it reads the real FLMMIS, which a viewer without approval detail sees as WITHHELD
  // (verification of PR #359, P2). A row that does not carry it offers nothing.
  if (!row.resubmission || !row.resubmission.can_open) return false;
  const openBoardException = row.exception && row.exception !== 'NONE' && row.exception !== 'CROSSOVER' && row.exception !== 'RESUBMISSION' && row.exception_state !== 'COMPLETE';
  return !openBoardException;
}

function DcfResubPanel({ row, onClose }: { row: BoardRow; onClose: () => void }) {
  const acts = React.useContext(BoardActs);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const key = React.useRef(freshKey());
  const save = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await umedApi.dcfResubmission(row.id, { note: note.trim() || null, expected_updated_at: row.updated_at || null, idempotency_key: key.current });
      if (!r.success) { setErr(refusal(r)); return; }
      onClose();
      const told = r.data && r.data.notified ? r.data.notified.recipients : null;
      acts?.announce(`${personName(row)} is DCF RESUB NEEDED.${told ? ' The submitter has been told.' : ''} The case stays in Review for Approvals.`, row.id);
    } catch { setErr('Could not reach the server. Nothing was recorded.'); } finally { setBusy(false); }
  };
  return (
    <div className="um-rowpanel um-resubpanel" role="group" aria-label={`Move ${personName(row)} to DCF RESUB NEEDED`}
      onClick={(e) => e.stopPropagation()} onKeyDown={busy ? undefined : escapeCancels(onClose)}>
      <p className="um-rowpanel-what">
        <FilledStatusChip word="DCF RESUB NEEDED" column={UMED_STATUS} size="sm" /> {DCF_RESUB_EFFECT}
      </p>
      <label className="um-rowfield"><span>Note to the submitter</span>
        <textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={2000} rows={2} disabled={busy}
          placeholder="Optional: what DCF said, or what to send" aria-label="Note to the submitter (optional)" />
      </label>
      {err && <p className="um-notice stop" role="alert">{err}</p>}
      <div className="um-rowpanel-acts">
        <button type="button" className="um-open um-primary" disabled={busy} onClick={save}>{busy ? 'Recording…' : 'Move to DCF RESUB NEEDED'}</button>
        <button type="button" className="um-link" disabled={busy} onClick={onClose}>Cancel</button>
      </div>
    </div>
  );
}

/*
 * REVERSAL / DCF RESUBMITTAL… (Isaiah Cotto, 2026-10-19: "Josie, Gwynn, Dominique and April can begin DCF resubmittals").
 *
 * Josie and Gwynn start one from the row: POST /umed/cases/:id/reversals, the same start as Finance's board, handed to
 * UMED in the same act (UMED is who sends it). Offered where the server would take it: who holds umed.reversal.manage,
 * on an approved case (its stage APPROVED or HANDED_TO_FINANCE -- an approval that is only the Monday board's word
 * counts, as it does for Finance -- or eMerge's own approval) and, since 2026-10-29, on UMED SUBMITTED TO DCF (a DCF
 * resubmission sent again included) and SFS CORRECTION (resubmittalStartStatus: the server's per-row answer), with no
 * resubmittal open (Finance's or UMED's) and no correction open but an SFS correction. The form writes nothing until its
 * confirmation, which says what will happen (on a case not approved: no Finance handoff to amend); a refusal is said in
 * the server's words. A case at SUBMITTED with the board's REVERSAL word is not offered it -- eMerge holds no approval
 * and it is in none of the four statuses -- and the status cell says so in one line (resubmittalNotApprovedNote).
 */
// 2026-10-20: "REVERSAL / " leads -- Dominique looks for the word reversal in the menu, and a full reversal is one kind.
export const RESUB_START_LABEL = 'REVERSAL / DCF RESUBMITTAL…';

/**
 * The DCF account cell's "Not required" (2026-10-19): on a SUBMITTED case whose DCF account record is Monday's, with no
 * email shown, the NOT_REQUIRED stamp only says nobody ticked the board's no-account box -- so the cell says "None
 * recorded". Every other case keeps "Not required" (CREATED and READY are eMerge's own preparation, where the stamp means it).
 */
export const DCF_NONE_RECORDED = 'None recorded';
export const notRequiredWords = (r: Pick<BoardRow, 'stage' | 'dcf_account'>): string =>
  (r.stage === 'SUBMITTED' && r.dcf_account?.source === 'MONDAY' && !r.dcf_account?.email ? DCF_NONE_RECORDED : 'Not required');
/*
 * Where it is offered (Isaiah Cotto, 2026-10-29, in chat, amending REV 01): on APPROVED, UMED SUBMITTED TO DCF (DCF
 * SUBMITTED, and a DCF resubmission sent again -- RESUB DCF SUBMITTED) and SFS CORRECTION (SFS CORRECTION SUBMITTED and
 * the correction review included). The server says which, per row (`reversal_start_status`, resubmittalState.
 * START_STATUS_SQL -- the start's own rule); a row from an older server falls back to REV 01's approved test.
 */
const RESUB_START_STAGES = ['APPROVED', 'HANDED_TO_FINANCE'];
export function resubmittalStartStatus(row: BoardRow): ReversalStartStatus | null {
  if (row.reversal_start_status !== undefined) return row.reversal_start_status;
  return (RESUB_START_STAGES.includes(row.stage) || isApproved(row)) && !row.resubmittal_pif_only ? 'APPROVED' : null;
}
/** An open correction that still holds the start: any but an SFS correction (the FLMMIS-issue or paid-in-full kinds). */
const correctionHoldsStart = (row: BoardRow): boolean => Boolean(row.correction) && row.correction?.correction_type !== 'SFS_CORRECTION';
export function resubmittalStartOffered(row: BoardRow, acts: Pick<BoardActsValue, 'canReverse'> | null): boolean {
  if (!acts?.canReverse || row.stage === 'CLOSED') return false;
  if (!resubmittalStartStatus(row)) return false;
  // Paid in full here before any approval: HANDED_TO_FINANCE without an approval -- the server refuses it (its one predicate).
  if (row.resubmittal_pif_only) return false;
  // 2026-10-29: an open SFS correction no longer holds it -- SFS CORRECTION is one of the ruling's statuses.
  return !row.reversal && !correctionHoldsStart(row) && !partialApprovalOpen(row);
}
/** The words each start status is said in (resubmittalState.START_STATUS_WORDS). */
export const RESUB_START_STATUS_WORDS: Record<ReversalStartStatus, string> = {
  APPROVED: 'APPROVED',
  DCF_SUBMITTED: 'UMED SUBMITTED TO DCF',
  RESUB_DCF_SUBMITTED: 'UMED SUBMITTED TO DCF (a DCF resubmission sent again)',
  SFS_CORRECTION: 'SFS CORRECTION',
};
/** The board's REVERSAL word on a case eMerge has not approved: said, so nobody looks for a start that cannot work. */
export const RESUB_NOT_APPROVED_NOTE = 'Monday says REVERSAL, but eMerge holds no approval for this case and it is not at UMED SUBMITTED TO DCF or SFS CORRECTION, so a reversal / DCF resubmittal cannot start yet. If DCF approved it, record the approval first.';
export function resubmittalNotApprovedNote(row: BoardRow, acts: Pick<BoardActsValue, 'canReverse'> | null): string | null {
  if (!acts?.canReverse || row.stage === 'CLOSED' || row.reversal) return null;
  if (resubmittalStartStatus(row) || row.resubmittal_pif_only) return null;
  const word = (row.exception === 'REVERSAL' && row.exception_state !== 'COMPLETE') || normalizeMondayWord(row.source_status_raw) === 'REVERSAL';
  return word ? RESUB_NOT_APPROVED_NOTE : null;
}
/*
 * 2026-10-20: where who may start one finds no REVERSAL / DCF RESUBMITTAL… tile on a case it could otherwise take, one
 * line says why (the same predicates as resubmittalStartOffered, in its order). A case in none of the four statuses says
 * so only where Monday's REVERSAL word makes someone look for it (RESUB_NOT_APPROVED_NOTE) -- not on every case still on
 * its way to DCF.
 */
export const RESUB_PIF_ONLY_NOTE = 'Paid in full before any approval, so there is no approval to reverse: a reversal / DCF resubmittal cannot start.';
export const RESUB_ALREADY_OPEN_NOTE = 'A reversal / DCF resubmittal is already open on this case. Another can start once it is finished or withdrawn.';
export const RESUB_CORRECTION_OPEN_NOTE = 'An SFS CORRECTION-FLMMIS ISSUE or a paid in full correction is open on this case. A reversal / DCF resubmittal can start once the correction is complete.';
export const RESUB_PARTIAL_OPEN_NOTE = 'This partial approval is still waiting on its correction. A reversal / DCF resubmittal can start once APPROVED completes it.';
export function resubmittalWhyNotNote(row: BoardRow, acts: Pick<BoardActsValue, 'canReverse'> | null): { note: string; notApproved: boolean } | null {
  if (!acts?.canReverse || row.stage === 'CLOSED' || resubmittalStartOffered(row, acts)) return null;
  if (row.resubmittal_pif_only) return { note: RESUB_PIF_ONLY_NOTE, notApproved: false };
  if (!resubmittalStartStatus(row)) {
    const note = resubmittalNotApprovedNote(row, acts);
    return note ? { note, notApproved: true } : null;
  }
  if (row.reversal) return { note: RESUB_ALREADY_OPEN_NOTE, notApproved: false };
  if (correctionHoldsStart(row)) return { note: RESUB_CORRECTION_OPEN_NOTE, notApproved: false };
  if (partialApprovalOpen(row)) return { note: RESUB_PARTIAL_OPEN_NOTE, notApproved: false };
  return null;
}
export const RESUB_KIND_HELP: Record<ResubmittalKind, string> = {
  FULL: 'Full reversal: unwind the claim and reverse all months back to the normal patient liability.',
  CORRECTED: 'Corrected: the claim stands at corrected terms. Type the corrected terms below.',
};
const RESUB_KIND_LABEL: Record<ResubmittalKind, string> = { FULL: 'Full reversal', CORRECTED: 'Corrected' };
const RESUB_REQUESTERS: ReversalRequester[] = ['FACILITY', 'RESPONSIBLE_PARTY', 'DCF', 'SFS', 'OTHER'];
/**
 * What the start does, said on its confirmation before anything is written. 2026-10-29: on a case not approved (UMED
 * SUBMITTED TO DCF, a DCF resubmission sent again, SFS CORRECTION) there is no Finance handoff, and the sentence says so
 * -- DCF's answer amends nothing in Finance; a corrected one, once DCF accepts it, reads UMED SUBMITTED TO DCF again
 * until the approval is recorded; an open SFS correction waits until the resubmittal is finished.
 */
export function resubmittalStartEffect(kind: ResubmittalKind, name: string, terms: string | null, status: ReversalStartStatus | null = 'APPROVED'): string {
  const approved = !status || status === 'APPROVED';
  const what = kind === 'FULL'
    ? (approved
      ? `Starts a full DCF resubmittal for ${name}: the claim is unwound and every month is reversed back to the normal patient liability.`
      : `Starts a full reversal for ${name}: the claim is withdrawn before any approval, back to the normal patient liability.`)
    : (approved
      ? `Starts a corrected DCF resubmittal for ${name}: the claim stands at the corrected terms${terms ? ` (${terms})` : ''}.`
      : `Starts a corrected DCF resubmittal for ${name}: DCF is sent the corrected UMED${terms ? ` (${terms})` : ''}; once DCF accepts it the status is UMED SUBMITTED TO DCF again until the approval is recorded.`);
  const noHandoff = approved ? ''
    : ` This case is at ${RESUB_START_STATUS_WORDS[status as ReversalStartStatus]} with no approval, so it has no Finance handoff of its own: Finance is only told if this resident's episode already has a handoff, which DCF's answer then amends.`
      + (status === 'SFS_CORRECTION' ? ' The open SFS correction waits until this resubmittal is finished or withdrawn.' : '');
  return `${what} It is with UMED at once: the packet is prepared, whoever makes packets is told, and Finance sees it on its DCF RESUBMIT board.${noHandoff} `
    + `The status becomes ${kind === 'FULL' ? 'REVERSAL' : 'RESUBMITTAL'}. It can be withdrawn until it is sent.`;
}

/** The panel's first sentence, before Review…: what is being reversed (2026-10-29: not always an approved UMED). */
export function resubmittalStartIntro(status: ReversalStartStatus | null): string {
  if (!status || status === 'APPROVED') return 'Starts a DCF resubmittal of this approved UMED. Nothing is recorded until you confirm.';
  return `Starts a reversal / DCF resubmittal of this UMED at ${RESUB_START_STATUS_WORDS[status]}. It is not approved, so it has no Finance handoff of its own; Finance is only told if the episode already has one. Nothing is recorded until you confirm.`;
}

/** Where a resubmittal stands, in the words the result line uses (a replay says where it has got to since). */
const RESUB_STATE_WORDS: Record<string, string> = {
  WITH_FINANCE: 'It is with Finance.', WITH_UMED: 'It is with UMED; the packet is being prepared.', WITH_DCF: 'It has been sent to DCF.',
  DCF_DECLINED: 'DCF declined it.', APPROVED: 'DCF approved it.', WITHDRAWN: 'It has since been withdrawn.',
};
/** The one sentence the start leaves in the board's status line: a new start, or a replay of one made earlier. */
export function resubmittalStartedLine(name: string, kind: ResubmittalKind, result: { replayed?: boolean; state?: string | null } | null | undefined): string {
  const where = (result && result.state && RESUB_STATE_WORDS[result.state]) || RESUB_STATE_WORDS.WITH_UMED;
  return result && result.replayed
    ? `This DCF resubmittal for ${name} was already started (${RESUB_KIND_LABEL[kind].toLowerCase()}). ${where}`
    : `DCF resubmittal started for ${name} (${RESUB_KIND_LABEL[kind].toLowerCase()}). ${where}`;
}

/*
 * Focus (verifier, 2026-10-19), as the file's other panels move it: on open, the first field (autoFocus); on Review…,
 * the confirm button (autoFocus); on Back, Review… again; on Cancel, the status chip (backToChip); after the start, the
 * chip once the board is read again (onDone -> announce focusTo). The panel's one sentence is a polite live region: its
 * words change from "what this is" to the effect on Review…, and a screen reader says the effect.
 */
function ResubmittalStartPanel({ row, onClose, onDone }: { row: BoardRow; onClose: () => void; onDone: (msg: string) => void }) {
  const name = personName(row);
  const idp = `um-resub-${row.id}`;
  const [requester, setRequester] = useState<ReversalRequester | ''>('');
  const [reason, setReason] = useState('');
  const [kind, setKind] = useState<ResubmittalKind | ''>('');
  const [terms, setTerms] = useState<TermsFields>(EMPTY_TERMS);
  const [confirming, setConfirming] = useState<StartResubmittalBody | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // One key per opening of the form: a retry of this click is a replay, not a second resubmittal.
  const [key] = useState(() => freshKey());
  // Back puts focus on Review… again (never on first open: that is the first field's).
  const reviewBtn = React.useRef<HTMLButtonElement | null>(null);
  const backToReview = React.useRef(false);
  useEffect(() => {
    if (!confirming && backToReview.current) { backToReview.current = false; reviewBtn.current?.focus(); }
  }, [confirming]);

  const review = () => {
    setErr(null);
    if (!requester) { setErr('Choose who asked for it.'); return; }
    const why = reason.trim();
    if (why.length < 3) { setErr('A DCF resubmittal needs its reason (at least 3 characters). It is kept for good.'); return; }
    if (!kind) { setErr('Choose a full reversal or a corrected resubmittal.'); return; }
    const body: StartResubmittalBody = { requester, reason: why, kind, idempotency_key: key };
    if (kind === 'CORRECTED') {
      const t = termsBody(terms);
      if (!t.ok) { setErr(t.message); return; }
      body.corrected_terms = t.terms;
    }
    setConfirming(body);
  };
  const save = async () => {
    if (!confirming || busy) return;
    setBusy(true); setErr(null);
    try {
      const r = await umedApi.startResubmittal(row.id, confirming);
      if (!r.success) { setErr(refusal(r)); return; }
      onDone(resubmittalStartedLine(name, confirming.kind, r.data));
    } catch { setErr('Could not reach the server. It may or may not have been recorded: read the board again before trying twice.'); } finally { setBusy(false); }
  };

  const reqLabel = `${idp}-who`;
  const reasonLabel = `${idp}-reason`;
  return (
    <div className="um-rowpanel" role="group" aria-label={`Start a DCF resubmittal for ${name}`}
      onClick={(e) => e.stopPropagation()} onKeyDown={busy ? undefined : escapeCancels(onClose)}>
      <p className="um-rowpanel-what" data-testid="resub-start-effect" aria-live="polite">
        {confirming
          ? resubmittalStartEffect(confirming.kind, name, confirming.corrected_terms ? termsLine(confirming.corrected_terms) : null, resubmittalStartStatus(row))
          : resubmittalStartIntro(resubmittalStartStatus(row))}
      </p>
      {!confirming && (
        <>
          <label className="um-rowfield"><span id={reqLabel}>Who asked</span>
            <select value={requester} disabled={busy} autoFocus onChange={(e) => setRequester(e.target.value as ReversalRequester)} aria-labelledby={reqLabel}>
              <option value="">Choose…</option>
              {RESUB_REQUESTERS.map((r) => <option key={r} value={r}>{REVERSAL_REQUESTER_LABEL[r]}</option>)}
            </select>
          </label>
          <label className="um-rowfield"><span id={reasonLabel}>Reason</span>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} maxLength={2000} rows={2} disabled={busy}
              placeholder="Why it is resubmitted — kept with the resubmittal" aria-labelledby={reasonLabel} />
          </label>
          <fieldset className="um-rowfield" style={{ border: 0, padding: 0, margin: 0 }}>
            <legend>What is resubmitted</legend>
            {(['FULL', 'CORRECTED'] as ResubmittalKind[]).map((k) => (
              <label key={k} htmlFor={`${idp}-kind-${k}`} style={{ display: 'block', margin: '4px 0' }}>
                <input id={`${idp}-kind-${k}`} type="radio" name={`${idp}-kind`} checked={kind === k} disabled={busy} onChange={() => setKind(k)} />{' '}
                {RESUB_KIND_HELP[k]}
              </label>
            ))}
          </fieldset>
          {kind === 'CORRECTED' && <TermsInputs idp={idp} value={terms} onChange={setTerms} />}
        </>
      )}
      {err && <p className="um-notice stop" role="alert">{err}</p>}
      <div className="um-rowpanel-acts">
        {confirming ? (
          <>
            <button type="button" className="um-open um-primary" aria-disabled={busy} autoFocus onClick={save}>{busy ? 'Starting…' : 'Start the DCF resubmittal'}</button>
            <button type="button" className="um-link" disabled={busy} onClick={() => { backToReview.current = true; setConfirming(null); setErr(null); }}>Back</button>
          </>
        ) : (
          <button type="button" className="um-open um-primary" ref={reviewBtn} disabled={busy} onClick={review}>Review…</button>
        )}
        <button type="button" className="um-link" disabled={busy} onClick={onClose}>Cancel</button>
      </div>
    </div>
  );
}

/**
 * The UMED status chip IS the control (Josie Evans, round 4, 2026-10-07, point 6).
 *
 * On Monday she clicks the coloured status and picks the next one from a grid of coloured labels. Here the
 * chip opens the same kind of grid: each tile filled with the Monday colour of the word it produces
 * (mondayPalette), and each tile the SAME native act the "Record…" select it replaces offered -- nothing is
 * declared by being picked; the act's own panel still says what will happen before it happens. A tile the
 * server would refuse for this case and this viewer is not shown (outcomesFor, dcfResubOffered, the
 * capability flags), and a chip with nothing to offer is not a button at all.
 *
 * Keyboard: the chip is a button (Enter or Space opens), arrow keys move through the tiles (left/right one,
 * up/down a row), Home/End jump, Escape closes, and focus goes back to the chip -- on Escape, and when the act's
 * panel is cancelled.
 */
/** `group`: tiles that belong together are drawn together under the group's heading (the "Paid in full" group). `title`: the act's sentence, on hover. */
/** `disabled`: drawn greyed with its reason; picking it records nothing and only says why (2026-10-29). */
export interface ActTile { act: RowAct; label: string; hex: string | null; group?: ActGroup; title?: string; disabled?: DisabledTile }
type ActGroup = 'PAID_IN_FULL';
const ACT_GROUP_LABEL: Record<ActGroup, string> = { PAID_IN_FULL: PAID_IN_FULL_GROUP_LABEL };
/** The mark's clearing, offered in the mark's place where the mark is in force. */
export const PIF_CLEAR_LABEL = 'Clear paid in full · UMED pending';

/**
 * The "Paid in full" group (2026-10-20), in PAID_IN_FULL_OPTIONS order, each tile only where the server would take it
 * from this viewer -- the same predicates the server applies, so nothing is offered that would only say no:
 *   Paid in full                 record_disposition (UMED_APPROVAL_REVIEW); an open case (CASE_CLOSED); not already
 *                                paid in full (DISPOSITION_ALREADY_SET, read from finance_state); no open DCF
 *                                resubmittal (RESUBMITTAL_OPEN). An approved case may take it: it is not one of the
 *                                dispositions an approval refuses (CONTRADICTS_APPROVAL).
 *   Paid in full · UMED pending  record_disposition; an open case. Where the mark is in force, its clearing instead.
 *                                An open correction or resubmittal does not stop it (the server warns, and so do we).
 *   Paid in full correction      manage_corrections (UMED_CORRECTION_MANAGE) and the type in the server's
 *                                correction_types; an open case; no correction already open (CORRECTION_OPEN); and no
 *                                DCF resubmittal open (newCorrectionOffered). The server still permits a correction
 *                                beside an open resubmittal (DOCUMENT_CORRECTION.md, round 9), but the two together
 *                                break each other's packet, task and word (UMED_CORRECTION_MODEL.md, "Known gap
 *                                (2026-10-21)"). So the menu offers neither beside the other: no new correction while
 *                                a resubmittal is open (here and in outcomesFor). The other way round was ruled on
 *                                2026-10-29 (Isaiah Cotto: reversals are allowed on SFS CORRECTION): a resubmittal may
 *                                start beside an open SFS correction, and the two obligation defects of that direction
 *                                are fixed server-side; a FLMMIS-issue or paid in full correction still holds the start.
 */
/** The "Paid in full · UMED pending" tiles' fill: the chip's light teal (umed.css --teal-soft). */
const PIF_UMED_PENDING_HEX = '#e4f1ee';
export function paidInFullTilesFor(row: BoardRow, acts: BoardActsValue | null): ActTile[] {
  if (!acts || row.stage === 'CLOSED') return [];
  // Coloured (Josie Evans, 2026-10-21): the disposition and the correction by the Monday palette's own words; the
  // "UMED pending" mark by the light teal of its chip (--teal-soft in umed.css, PaidInFullChip), never the disposition's colour.
  const tile = (act: RowAct, key: Parameters<typeof paidInFullOption>[0], label?: string): ActTile => {
    const o = paidInFullOption(key);
    const hex = key === 'PAID_IN_FULL' ? mondayHex(UMED_STATUS, 'PAID IN FULL')
      : key === 'PAID_IN_FULL_CORRECTION' ? mondayHex(UMED_STATUS, 'PAID IN FULL CORRECTION')
        : PIF_UMED_PENDING_HEX;
    return { act, label: label || o.label, hex, group: 'PAID_IN_FULL', title: o.effect };
  };
  const out: ActTile[] = [];
  if (acts.canDispose && row.finance_state !== 'PAID_IN_FULL' && row.disposition?.word !== 'PAID_IN_FULL' && !row.reversal) {
    out.push(tile('PIF_DISPOSITION', 'PAID_IN_FULL'));
  }
  if (acts.canDispose) {
    out.push(row.paid_in_full_mark?.state === 'MARKED'
      ? { ...tile('PIF_CLEAR', 'PAID_IN_FULL_UMED_PENDING', PIF_CLEAR_LABEL), title: PAID_IN_FULL_CLEAR_EFFECT }
      : tile('PIF_MARK', 'PAID_IN_FULL_UMED_PENDING'));
  }
  if (acts.canCorrect && acts.pifCorrection && !row.correction && newCorrectionOffered(row)) out.push(tile('PIF_CORRECTION', 'PAID_IN_FULL_CORRECTION'));
  return out;
}

/**
 * A NEW correction (any of the three types) is offered only while no DCF resubmittal is open on the case -- handed to
 * UMED or still with Finance: `row.reversal` is the open one (readModel open_reversal: not COMPLETED, not WITHDRAWN).
 * (#445's resubmittalStartOffered held the other direction too, until 2026-10-29: a resubmittal may now start beside an
 * open SFS correction -- the ruling's SFS CORRECTION status -- but not beside the other two kinds.)
 * Completing a correction already open is not a new correction, and stays offered.
 */
export const newCorrectionOffered = (row: Pick<BoardRow, 'reversal'>): boolean => !row.reversal;
export const CORRECTION_RESUB_OPEN_NOTE = 'A DCF resubmittal is open on this case; finish or withdraw it before raising a correction.';
/** The line said beside the chip where a correction would be offered but for the open resubmittal; null otherwise. */
export function correctionWhyNotNote(row: BoardRow, acts: Pick<BoardActsValue, 'canCorrect'> | null): string | null {
  if (!acts?.canCorrect || row.stage === 'CLOSED' || row.correction) return null;
  return newCorrectionOffered(row) ? null : CORRECTION_RESUB_OPEN_NOTE;
}

/** The approval tiles, first in the grid: APPROVED, APPROVED - SFS CORRECTION, and APPROVED completing a partial approval. */
const APPROVAL_TILES: readonly RowAct[] = ['PARTIAL_COMPLETE', 'APPROVAL', 'PARTIAL_APPROVAL'];
export function actTilesFor(row: BoardRow, acts: BoardActsValue | null): ActTile[] {
  const tiles: ActTile[] = outcomesFor(row, acts).map((o) => {
    const label = outcomeLabel(o, row);
    // APPROVED - SFS CORRECTION is eMerge's own word, never Monday's: its colour is the legend's (PAR 01).
    // SEND AGAIN wears SFS CORRECTION's colour: it is that correction, sent again (2026-10-28).
    // PAID IN FULL CORRECTION · COMPLETED wears the word it writes (2026-10-29), and says so on hover.
    const pifDone = o === 'CORRECTION_COMPLETE' && isPifCorrection(row);
    // Not sent yet (2026-10-29 ruling): greyed, with the reason in place of the effect, and no colour of the word.
    // R2 (#580): SEND AGAIN under a fax in flight is greyed with the server's reason, never offered to be refused.
    const held = o === 'CORRECTION_COMPLETE' ? correctionCompleteHeld(row)
      : o === 'CORRECTION_RESEND' && row.fax_in_flight ? { reason: CORRECTION_RESEND_FAX_IN_FLIGHT } : null;
    if (held) return { act: o as RowAct, label, hex: null, title: held.reason, disabled: held };
    return { act: o as RowAct, label, hex: mondayHex(UMED_STATUS, pifDone ? PIF_CORRECTION_WORD : label) || (o === 'PARTIAL_APPROVAL' ? PARTIAL_APPROVAL_HEX : o === 'CORRECTION_RESEND' ? mondayHex(UMED_STATUS, 'SFS CORRECTION') : null),
      ...(o === 'CORRECTION_RESEND' ? { title: CORRECTION_RESEND_EFFECT } : {}),
      ...(pifDone ? { title: PIF_CORRECTION_COMPLETE_EFFECT } : {}) };
  });
  // "Add a note for Gwynn" (2026-10-29), right after the open correction's own tiles.
  const noteTile = correctionNoteTile(row, acts);
  if (noteTile) {
    const lastCorrection = tiles.map((t) => t.act).reduce((at, a, i) => (a === 'CORRECTION_COMPLETE' || a === 'CORRECTION_RESEND' ? i : at), -1);
    tiles.splice(lastCorrection === -1 ? tiles.length : lastCorrection + 1, 0, noteTile);
  }
  /*
   * NEEDS MCD RECERT and NO LONGER QUALIFIES right after APPROVED (2026-10-10), offered where the disposition panel
   * would offer them: a caller who may dispose, a case not closed and not approved (CONTRADICTS_APPROVAL), and not
   * already that disposition.
   */
  const direct: ActTile[] = DIRECT_DISPOSITIONS
    .filter((d) => Boolean(acts?.canDispose) && row.stage !== 'CLOSED' && row.disposition?.word !== d
      && !(isApproved(row) && CONTRADICTS_APPROVAL.includes(d)))
    .map((d) => {
      const label = DISPOSITION_STATUS_WORD[d] as string;
      return { act: d, label, hex: mondayHex(UMED_STATUS, label) };
    });
  // After the approval tiles (APPROVED, and APPROVED - SFS CORRECTION beside it). REVERSAL / DCF RESUBMITTAL… first among
  // them (2026-10-20: up beside the approval tiles, where Dominique looks -- it was last in the grid). Starting one opens
  // its form and writes nothing; it is offered where the server would take it.
  const resub: ActTile[] = resubmittalStartOffered(row, acts) ? [{ act: 'RESUB_START', label: RESUB_START_LABEL, hex: null }] : [];
  const firstOther = tiles.findIndex((t) => !APPROVAL_TILES.includes(t.act));
  tiles.splice(firstOther === -1 ? tiles.length : firstOther, 0, ...resub, ...direct);
  // Where UMED UPLOADED TO DCF is offered (the packet's own Confirm DCF upload), "Record sending…" is not: one way to
  // the one sending act, never two tiles for it.
  const gwynn = gwynnTilesFor(row, acts);
  if (acts?.canMarkSubmitted && row.stage === 'READY' && !isApproved(row) && !gwynn.some((t) => t.act === 'UPLOADED' || t.act === 'SUBMITTED_DCF')
    && !row.sent_to_dcf_on && !row.submitted_on
    && !['NO_LONGER_QUALIFIES', 'NEEDS_RECERT', 'DECEASED'].includes(row.lifecycle || '')) {
    tiles.unshift({ act: 'SUBMISSION', label: 'Record sending…', hex: null });
  }
  // Gwynn's acts first (2026-10-12): what she looked for in this menu.
  tiles.unshift(...gwynn);
  // PROBLEM ACCOUNT is a disposition (reason required, Reactivate undoes it). Not on an approved case (P2-2).
  const problem = Boolean(acts?.canDispose) && row.stage !== 'CLOSED' && row.disposition?.word !== 'PROBLEM_ACCOUNT' && !isApproved(row);
  if (dcfResubOffered(row, Boolean(acts?.canDispose))) tiles.push({ act: 'DCF_RESUB', label: 'DCF RESUB NEEDED', hex: mondayHex(UMED_STATUS, 'DCF RESUB NEEDED') });
  // Not where a correction or reversal is open: the server refuses a second exception (409 EXCEPTION_ALREADY_OPEN).
  if (problem && !row.correction && !row.reversal) tiles.push({ act: 'PROBLEM_ACCOUNT', label: 'PROBLEM ACCOUNT', hex: mondayHex(UMED_STATUS, 'PROBLEM ACCOUNT') });
  // The "Paid in full" group (2026-10-20): the disposition, the "UMED pending" mark (2026-10-15) and the correction,
  // together, where the mark's tile used to stand alone. (REVERSAL / DCF RESUBMITTAL… is offered above, beside the
  // approval tiles, since #445.)
  tiles.push(...paidInFullTilesFor(row, acts));
  // The other dispositions are not one word: an uncoloured tile that opens the disposition panel.
  if (acts?.canDispose) tiles.push({ act: 'DISPOSITION', label: 'Disposition…', hex: null });
  // Processes 5 and 6 (2026-10-12): their own block, at the end of this file.
  tiles.push(...afterApprovalTilesFor(row, acts));
  return tiles;
}

/**
 * The native act that records Monday's word on this row, as the tiles offer it (ruling #10): the tile whose word IS
 * Monday's word, or -- for a disposition word (DECEASED, PAID IN FULL, ...) -- the disposition panel opened on it,
 * where the panel would offer it. Null when no native act maps to the word, or the act is not allowed here.
 */
export function acceptActFor(row: BoardRow, acts: BoardActsValue | null): { act: RowAct; disposition?: Disposition } | null {
  const raw = row.monday_differs?.word || row.monday_word_differs?.word || null;
  const word = normalizeMondayWord(raw);
  if (!word) return null;
  // A disabled tile (2026-10-29) is not an act that can be taken: Monday's word never opens it.
  const tiles = actTilesFor(row, acts).filter((t) => !t.disabled);
  /*
   * The "Paid in full" group (2026-10-20): Monday's PAID IN FULL is accepted as the group's "Paid in full" (the
   * disposition that writes that word -- found by its label). Its correction's tile is the REQUEST, and Monday's PAID IN
   * FULL CORRECTION is the word a COMPLETED one writes, so that word never opens a request; the mark writes no word.
   */
  const tile = tiles.find((t) => t.act !== 'DISPOSITION' && t.act !== 'PIF_CORRECTION' && normalizeMondayWord(t.label) === word);
  // Monday's APPROVED is accepted only as today's confirmation: it never prompts recording FLMMIS evidence (#373, P2-1).
  if (tile && tile.act === 'APPROVAL' && !row.approval.ready_to_approve) return null;
  // A partial approval completes on FLMMIS, which Monday never knew about: Monday's word never offers it (PAR 03).
  if (tile && tile.act === 'PARTIAL_COMPLETE') return null;
  // Monday's SUBMITTED word never records a sending: the chooser is for a person who sent it (SUB 06).
  if (tile && (tile.act === 'SUBMITTED_DCF' || tile.act === 'FAX_DCF')) return null;
  if (tile) return { act: tile.act };
  /*
   * Verification of #378 (4): a reversal word Monday is ahead with (REVERSAL COMPLETE, the board's move into closed
   * that eMerge refused; REVERSAL RESUB; REVERSAL NOT APPROVED) opens the reversal act PATH where it stands here --
   * its outcome once the reversal was sent, its sending once it was handed to UMED; Monday's word never starts one (REVERSAL / DCF RESUBMITTAL… is its own form). A
   * person records each step; nothing is applied on its own.
   */
  if (REVERSAL_PATH_WORDS.includes(word)) {
    const step = REVERSAL_PATH.map((a) => tiles.find((t) => t.act === a)).find(Boolean);
    if (step) return { act: step.act };
  }
  if (!tiles.some((t) => t.act === 'DISPOSITION') || row.stage === 'CLOSED') return null;
  const d = (Object.keys(DISPOSITION_STATUS_WORD) as Disposition[]).find((k) => normalizeMondayWord(DISPOSITION_STATUS_WORD[k]) === word);
  // Paid in full is the group's tile, matched above where it is offered; the disposition panel no longer lists it.
  if (!d || d === 'PAID_IN_FULL' || row.disposition?.word === d || (isApproved(row) && CONTRADICTS_APPROVAL.includes(d))) return null;
  return { act: 'DISPOSITION', disposition: d };
}

/** Monday's reversal words, and the reversal act's steps in the order a word that far along is accepted. */
const REVERSAL_PATH_WORDS = ['REVERSAL COMPLETE', 'REVERSAL RESUB', 'REVERSAL NOT APPROVED'];
const REVERSAL_PATH: RowAct[] = ['REVERSAL_APPROVED', 'REVERSAL_SUBMITTED'];

/** Two tiles a row, as Monday's label picker lays them out at this size. */
const ACT_GRID_COLUMNS = 2;

/**
 * Where each tile is drawn, as { row, col } (verification of e38b5299, D5). Ungrouped tiles fill the grid two a row; a
 * group starts on a row of its own (it spans the grid), lays its tiles two a row inside it, and whatever follows it
 * starts a fresh row. Up and Down move by these rows, not by index +/- 2, so focus lands on the tile drawn above or below.
 */
export function actGridCells(tiles: ReadonlyArray<Pick<ActTile, 'group'>>): Array<{ row: number; col: number }> {
  const cells: Array<{ row: number; col: number }> = [];
  let row = 0;
  let col = 0;
  let inGroup: ActGroup | undefined;
  tiles.forEach((t) => {
    const entering = t.group !== inGroup;
    if (entering && (col > 0)) { row += 1; col = 0; }
    inGroup = t.group;
    cells.push({ row, col });
    col += 1;
    if (col === ACT_GRID_COLUMNS) { row += 1; col = 0; }
  });
  return cells;
}

/** The tile Up (-1) or Down (+1) lands on from tile `from`: the same column in the next drawn row, else that row's nearest. */
export function actGridVertical(cells: ReadonlyArray<{ row: number; col: number }>, from: number, dir: 1 | -1): number {
  const here = cells[from];
  if (!here) return from;
  const rows = Array.from(new Set(cells.map((c) => c.row))).sort((a, b) => a - b);
  const target = rows[rows.indexOf(here.row) + dir];
  if (target === undefined) return from;
  const inRow = cells.map((c, i) => ({ ...c, i })).filter((c) => c.row === target);
  const same = inRow.find((c) => c.col === here.col);
  if (same) return same.i;
  return inRow.reduce((best, c) => (Math.abs(c.col - here.col) < Math.abs(best.col - here.col) ? c : best)).i;
}

export function ActGrid({ name, tiles, onPick, onClose, id, onNext, onAlso }: {
  name: string; tiles: ActTile[]; onPick: (act: RowAct) => void; onClose: () => void; id: string;
  /** The way to a disabled tile's next step (the case panel's Terms or packet section); absent, no button is drawn. */
  onNext?: (to: CaseFocus['kind']) => void;
  /** A disabled tile's other ways forward (`also`: a note for Gwynn, already sent outside eMerge), 2026-10-29; absent, not drawn. */
  onAlso?: (act: RowAct) => void;
}) {
  const [at, setAt] = useState(0);
  // The disabled tile last picked (2026-10-29): its reason is said below the grid, with the way to the next step.
  const [why, setWhy] = useState<number | null>(null);
  const refs = React.useRef<Array<HTMLButtonElement | null>>([]);
  const saidRef = React.useRef<HTMLDivElement | null>(null);
  useEffect(() => { refs.current[at]?.focus(); }, [at]);
  const move = (to: number) => setAt(Math.max(0, Math.min(tiles.length - 1, to)));
  const said = why !== null ? tiles[why] : undefined;
  const saidNext = said?.disabled?.next && onNext ? said.disabled.next : null;
  const saidButtons = (): HTMLButtonElement[] => Array.from(saidRef.current?.querySelectorAll('button') || []);
  const onKeyDown = (e: React.KeyboardEvent) => {
    const k = e.key;
    if (k === 'Escape') { e.preventDefault(); e.stopPropagation(); onClose(); return; }
    // From the tile whose reason is said, Tab reaches its actions instead of closing the grid.
    if (k === 'Tab' && !e.shiftKey && why === at && saidButtons().length) { e.preventDefault(); saidButtons()[0].focus(); return; }
    if (k === 'Tab') { onClose(); return; }
    if (k === 'ArrowDown' || k === 'ArrowUp') { e.preventDefault(); move(actGridVertical(actGridCells(tiles), at, k === 'ArrowDown' ? 1 : -1)); return; }
    const step = k === 'ArrowRight' ? 1 : k === 'ArrowLeft' ? -1 : 0;
    if (step) { e.preventDefault(); move(at + step); return; }
    if (k === 'Home') { e.preventDefault(); move(0); return; }
    if (k === 'End') { e.preventDefault(); move(tiles.length - 1); }
  };
  const tileButton = (t: ActTile, i: number) => (t.disabled ? (
    // Greyed, still focusable (aria-disabled, not disabled) so its reason is reachable; picking it only says why.
    <button key={t.act} type="button" role="menuitem" data-act={t.act} tabIndex={i === at ? 0 : -1} aria-disabled="true"
      ref={(el) => { refs.current[i] = el; }}
      className="um-acttile um-acttile-off" title={t.title}
      aria-labelledby={`${id}-t${i}`} aria-describedby={`${id}-t${i}-why`}
      onFocus={() => { if (at !== i) setAt(i); }}
      onClick={() => { setAt(i); setWhy(i); }}>
      <span id={`${id}-t${i}`}>{t.label}</span>
      <small id={`${id}-t${i}-why`} className="um-acttile-why">{t.disabled.reason}</small>
    </button>
  ) : (
    <button key={t.act} type="button" role="menuitem" data-act={t.act} tabIndex={i === at ? 0 : -1}
      ref={(el) => { refs.current[i] = el; }}
      className={`um-acttile${t.hex ? ' filled' : ''}`} style={t.hex ? filledChipStyle(t.hex) : undefined}
      title={t.title}
      onFocus={() => { if (at !== i) setAt(i); }}
      onClick={() => onPick(t.act)}>
      {t.label}
    </button>
  ));
  const anyDisabled = tiles.some((t) => t.disabled);
  // Consecutive tiles of one group are drawn as one block under its heading (the "Paid in full" group, 2026-10-20). The
  // arrow keys still walk every tile in order, group or not.
  const runs: Array<{ group?: ActGroup; items: Array<{ t: ActTile; i: number }> }> = [];
  tiles.forEach((t, i) => {
    const last = runs[runs.length - 1];
    if (t.group && last && last.group === t.group) last.items.push({ t, i });
    else runs.push({ group: t.group, items: [{ t, i }] });
  });
  const grid = (
    <div className="um-actgrid" role="menu" id={id} aria-label={`Record for ${name}`} onKeyDown={onKeyDown}
      onClick={(e) => e.stopPropagation()}>
      {runs.flatMap((run) => (run.group ? [(
        <div key={`group-${run.group}`} className="um-actgroup" role="group" aria-label={ACT_GROUP_LABEL[run.group]} data-group={run.group}>
          <span className="um-actgroup-head" aria-hidden="true">{ACT_GROUP_LABEL[run.group]}</span>
          {run.items.map(({ t, i }) => tileButton(t, i))}
        </div>
      )] : run.items.map(({ t, i }) => tileButton(t, i))))}
    </div>
  );
  if (!anyDisabled) return grid;
  // Beside the menu, never in it (a menu holds menu items): present while a disabled tile is drawn, so what a pick of
  // one says is announced; empty until then.
  return (
    <>
      {grid}
      <div className="um-actgrid-said" role="status" data-testid="act-tile-why" ref={saidRef} onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onClose(); return; }
          if (e.key !== 'Tab') return;
          const buttons = saidButtons();
          const i = buttons.indexOf(e.target as HTMLButtonElement);
          // Shift+Tab from the first goes back to the tile; Tab from the last leaves the grid; between them, as usual.
          if (e.shiftKey && i === 0 && why !== null) { e.preventDefault(); refs.current[why]?.focus(); return; }
          if (!e.shiftKey && i === buttons.length - 1) onClose();
        }}>
        {said?.disabled && (
          <>
            <strong>{said.label}</strong> cannot be recorded yet. {said.disabled.reason} Nothing was recorded.
            {saidNext && (
              <span className="um-actgrid-said-step">
                {said.disabled.help ? <>{said.disabled.help}{' '}</> : null}
                <button type="button" className="um-link" onClick={() => onNext?.(saidNext.to)}>{saidNext.label}</button>
              </span>
            )}
            {onAlso && (said.disabled.also || []).map((x) => (
              <span key={x.act} className="um-actgrid-said-step">
                {x.lead}{' '}
                <button type="button" className="um-link" onClick={() => onAlso(x.act)}>{x.label}</button>
              </span>
            ))}
          </>
        )}
      </div>
    </>
  );
}

/**
 * A row approved here (2026-10-20): the approval, where it went, and the way to the resident in Finance -- the
 * Finance dossier (/portal/finance?view=search&resident=<patient id>, FinanceShell's resident link), offered only to
 * someone who can open Finance. In place of the Approve control, never beside it.
 */
function ApprovedHereChip({ row }: { row: BoardRow }) {
  const acts = React.useContext(BoardActs);
  const chipText = approvedChipOf(acts?.approvedHere?.(row.id)?.outcome);
  const routed = useInRouterContext();
  const href = row.patient_id && acts?.financeHref ? acts.financeHref(row.patient_id) : null;
  return (
    <span className="um-approvedhere" data-testid="approved-here" onClick={(e) => e.stopPropagation()}>
      <span className="um-chip um-approvedhere-chip">{chipText}</span>
      {href && (routed
        ? <Link className="um-link" to={href} aria-label={`Open ${personName(row)} in Finance`}>Open in Finance</Link>
        : <a className="um-link" href={href} aria-label={`Open ${personName(row)} in Finance`}>Open in Finance</a>)}
    </span>
  );
}

function StatusCell({ row }: { row: BoardRow }) {
  const acts = React.useContext(BoardActs);
  const [outcome, setOutcome] = useState<RowAct | ''>('');
  const [open, setOpen] = useState(false);
  // The disposition Accept Monday's word opened the panel on (ruling #10); null when the panel opened from the tile.
  const [initialDisposition, setInitialDisposition] = useState<Disposition | null>(null);
  // APPROVED opened on a case without FLMMIS on record: the two steps, kept for as long as the panel is open.
  const [twoStep, setTwoStep] = useState(false);
  const chip = React.useRef<HTMLButtonElement | null>(null);
  const refocus = React.useRef(false);
  // Set status (StatusPicker, 2026-10-29): a panel opened from it gives focus back to it, not to the chip.
  const pickerToggle = React.useRef<HTMLButtonElement | null>(null);
  const returnTo = React.useRef<'chip' | 'picker'>('chip');
  // A different resident in this cell's position is a different case.
  useOnRowChange(row.id, () => { setOutcome(''); setOpen(false); });
  // A grid closed with Escape, or a panel cancelled, gives focus back to the control that opened it (P2-1).
  useEffect(() => {
    if (!outcome && !open && refocus.current) {
      refocus.current = false;
      (returnTo.current === 'picker' && pickerToggle.current ? pickerToggle.current : chip.current)?.focus();
    }
  }, [outcome, open]);
  const backToChip = () => { refocus.current = true; setOutcome(''); };
  const closeGrid = () => { returnTo.current = 'chip'; refocus.current = true; setOpen(false); };
  const openAct = (a: RowAct, disposition: Disposition | null) => {
    returnTo.current = 'chip';
    setInitialDisposition(disposition);
    setTwoStep(a === 'APPROVAL' && !row.approval.ready_to_approve);
    setOutcome(a);
  };
  const tiles = actTilesFor(row, acts);
  /*
   * The one routing of a picked tile, from the status menu (ActGrid) and from Set status (StatusPicker, 2026-10-29): the
   * acts that live in the case panel continue there; every other act opens its own panel here. `from` is where focus goes
   * back when that panel is cancelled.
   */
  const pickTile = (a: RowAct, from: 'chip' | 'picker' = 'chip') => {
    setOpen(false);
    if (a === 'SUBMISSION') { acts?.openCase(row.id); return; }
    // Gwynn, 2026-10-12: these continue where the act lives, in the case panel (one act, one place).
    if (a === 'SUBMITTED_DCF') { if (acts?.continueCase) acts.continueCase(row.id, 'choose-sending'); else acts?.openCase(row.id); return; }
    if (a === 'FAX_DCF') { if (acts?.continueCase) acts.continueCase(row.id, 'fax-dcf'); else acts?.openCase(row.id); return; }
    // SCRUM-126: a resubmittal's held packet -- the packet panel, where its hold is fixed (nothing is sent from here).
    if (a === 'OPEN_PACKET') { if (acts?.continueCase) acts.continueCase(row.id, 'packet'); else acts?.openCase(row.id); return; }
    if (a === 'UPLOADED') { if (acts?.continueCase) acts.continueCase(row.id, 'confirm-upload'); else acts?.openCase(row.id); return; }
    if (a === 'DCF_EMAIL_CREATED') { if (acts?.continueCase) acts.continueCase(row.id, 'dcf-email'); else acts?.openCase(row.id); return; }
    openAct(a, null);
    returnTo.current = from;
  };
  const accepting = acceptActFor(row, acts);
  const resubWhyNot = resubmittalWhyNotNote(row, acts);
  // 2026-10-21: why no new correction is offered here, for who may raise one (a DCF resubmittal is open).
  const correctionNote = correctionWhyNotNote(row, acts);
  // 2026-10-28: an open correction not sent yet: why SEND AGAIN is not offered, and what holds its packet.
  const notSent = correctionNotSentNote(row, acts);
  const approvalNote = approvalWhyNotNote(row, Boolean(acts?.canReview));
  // Approved: the approval email opens next, in the case panel (2026-10-13). 2026-10-20: an Approve answered by the
  // server keeps the row on the board, saying where it went, until the board is left (a replay changed nothing here).
  const approved = (msg: string, outcome?: ApprovedOutcome) => {
    if (outcome && !outcome.replayed) acts?.approvedInPlace?.(row, outcome);
    acts?.announce(msg, row.id);
    acts?.continueCase?.(row.id, 'approval-email');
  };
  const here = acts?.approvedHere?.(row.id) || null;
  const name = personName(row);
  const gridId = `um-actgrid-${row.id}`;
  const chipEl = row.correction_status?.replaces_word
    ? <PifCorrectionWordChip status={row.correction_status} />
    : <StatusChip word={row.source_status_raw} legend={row.status_legend} source={row.status_word_source} approvedInEmerge={isApproved(row)} />;
  // A press anywhere else closes the grid (focus stays where that press put it).
  const cell = React.useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!open) return undefined;
    const away = (e: MouseEvent) => { if (cell.current && !cell.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, [open]);
  // The copy of a row approved here that has left this view: what was done and where it went, and nothing to act on.
  if (here && here.kept) {
    return <div className="um-statuscell" ref={cell}><ApprovedHereChip row={row} /></div>;
  }
  return (
    <div className="um-statuscell" ref={cell}>
      {here && <ApprovedHereChip row={row} />}
      {tiles.length > 0 && !outcome ? (
        <button type="button" className={`um-statusbtn${open ? ' open' : ''}`} ref={chip}
          aria-haspopup="menu" aria-expanded={open} aria-controls={open ? gridId : undefined}
          aria-label={`Record an outcome for ${name}`} title="Choose what to record"
          onClick={(e) => { e.stopPropagation(); if (open) closeGrid(); else setOpen(true); }}>
          {chipEl}
          <span className="um-statusbtn-caret" aria-hidden="true">▾</span>
        </button>
      ) : chipEl}
      {/* Josie Evans, 2026-10-29: every Monday status, always -- each the status menu's own tile, or why it cannot apply now. */}
      {!outcome && <StatusPicker row={row} tiles={tiles} name={name} toggleRef={pickerToggle} onPick={(a) => pickTile(a, 'picker')} onOpen={() => setOpen(false)} />}
      {/* Not when it only repeats the status word beside it (verification of 676fdb9, P2-4): DCF RESUB NEEDED twice says nothing more. */}
      {!dispositionRepeatsWord(row) && <DispositionChip disposition={row.disposition} />}
      <PaidInFullChip mark={row.paid_in_full_mark} />
      <CorrectionStateChip status={row.correction_status} />
      <CorrectionNoteLine correction={row.correction} />
      {/* SCRUM-128 (2026-11-03): reported deceased outside UMED -- the chip points to the DECEASED disposition. */}
      <DeceasedReportedChip row={row} onRecord={acts?.canDispose && row.stage !== 'CLOSED' ? () => { setOpen(false); openAct('DECEASED' as RowAct, null); } : undefined} />
      {/* SCRUM-126: what holds a DCF resubmittal's packet, said on the row (UMEDs to Fax lists it). */}
      {resubmittalHeldLine(row) && <span className="um-quiet um-wordonly" data-testid="resub-packet-held">{resubmittalHeldLine(row)}</span>}
      <AfterApprovalFacts row={row} />
      {/* The word with no move behind it: said, so nobody assumes the submitter was told (P2-1). */}
      {dcfResubWordOnly(row) && <span className="um-quiet um-wordonly">Not moved in eMerge: nobody was told here.</span>}
      {/*
        * Why no REVERSAL / DCF RESUBMITTAL… tile here, for who may start one: Monday's REVERSAL on a case eMerge has not
        * approved (2026-10-19), or -- on an approved case -- paid in full before approval, one already open, an SFS
        * correction open, or a partial approval's correction open (2026-10-20).
        */}
      {resubWhyNot && <span className="um-quiet um-wordonly" data-testid={resubWhyNot.notApproved ? 'resub-not-approved' : 'resub-not-offered'}>{resubWhyNot.note}</span>}
      {/* 2026-10-28: why no APPROVED here, in words, with the way to the review in the case (never a silently hidden tile). */}
      {approvalNote && (
        <span className="um-quiet um-wordonly" data-testid="approval-not-offered">
          {approvalNote}{' '}
          {acts?.continueCase && <button type="button" className="um-link" onClick={(e) => { e.stopPropagation(); acts.continueCase?.(row.id, 'review'); }}>Record the review</button>}
        </span>
      )}
      {/* Its mirror (2026-10-21): why no new correction is offered, for who may raise one -- a DCF resubmittal is open. */}
      {correctionNote && <span className="um-quiet um-wordonly" data-testid="correction-not-offered">{correctionNote}</span>}
      {/* 2026-10-28 (Josie Evans): why there is no SFS CORRECTION · SEND AGAIN yet, and the way to what holds the packet. */}
      {notSent && !outcome && (
        <span className="um-quiet um-wordonly" data-testid="correction-not-sent">
          {notSent.note}{' '}
          {acts?.continueCase && (
            <button type="button" className="um-link" onClick={(e) => { e.stopPropagation(); acts.continueCase?.(row.id, notSent.fix); }}>
              {notSent.fix === 'terms' ? 'Record the terms' : 'Open the packet'}
            </button>
          )}
        </span>
      )}
      {/*
        * Monday says a later stage (2026-10-06, P2-6): beside the UMED status, where Josie reads at 1440, as a compact
        * chip -- the eMerge stage column it used to sit in is off the right edge of the screen on every tab she works.
        */}
      <MondayDiffers row={row} compact canMark={Boolean(acts?.canMarkSubmitted)} onDone={(m) => acts?.announce(m, row.id)}
        accept={accepting && !outcome ? { onAccept: () => { setOpen(false); openAct(accepting.act, accepting.disposition || null); } } : null} />
      {acts?.canReview && row.approval.ready_to_approve && !outcome && !open && <ApproveAct row={row} compact onDone={approved} />}
      {open && !outcome && (
        <ActGrid name={name} tiles={tiles} id={gridId} onClose={closeGrid}
          onNext={acts?.continueCase ? (to) => { setOpen(false); acts.continueCase?.(row.id, to); } : undefined}
          onAlso={(a) => { if ((a !== 'CORRECTION_OUTSIDE' && a !== 'CORRECTION_NOTE') || !row.correction) return; setOpen(false); openAct(a, null); }}
          onPick={(a) => pickTile(a)} />
      )}
      {outcome === 'APPROVAL' && (twoStep
        ? <ApproveInTwoSteps row={row} onCancel={backToChip} onDone={(m) => { setOutcome(''); approved(m); }} />
        : <ApproveAct row={row} compact startConfirming onCancel={backToChip} onDone={(m) => { setOutcome(''); approved(m); }} />)}
      {outcome === 'PARTIAL_APPROVAL' && <PartialApprovalPanel row={row} onCancel={backToChip} onDone={(m) => { setOutcome(''); approved(m); }} />}
      {outcome === 'PARTIAL_COMPLETE' && <PartialCompletePanel row={row} onCancel={backToChip} onDone={(m) => { setOutcome(''); approved(m); }} />}
      {outcome === 'CORRECTION_RESEND' && (
        <CorrectionResendPanel row={row} onCancel={backToChip}
          onDone={(m) => { setOutcome(''); acts?.announce(m, row.id, { focusTo: () => chip.current }); }} />
      )}
      {outcome === 'CORRECTION_NOTE' && (
        <CorrectionNotePanel row={row} onCancel={backToChip}
          onDone={(m) => { setOutcome(''); acts?.announce(m, row.id, { focusTo: () => chip.current }); }} />
      )}
      {outcome === 'CORRECTION_OUTSIDE' && (
        <CorrectionOutsidePanel row={row} onCancel={backToChip}
          onDone={(m) => { setOutcome(''); acts?.announce(m, row.id, { focusTo: () => chip.current }); }} />
      )}
      {outcome === 'DISPOSITION' && <DispositionPanel row={row} initial={initialDisposition} onClose={backToChip} />}
      {(outcome === 'PROBLEM_ACCOUNT' || isDirectDisposition(outcome)) && <DispositionPanel row={row} initial={outcome as Disposition} onClose={backToChip} />}
      {outcome === 'DCF_RESUB' && <DcfResubPanel row={row} onClose={backToChip} />}
      {outcome === 'RESUB_START' && (
        <ResubmittalStartPanel row={row} onClose={backToChip}
          onDone={(m) => { setOutcome(''); acts?.announce(m, row.id, { focusTo: () => chip.current }); }} />
      )}
      {isAfterApprovalAct(outcome) && <AfterApprovalPanel row={row} act={outcome} onClose={backToChip} />}
      {(outcome === 'BENEFITS_STUCK' || outcome === 'BENEFITS_LINKED') && (
        <BenefitsForm caseId={row.id} mode={outcome === 'BENEFITS_STUCK' ? 'stuck' : 'linked'} onClose={backToChip} account={stuckFactsOfRow(row.dcf_account, row.readiness?.purpose || row.creation_reason || null)}
          onDone={(m) => { acts?.announce(`${personName(row)}: ${m}`, row.id); }} />
      )}
      {outcome === 'PIF_DISPOSITION' && <PaidInFullDispositionPanel row={row} onClose={backToChip} />}
      {outcome === 'PIF_CORRECTION' && <PaidInFullCorrectionPanel row={row} onClose={backToChip} />}
      {(outcome === 'PIF_MARK' || outcome === 'PIF_CLEAR') && (
        <PaidInFullForm mode={outcome === 'PIF_MARK' ? 'mark' : 'clear'} name={name} onClose={backToChip}
          effect={outcome === 'PIF_MARK' ? <><strong>{paidInFullOption('PAID_IN_FULL_UMED_PENDING').label}.</strong> {paidInFullOption('PAID_IN_FULL_UMED_PENDING').effect} {PIF_MARK_DETAIL}</> : undefined}
          act={(body) => (outcome === 'PIF_MARK' ? umedApi.markPaidInFull(row.id, body) : umedApi.clearPaidInFull(row.id, { reason: body.reason, idempotency_key: body.idempotency_key }))}
          onDone={(m) => acts?.announce(m, row.id)} />
      )}
      {outcome && !OWN_PANEL_ACTS.has(outcome) && <OutcomePanel row={row} outcome={outcome as Outcome} onClose={backToChip} />}
    </div>
  );
}

/*
 * "Set status" on the docked record (2026-11-03, SCRUM-127/129): the Kanban is the default layout and a card opens the
 * dock, but every status act lived only in the Table's status cell. The dock now draws the SAME status cell -- its chip
 * menu, Set status (StatusPicker), the reason lines and every act's own panel, routed by the same pickTile -- for the
 * case it holds, with the capabilities of the same scope (boardActsCapabilities). Nothing is written on a pick: each act
 * still says what it does before it does it, and the server decides again. The Kanban's rule stands: a drag never writes.
 *
 * `row` is the case the dock read (GET /cases/:id, decorated as a board row is). `onChanged` is the dock's own "say it,
 * then re-read the case, the list and the counts"; `onContinue` lands an act that continues in the record on its section.
 */
export function boardRowOfCase(c: UmedCase, legend: LegendEntry[] = []): BoardRow {
  const word = normalizeMondayWord(c.source_status_raw);
  const own = (c as Partial<BoardRow>).status_legend;
  const entry = own !== undefined ? own : (legend.find((e) => normalizeMondayWord(e.word) === word) || null);
  return {
    resident_dob: null, case_number: null, financial_responsibility: null, ssn_on_file: false, lead_available: null, lead_assigned_on: null,
    advocate_name: null, patient_liability_cents: null, patient_liability_status: null, sale_amount: null, sale_amount_source: null, sale_product_label: null,
    submitted_terms: null, enrollment_state: 'NOT_ENROLLED', enrollment_confirmed_at: null,
    ...(c as Partial<BoardRow>),
    ...c,
    status_legend: entry,
  } as BoardRow;
}
export function DockStatus({ row, scope, onChanged, onContinue, onFocusNotice }: {
  row: UmedCase;
  scope: Scope;
  onChanged: (msg: string) => Promise<void> | void;
  onContinue?: (kind: CaseFocus['kind']) => void;
  /** Where focus goes after an act when the control that opened it is gone: the record's notice line. */
  onFocusNotice?: () => void;
}) {
  const value = React.useMemo<BoardActsValue>(() => ({
    ...boardActsCapabilities(scope),
    // The dock IS the open case: nothing to open; an act that continues in the record lands on its section.
    openCase: () => { onContinue?.('packet'); },
    continueCase: onContinue ? (_id, kind) => onContinue(kind) : undefined,
    reload: () => { void onChanged('Updated.'); },
    /*
     * As the table's announce (Copilot on #580): say it, re-read, then put focus where the act asked -- `focusTo` (the
     * control that opened it, e.g. the status chip) when it is still there and enabled; `keepFocus` leaves it where it
     * is; otherwise the record's notice that says what happened. Never left on the page body.
     */
    announce: (msg, _rowId, opts) => {
      // A re-read that fails still lands focus (the notice says what was recorded).
      void Promise.resolve(onChanged(msg)).catch(() => undefined).then(() => window.setTimeout(() => {
        if (opts?.keepFocus) return;
        const own = opts?.focusTo ? opts.focusTo() : null;
        if (own && own.isConnected && !(own as HTMLButtonElement).disabled) { own.focus({ preventScroll: true }); return; }
        // The re-read can draw the record afresh (the board re-reads under the dock), so the opener's ref may be gone:
        // the case's own status chip, found by its case, as the table falls back to the row's name; else the notice.
        const chipNow = document.querySelector<HTMLButtonElement>(`[data-dock-case="${row.id}"] button.um-statusbtn`);
        if (opts?.focusTo && chipNow && !chipNow.disabled) { chipNow.focus({ preventScroll: true }); return; }
        if (onFocusNotice) onFocusNotice();
        else document.querySelector<HTMLElement>('.um-case .um-notice[role="status"]')?.focus({ preventScroll: true });
      }, 0));
    },
  }), [scope, onChanged, onContinue, onFocusNotice, row.id]);
  const boardRow = React.useMemo(() => boardRowOfCase(row, scope.status_legend || []), [row, scope.status_legend]);
  return (
    <BoardActs.Provider value={value}>
      <div className="um-section um-dockstatus" data-testid="dock-status" data-dock-case={row.id} aria-label={`Status of ${personName(row)}`}>
        <h3>Status</h3>
        <StatusCell row={boardRow} />
      </div>
    </BoardActs.Provider>
  );
}

/**
 * Josie Evans, 2026-09-24: "Where is says last follow up - I want it to pull
 * up a calendar so I can put the date in that I last checked this patient for
 * an approval. When I click on it now, I'm unable to change anything."
 *
 * Setting the date IS recording a review: she looked, and the facts had not
 * moved. So the cell posts exactly that — the two facts carried forward
 * unchanged, dated the day she looked — which re-arms the ten-day clock from
 * that day and leaves the check in the chronology with her name on it. A date
 * written straight into the column would have been a number with no act
 * behind it, and the projector would have had every right to overwrite it.
 *
 * Not offered on an approved case: once FLMMIS is reflected there is nothing
 * left to follow up, and the date is history.
 */
/**
 * The day she last checked. Only that.
 *
 * Josie Evans, 2026-09-28: "Last follow up date. I just need the Calendar. It
 * pulls way too many options down. I just want to click the date and move on.
 * I can record NOCA and FLMMIS in a different category."
 *
 * This cell shipped that morning as a three-field review -- date, NOCA and
 * FLMMIS together -- because the three are recorded by one API call and it
 * seemed tidy to ask for them at once. It was tidy for the code and wrong for
 * the person: she checks a dozen cases in a row and changes the approval facts
 * on almost none of them, so two thirds of the panel was furniture in the way
 * of a date.
 *
 * The three facts are still ONE call, because they are one review and the
 * server's chronology should say so. The date picker simply sends the NOCA and
 * FLMMIS the row already has, unchanged, and the two of them get their own
 * cells beside this one where they can be changed deliberately.
 */
function FollowUpCell({ row }: { row: BoardRow }) {
  const acts = React.useContext(BoardActs);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  /*
   * The day she just recorded, held until the board's refetch catches up.
   *
   * Josie Evans, 2026-09-29: "When I click on the last follow up date. It goes
   * back to the original date. It's not letting me update it to today's date
   * unless I click on 'NOCA received'."
   *
   * The server was never the problem -- posting her exact payload to those
   * exact cases returns 201. The cell was: on success it closed the editor and
   * re-rendered `row.last_follow_up_on`, which is a PROP and still held the old
   * date, because `reload()` had not come back yet. So the old date reappeared
   * the instant she picked a new one. Changing the NOCA "fixed" it only by
   * causing a second refetch.
   *
   * A control must never show a value it knows is stale. What she recorded is
   * shown immediately, and released once the row agrees.
   */
  const [justSet, setJustSet] = useState<string | null>(null);
  // Set / Change takes focus back when the picker closes -- Cancel, Escape or a recorded day (P2-4).
  const back = useReturnFocus<HTMLButtonElement>(open);
  useEffect(() => { setOpen(false); setErr(null); }, [row.id]);
  useEffect(() => { setJustSet(null); }, [row.id, row.last_follow_up_on]);
  const close = () => { back.restore(); setOpen(false); setErr(null); };

  const on = justSet || row.last_follow_up_on;
  const shown = on ? <span>{shortDate(on)}</span> : <NotRecorded />;
  // Offered on every open case. A case already approved still gets checked --
  // that is how a NOCA that arrives after the approval gets recorded.
  if (!acts?.canReview || row.stage === 'CLOSED') return shown;

  const record = async (on: string) => {
    setBusy(true); setErr(null);
    try {
      /*
       * A follow-up, not a review.
       *
       * This used to carry the two approval facts forward through the review
       * vocabulary, which has three words per fact where the case has five.
       * `nocaForward('REJECTED')` and `flmmisForward('EXCEPTION')` both return
       * null, and `|| 'NOT_RECORDED'` turned "I cannot say this" into "it is
       * not recorded". On 2026-09-30 that erased FLMMIS EXCEPTION from ten
       * cases -- every one that had it -- and dropped them out of Josie's
       * queue mid-morning.
       *
       * Recording the day she looked now asserts nothing about what she
       * found, which is the truth of what she did.
       */
      const r = await umedApi.recordFollowUp(row.id, on);
      if (!r.success) { setErr(refusal(r)); return; }
      setJustSet(on);
      setOpen(false);
      // Said in the board's status line; focus back on Set / Change once the board has re-read (P2-4).
      acts?.announce(`Last follow-up for ${personName(row)} is recorded as ${shortDate(on)}.`, row.id, { focusTo: () => back.opener.current });
    } catch { setErr('Could not reach the server. Nothing was recorded.'); } finally { setBusy(false); }
  };

  if (!open) {
    return (
      <span className="um-followup">
        {shown}
        <button type="button" className="um-link" disabled={busy} ref={back.opener} onClick={(e) => { e.stopPropagation(); setOpen(true); }}
          aria-label={`Record the day you last checked on ${personName(row)}`}>
          {on ? 'Change' : 'Set'}
        </button>
        {err && <span className="um-notice stop" role="alert">{err}</span>}
      </span>
    );
  }

  /*
   * A date input and nothing else. Picking a day records it -- no Record
   * button to find, because choosing the date IS the whole act.
   */
  return (
    <span className="um-followup" onClick={(e) => e.stopPropagation()}>
      <input
        type="date" className="um-dateonly" autoFocus disabled={busy}
        defaultValue={on ? String(on).slice(0, 10) : todayLocal()}
        max={todayLocal()}
        aria-label={`The day you last checked on ${personName(row)}`}
        onChange={(e) => { if (e.target.value) record(e.target.value); }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') { e.stopPropagation(); close(); }
          if (e.key === 'Enter') { const v = (e.target as HTMLInputElement).value; if (v) record(v); }
        }}
      />
      {/*
        * One click for the answer she gives most of the time.
        *
        * The picker opens ON today, so choosing today fires no change event and
        * the control appeared to do nothing — worst exactly where the cell is
        * empty and "Set" is the first thing she reaches for. Writing the test
        * for the revert fix is what surfaced it.
        */}
      <button type="button" className="um-link um-today" disabled={busy}
        aria-label={`Record today as the day you last checked on ${personName(row)}`}
        onClick={() => record(todayLocal())}>Today</button>
      <button type="button" className="um-link" disabled={busy} onClick={close}>Cancel</button>
      {err && <span className="um-notice stop" role="alert">{err}</span>}
    </span>
  );
}

/**
 * Attach a file where it is read (Josie Evans, round 4, 2026-10-07, point 5): "no file · Attach" under the NOCA
 * and under FLMMIS, as one compact drop zone. Click (or Enter/Space on Attach) to pick, or drag a file onto it;
 * it uploads at once -- no dialog, no class to choose, the column IS the class. Images and PDFs only, as the
 * picker offers; the server checks the bytes again. The result is said in the board's status line, and focus
 * comes back to this zone's Attach after the re-read.
 */
export const DROP_ACCEPT = 'application/pdf,image/*';
export const acceptsFile = (f: Pick<File, 'type' | 'name'>): boolean =>
  f.type === 'application/pdf' || f.type.startsWith('image/') || (!f.type && /\.(pdf|png|jpe?g|gif|webp|tiff?|heic)$/i.test(f.name));

/** What eMerge holds, in words: On file, no file, or nothing said when it is not known (or not for this viewer). */
function HeldWords({ held, what }: { held: boolean | null | undefined; what: string }) {
  if (held === true) return <span className="um-tag ok" title={`eMerge holds ${what} itself.`}>On file</span>;
  if (held === false) return <span className="um-sub">no file</span>;
  return null;
}

export function DropAttach({ row, documentClass, what, held }: {
  row: Pick<BoardRow, 'id' | 'first_name' | 'last_name'>;
  documentClass: 'NOCA' | 'FLMMIS';
  /** How the file is named to her: "NOCA", "FLMMIS evidence". */
  what: string;
  held: boolean | null | undefined;
}) {
  const acts = React.useContext(BoardActs);
  const readOnly = React.useContext(ReadOnlyRow);
  const [busy, setBusy] = useState(false);
  const [over, setOver] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // 2026-10-15: the same bytes are on another resident and this kind may be shared (FLMMIS evidence): the file waits
  // here for the reason, so the refusal is never a dead end. A NOCA is refused outright and nothing waits.
  const [pending, setPending] = useState<{ file: File; preset: string | null; maxLength: number; minLength: number } | null>(null);
  const [reason, setReason] = useState('');
  const fileRef = React.useRef<HTMLInputElement | null>(null);
  const attachRef = React.useRef<HTMLButtonElement | null>(null);
  useOnRowChange(row.id, () => { setErr(null); setOver(false); setPending(null); setReason(''); });
  const name = personName(row);

  const upload = async (file: File, overrideReason?: string) => {
    if (!acceptsFile(file)) { setErr('Images and PDFs only.'); return; }
    setBusy(true); setErr(null);
    try {
      const r = await umedApi.uploadDocument(row.id, documentClass, file, freshKey(), overrideReason);
      if (!r.success) {
        const sh = r.code === 'DOCUMENT_BELONGS_TO_ANOTHER_RESIDENT' ? readSharedRefusal(r.detail, documentClass) : null;
        if (sh && sh.overridable) { setPending({ file, preset: sh.preset, maxLength: sh.maxLength, minLength: sh.minLength }); setErr(refusal(r)); return; }
        // A refused confirm keeps the file waiting (a reason too short, a passing failure) -- unless the refusal is now
        // a hard one (the bytes became another resident's record): then nothing waits.
        if (!overrideReason || r.code === 'DOCUMENT_BELONGS_TO_ANOTHER_RESIDENT') setPending(null);
        setErr(refusal(r)); return;
      }
      setPending(null); setReason('');
      acts?.announce(`The ${what} for ${name} is attached.`, row.id, { focusTo: () => attachRef.current });
    } catch { setErr('Could not reach the server. Nothing was uploaded.'); } finally { setBusy(false); }
  };
  const dragging = (e: React.DragEvent) => {
    if (busy || !Array.from(e.dataTransfer?.types || []).includes('Files')) return;
    e.preventDefault(); e.stopPropagation();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
    setOver(true);
  };
  // The kept copy: what is on file, and no way to attach (no drop handlers, no picker).
  if (readOnly) {
    return (
      <span className={`um-drop um-drop-readonly${held === true ? ' held' : ''}`} data-class={documentClass} data-readonly="true">
        <HeldWords held={held} what={`the ${what}`} />
        {held !== true && held !== false && <span className="um-sub">{what}</span>}
      </span>
    );
  }
  return (
    <span className={`um-drop${over ? ' over' : ''}${busy ? ' busy' : ''}${held === true ? ' held' : ''}`} data-class={documentClass}
      onClick={(e) => e.stopPropagation()}
      onDragEnter={dragging} onDragOver={dragging}
      onDragLeave={(e) => { if (!(e.currentTarget as HTMLElement).contains(e.relatedTarget as Node)) setOver(false); }}
      onDrop={(e) => {
        e.preventDefault(); e.stopPropagation(); setOver(false);
        const f = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
        if (f && !busy) upload(f);
      }}>
      <HeldWords held={held} what={`the ${what}`} />
      {held !== true && held !== false && <span className="um-sub">{what}</span>}
      <span aria-hidden="true" className="um-drop-sep">·</span>
      <button type="button" className="um-link um-drop-pick" disabled={busy} ref={attachRef}
        onClick={() => fileRef.current?.click()}
        aria-label={`Attach ${documentClass === 'NOCA' ? 'a NOCA' : 'FLMMIS evidence'} for ${name}: choose a file, or drop an image or PDF here`}>
        {busy ? 'Uploading…' : over ? 'Drop to attach' : 'Attach'}
      </button>
      <input ref={fileRef} type="file" className="um-filehidden" tabIndex={-1} accept={DROP_ACCEPT}
        aria-hidden="true" data-testid={`um-drop-input-${documentClass.toLowerCase()}`}
        onChange={(e) => { const f = e.target.files && e.target.files[0]; e.target.value = ''; if (f) upload(f); }} />
      {err && <span className="um-notice stop" role="alert">{err}</span>}
      {pending && (
        <span style={{ display: 'inline-flex', flexWrap: 'wrap', alignItems: 'center', gap: 6 }}>
          <SharedFileReason tone="um" value={reason} onChange={setReason} preset={pending.preset} maxLength={pending.maxLength} />
          <button type="button" className="um-link" disabled={busy || reason.trim().length < pending.minLength}
            onClick={() => upload(pending.file, reason.trim())}>Upload anyway, it is shared</button>
          <button type="button" className="um-link" disabled={busy} onClick={() => { setPending(null); setReason(''); setErr(null); }}>Cancel</button>
        </span>
      )}
    </span>
  );
}

/**
 * NOCA: the state, and the notice itself.
 *
 * Josie Evans, 2026-09-28: "If I can have a box to put the NOCA and just
 * upload it." Uploading a NOCA was already supported end to end -- the class,
 * the route, the capability and the API call all existed -- and the board
 * never offered it, so the only way to attach one was to open the case.
 *
 * eMerge holds 2,328 NOCAs as of that morning, 92 of them fetched that day by
 * the importer, so the cell says whether the notice is in hand rather than
 * only what somebody typed.
 */
function NocaCell({ row }: { row: BoardRow }) {
  const acts = React.useContext(BoardActs);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // Where focus goes after the board re-reads (P2-4): the NOCA select (the "Record NOCA received" button is gone
  // once the NOCA is recorded), and the Attach button after an upload.
  const selectRef = React.useRef<HTMLSelectElement | null>(null);
  useEffect(() => { setErr(null); }, [row.id]);

  const state = nocaForward(row.approval.noca) || 'NOT_RECORDED';
  const held = row.documents?.noca;
  /*
   * UM-R3 (Josie Evans, 2026-10-02): Jerry Whitehead's NOCA was uploaded and
   * this cell said "Not recorded". The notice is EVIDENCE, not a review: the
   * cell says it is on file and nobody has recorded reading it, and offers the
   * one click that does -- the same native review as the select, carrying
   * FLMMIS through (and standing down where it cannot).
   */
  const onFileUnreviewed = row.noca_evidence === 'DOCUMENT_ON_FILE';
  // An approved case keeps the evidence it was approved on (P1-A): the control stands down and says why.
  const locked = isApproved(row);

  const set = async (next: NocaResult) => {
    /*
     * A review states BOTH facts, so changing the NOCA must carry the FLMMIS
     * faithfully. Where the case holds a FLMMIS a review has no word for --
     * EXCEPTION -- it cannot, and it stands down rather than writing
     * "not recorded" over it. That coercion cost ten cases their EXCEPTION on
     * 2026-09-30.
     */
    if (flmmisForward(row.approval.flmmis) === null) {
      setErr(`FLMMIS is ${FLMMIS_LABEL[row.approval.flmmis as keyof typeof FLMMIS_LABEL] || row.approval.flmmis}, which a review has no word for. Open the case to record the NOCA.`);
      return;
    }
    setBusy(true); setErr(null);
    try {
      const r = await umedApi.review(row.id, {
        noca: next,
        flmmis: flmmisForward(row.approval.flmmis) as FlmmisResult,
        decision: 'CONTINUE',
      });
      if (!r.success) { setErr(refusal(r)); return; }
      acts?.announce(`NOCA ${NOCA_RESULT_WORDS[next].toLowerCase()} is recorded for ${personName(row)}.`, row.id, { focusTo: () => selectRef.current });
    } catch { setErr('Could not reach the server. Nothing was recorded.'); } finally { setBusy(false); }
  };

  return (
    <span className="um-noca" onClick={(e) => e.stopPropagation()}>
      {onFileUnreviewed ? (
        <>
          <span className="um-tag attn" title="eMerge holds the notice. Nobody has recorded reading it yet, so it is not counted as received.">On file · not yet reviewed</span>
          {acts?.canReview && row.stage !== 'CLOSED' && !locked && (
            <button type="button" className="um-link um-nocarecord" disabled={busy} onClick={() => set('RECEIVED')}
              aria-label={`Record NOCA received for ${personName(row)}`}>{busy ? 'Recording…' : 'Record NOCA received'}</button>
          )}
        </>
      ) : (
        <>
          {acts?.canReview && row.stage !== 'CLOSED' ? (
            <select value={state} disabled={busy || locked} aria-label={`NOCA for ${personName(row)}`} ref={selectRef}
              aria-describedby={locked ? `um-lock-noca-${row.id}` : undefined}
              onChange={(e) => set(e.target.value as NocaResult)}>
              {(['NOT_RECORDED', 'PENDING', 'RECEIVED'] as NocaResult[]).map((k) => (
                <option key={k} value={k}>{NOCA_RESULT_WORDS[k]}</option>
              ))}
            </select>
          ) : <span>{NOCA_RESULT_WORDS[state]}</span>}
        </>
      )}
      {locked && acts?.canReview && row.stage !== 'CLOSED' && <LockedNote id={`um-lock-noca-${row.id}`} />}
      {/* The NOCA arrived after the approval (2026-10-06): its own narrow act, where the server offers it. */}
      {locked && acts?.canReview && <LateNocaAct row={row} onDone={(m) => acts.announce(m, row.id)} />}

      {/* Round 4 (2026-10-07): the notice itself, dropped or picked, uploaded at once. Only the words when she may not attach. */}
      {acts?.canDocument && row.stage !== 'CLOSED'
        ? <DropAttach row={row} documentClass="NOCA" what="NOCA" held={held} />
        : <HeldWords held={held} what="the notice" />}
      {err && <span className="um-notice stop" role="alert">{err}</span>}
    </span>
  );
}

/**
 * FLMMIS: the state, and what FLMMIS showed.
 *
 * FLMMIS is a state in Florida's Medicaid system, not a document anyone
 * issues, so the STATE is still what Josie records (Reflected makes the case
 * ready to approve; the approval is the Approve act in the UMED status cell).
 * Round 4 (2026-10-07, point 5): she also keeps what she saw there -- a
 * screenshot or a PDF -- as the approval's evidence, attached like the NOCA
 * (document class FLMMIS, migration 20261007b). The file never sets the state:
 * FLMMIS stays the system of record. On an approved case the state is kept as
 * the approval left it (P1-A).
 */
function LockedNote({ id }: { id: string }) {
  return (
    <span className="um-locked" id={id} title={APPROVED_LOCK}>
      Approved · kept as recorded<span className="um-sr-only">. To undo an approval, record a reversal or correction.</span>
    </span>
  );
}

/**
 * A NOCA that arrives after the approval (2026-10-06).
 *
 * The NOCA select is locked on an approved row ("Approved · kept as
 * recorded"), because a review restates both facts and the server refuses it
 * there. The notice still arrives, often days after FLMMIS reflected and the
 * approval. So the row offers the one act that records it -- NOCA received,
 * with the day it came, and nothing else -- where the server says it can work
 * (`approval.late_noca_recordable`: approved, NOCA not in yet, this viewer's
 * approval scope). A small confirmation in the row, like every other row act;
 * the result is said in the board's status line and focus goes back to the row.
 */
export const LATE_NOCA_EFFECT = (name: string) =>
  `Records the NOCA for ${name} as received. The case stays approved: FLMMIS, the status, the stage and the Finance handoff do not change.`;

export function LateNocaAct({ row, onDone }: {
  row: Pick<BoardRow, 'id' | 'first_name' | 'last_name' | 'approval'>;
  onDone: (msg: string) => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const [on, setOn] = useState(todayLocal());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const opener = React.useRef<HTMLButtonElement | null>(null);
  const refocus = React.useRef(false);
  useEffect(() => { setConfirming(false); setErr(null); }, [row.id]);
  // Cancel or Escape gives focus back to the button that opened it (P2-1, P2-2).
  useEffect(() => {
    if (!confirming && refocus.current) { refocus.current = false; opener.current?.focus(); }
  }, [confirming]);
  if (!row.approval.late_noca_recordable) return null;
  const name = personName(row);
  const cancel = () => { setErr(null); refocus.current = true; setConfirming(false); };
  if (!confirming) {
    return (
      <button type="button" className="um-link um-nocarecord" ref={opener}
        onClick={(e) => { e.stopPropagation(); setErr(null); setOn(todayLocal()); setConfirming(true); }}
        aria-label={`Record NOCA received for ${name}`}>Record NOCA received</button>
    );
  }
  return (
    <div className="um-rowpanel um-confirm" role="group" aria-label={`Record NOCA received for ${name}`}
      onClick={(e) => e.stopPropagation()} onKeyDown={busy ? undefined : escapeCancels(cancel)}>
      <p className="um-rowpanel-what">{LATE_NOCA_EFFECT(name)}</p>
      <label className="um-rowfield"><span>The day it arrived</span>
        <input type="date" value={on} max={todayLocal()} disabled={busy}
          onChange={(e) => setOn(e.target.value)} aria-label="The day the NOCA arrived" />
      </label>
      {err && <p className="um-notice stop" role="alert">{err}</p>}
      <div className="um-rowpanel-acts">
        <button type="button" className="um-open um-primary" aria-disabled={busy} aria-describedby={(!on && 'Enter the day it arrived first.') ? `why-late-noca-${row.id}` : undefined} autoFocus disabled={!on}
          onClick={async () => {
            // aria-disabled while busy, so a refusal leaves focus on this button rather than the page body.
            if (busy || !on) return;
            setBusy(true); setErr(null);
            try {
              const r = await umedApi.recordLateNoca(row.id, { received_on: on });
              if (!r.success) { setErr(refusal(r)); return; }
              setConfirming(false);
              onDone(r.data?.replayed
                ? `The NOCA for ${name} was already recorded as received.`
                : `NOCA received is recorded for ${name}. The case stays approved.`);
            } catch { setErr('Could not reach the server. Nothing was recorded.'); } finally { setBusy(false); }
          }}>{busy ? 'Recording…' : 'Record NOCA received'}</button><Why id={`why-late-noca-${row.id}`} reason={!on && 'Enter the day it arrived first.'} className="um-text um-why" />
        <button type="button" className="um-link" disabled={busy} onClick={cancel}>Cancel</button>
      </div>
    </div>
  );
}

function FlmmisCell({ row }: { row: BoardRow }) {
  const acts = React.useContext(BoardActs);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  /*
   * Josie Evans, 2026-10-06: "I want to be able to click down on FLMMIS
   * reflecting, but I do not want it to move to approval." Choosing Reflected
   * no longer approves anything, and no browser dialog asks about it: the row
   * opens a confirmation that says exactly what it records, and the approval
   * stays her separate act (ApproveAct, in the status cell).
   */
  const [pending, setPending] = useState<FlmmisResult | null>(null);
  const select = React.useRef<HTMLSelectElement | null>(null);
  const refocus = React.useRef(false);
  useEffect(() => { setErr(null); setPending(null); }, [row.id]);
  // Cancel or Escape gives focus back to the select that opened the confirmation (P2-1, P2-2).
  useEffect(() => {
    if (!pending && refocus.current) { refocus.current = false; select.current?.focus(); }
  }, [pending]);

  const state = flmmisForward(row.approval.flmmis) || 'NOT_RECORDED';
  // Round 4 (2026-10-07): what FLMMIS showed, attached like the NOCA. Withheld (null) where approval detail is.
  const flmmisHeld = row.documents ? row.documents.flmmis : undefined;
  const evidence = acts?.canDocument && row.stage !== 'CLOSED'
    ? <DropAttach row={row} documentClass="FLMMIS" what="FLMMIS evidence" held={flmmisHeld} />
    : <HeldWords held={flmmisHeld} what="FLMMIS evidence" />;
  if (!acts?.canReview || row.stage === 'CLOSED') {
    return <span className="um-flmmis">{FLMMIS_RESULT_WORDS[state]}{evidence}</span>;
  }
  const locked = isApproved(row);
  const name = personName(row);
  const cancel = () => { refocus.current = true; setPending(null); };
  const record = async (next: FlmmisResult) => {
    // Same rule in reverse: never write "not recorded" over a NOCA a review cannot express (REJECTED).
    const blocked = flmmisBlocked(row);
    if (blocked) { setErr(blocked); return; }
    setBusy(true); setErr(null);
    try {
      const refused = await writeFlmmis(row, next);
      if (refused) { setErr(refused); return; }
      const confirmed = pending !== null;
      setPending(null);
      // The confirmation closes under her: say what was recorded and where the next act is, and keep focus on the row.
      acts?.announce(next === 'REFLECTED'
        ? `FLMMIS reflected is recorded for ${name}. ${FLMMIS_NEXT}`
        : `FLMMIS ${FLMMIS_RESULT_WORDS[next].toLowerCase()} is recorded for ${name}.`, row.id, { keepFocus: !confirmed });
    } catch { setErr('Could not reach the server. Nothing was recorded.'); } finally { setBusy(false); }
  };
  return (
    <span className="um-flmmis" onClick={(e) => e.stopPropagation()}>
      <select value={pending || state} disabled={busy || Boolean(pending) || locked} ref={select} aria-label={`FLMMIS for ${name}`}
        aria-describedby={locked ? `um-lock-flmmis-${row.id}` : undefined}
        onChange={(e) => {
          const next = e.target.value as FlmmisResult;
          if (next === state) return;
          // Reflected is the one answer that changes what Josie may do next, so it is confirmed in the row first.
          if (next === 'REFLECTED') { setErr(null); setPending(next); return; }
          record(next);
        }}>
        {(['NOT_RECORDED', 'NOT_REFLECTED', 'REFLECTED'] as FlmmisResult[]).map((k) => (
          <option key={k} value={k}>{FLMMIS_RESULT_WORDS[k]}</option>
        ))}
      </select>
      {locked && <LockedNote id={`um-lock-flmmis-${row.id}`} />}
      {evidence}
      {pending && (
        <div className="um-rowpanel um-confirm" role="group" aria-label={`Record FLMMIS reflected for ${name}`}
          onKeyDown={busy ? undefined : escapeCancels(cancel)}>
          <p className="um-rowpanel-what">{FLMMIS_EFFECT} {FLMMIS_NEXT}</p>
          <div className="um-rowpanel-acts">
            <button type="button" className="um-open um-primary" aria-disabled={busy} autoFocus onClick={() => { if (!busy) record(pending); }}>
              {busy ? 'Recording…' : 'Record FLMMIS reflected'}
            </button>
            <button type="button" className="um-link" disabled={busy} onClick={cancel}>Cancel</button>
          </div>
        </div>
      )}
      {err && <span className="um-notice stop" role="alert">{err}</span>}
    </span>
  );
}

/**
 * The Medicaid number, entered where it is read.
 *
 * The column shipped on 2026-09-24 saying the number is "entered here once
 * and copied afterwards" — and there was nowhere to enter it. The route was
 * built and nothing called it, so the column could only ever have shown a
 * dash. A promise in a tooltip is not a control.
 */
function MedicaidCell({ row }: { row: BoardRow }) {
  const acts = React.useContext(BoardActs);
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(row.medicaid_number || '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => { setOpen(false); setValue(row.medicaid_number || ''); setErr(null); }, [row.id, row.medicaid_number]);
  const back = useReturnFocus<HTMLButtonElement>(open);

  const shown = row.medicaid_number
    ? <><span className="um-mono">{row.medicaid_number}</span><CopyCell value={row.medicaid_number} what="the Medicaid number" /></>
    : <NotRecorded why="Not recorded yet. CounselEAR does not send it, so it is entered here once and copied afterwards." />;

  if (!acts?.canMedicaid) return <span className="um-medicaid">{shown}</span>;
  if (!open) {
    return (
      <span className="um-medicaid">
        {shown}
        <button type="button" className="um-link" ref={back.opener} onClick={(e) => { e.stopPropagation(); setOpen(true); }}
          aria-label={`${row.medicaid_number ? 'Change' : 'Record'} the Medicaid number for ${personName(row)}`}>
          {row.medicaid_number ? 'Change' : 'Record'}
        </button>
      </span>
    );
  }
  return (
    <div className="um-rowpanel" onClick={(e) => e.stopPropagation()}
      onKeyDown={busy ? undefined : escapeCancels(() => { back.restore(); setOpen(false); setErr(null); })}>
      <label className="um-rowfield"><span>Medicaid #</span>
        <input value={value} onChange={(e) => setValue(e.target.value)} maxLength={40} disabled={busy} autoFocus
          aria-label="Medicaid number" placeholder="CounselEAR: Employee ID" />
      </label>
      {err && <p className="um-notice stop" role="alert">{err}</p>}
      <div className="um-rowpanel-acts">
        <button type="button" className="um-open um-primary" disabled={busy || !value.trim()} aria-describedby={(!value.trim() && 'Enter the Medicaid number first.') ? `why-medicaid-${row.id}` : undefined} onClick={async () => {
          setBusy(true); setErr(null);
          try {
            const r = await umedApi.recordMedicaidNumber(row.id, value.trim());
            if (!r.success) { setErr(refusal(r)); return; }
            setOpen(false);
            acts.announce(`The Medicaid number for ${personName(row)} is recorded.`, row.id, { focusTo: () => back.opener.current });
          } catch { setErr('Could not reach the server. Nothing was recorded.'); } finally { setBusy(false); }
        }}>{busy ? 'Recording…' : 'Record'}</button><Why id={`why-medicaid-${row.id}`} reason={!value.trim() && 'Enter the Medicaid number first.'} className="um-text um-why" />
        <button type="button" className="um-link" disabled={busy} onClick={() => { back.restore(); setOpen(false); setErr(null); }}>Cancel</button>
      </div>
    </div>
  );
}

/**
 * A Patient Advocate sent this one over, and nobody has taken it.
 *
 * Isaiah Cotto, 2026-09-28: "Gwynn is missing the PAs submissions and needs
 * them to execute."
 *
 * Measured the same day, because the obvious explanations were all wrong.
 * They are not buried: all sixteen sit on PAGE ONE of UMEDs Needed,
 * positions 1 to 23. She was not un-told: nine in-platform notifications and
 * nine emails reached her, every one DELIVERED. The queue and the notice both
 * worked.
 *
 * What is true is that FIVE of the sixteen exist nowhere but eMerge -- no
 * Monday row, none coming, because eMerge never writes to that board. A
 * person working from Monday cannot see them at all, and a row that looks
 * like every other row gives her no reason to think otherwise.
 *
 * So the row says what it is. Not a new queue, not another notification --
 * the thing she is looking at, saying out loud that an advocate handed this
 * over, how long ago, and that it is still nobody's.
 */
function FromAnAdvocate({ row }: { row: BoardRow }) {
  const waiting = row.case_created_at;
  if (row.source_system !== 'EMERGE' || !row.claimable) return null;
  const days = waiting ? Math.floor((Date.now() - new Date(waiting).getTime()) / 86400000) : null;
  return (
    <span className="um-fromadvocate"
      title={`A Patient Advocate handed this over${days !== null && days >= 0 ? ` ${days === 0 ? 'today' : days === 1 ? '1 day ago' : `${days} days ago`}` : ''} and nobody has taken it yet.`}>
      From an advocate
      {days !== null && days >= 0 && <> · {days === 0 ? 'today' : `${days}d`}</>}
      {' · unclaimed'}
    </span>
  );
}

/**
 * The same resident, open on another UMED case.
 *
 * Josie Evans, 2026-09-28: "This patient is in here twice. Not sure if he's
 * been submitted or not. Same with Paticia Sena, Wilson Rudolph."
 *
 * Eleven residents are in there twice, and the second record usually knows
 * the answer she wants: one says Created, the other says Submitted to DCF.
 * Rather than make her find the twin by scrolling, the row carries what the
 * twin says.
 *
 * It sits in the frozen patient column on purpose — it is the one cell on
 * screen whatever the horizontal scroll is doing, and a duplicate you have to
 * scroll to notice is a duplicate you act on twice.
 */
/*
 * Josie Evans, 2026-10-02: "Some of this information under the patients names
 * are not necessary." The line under the name -- "Also open: APPROVED · sent
 * Oct 13, 2025" -- is gone from the row. What it said is not: the row carries
 * a small count ("1 other case") that opens the case, and the opened case lists
 * each of the resident's other open UMEDs with its word, its date and who
 * moves it (OtherOpenList), which is everything the line and its tooltip said.
 */
const sayOther = (o: OtherUmed) => o.word || STAGE_LABEL[o.stage as keyof typeof STAGE_LABEL] || o.stage;
const datedOther = (o: OtherUmed) => (o.sent_to_dcf_on ? `sent ${shortDate(o.sent_to_dcf_on)}`
  : o.submitted_on ? `submitted ${shortDate(o.submitted_on)}` : null);

function AlsoOpen({ others, onOpen, name }: { others?: OtherUmed[] | null; onOpen: () => void; name: string }) {
  if (!others || !others.length) return null;
  const label = `${others.length} other ${others.length === 1 ? 'case' : 'cases'}`;
  return (
    <button type="button" className="um-tag um-alsoopen-chip" onClick={(e) => { e.stopPropagation(); onOpen(); }}
      aria-label={`${label} open for ${name} — open the case to see them`} title="Open the case to see the resident's other open UMEDs">
      {label}
    </button>
  );
}

/**
 * Two open cases for this resident (#363 round 3): a Monday item was linked to the native case, the native case was
 * suspended so the item got its own case, and the native case came back. Nothing closes either on its own -- closing
 * is a person's act -- so both rows carry this, and the duplicate query lists the pair (RELINK_AFTER_REACTIVATION).
 */
export const TWO_OPEN_EXPLAIN = 'This resident has two open UMED cases: the eMerge case was reactivated after the Monday item got a case of its own. Decide which one is the work; nothing is closed automatically.';
function TwoOpenFlag() {
  return <span className="um-tag attn um-twoopen" title={TWO_OPEN_EXPLAIN}>Two open cases for this resident<span className="um-sr-only">. {TWO_OPEN_EXPLAIN}</span></span>;
}

/**
 * Where a search result lives (design verification 2026-10-06, P2-6). A search
 * answers from every case, so a row no longer says which tab it belongs to by
 * being in it. The server names the views whose predicate the row meets
 * (`board_views`, the tabs' own predicates); a closed case says Closed; a case
 * only Main table holds says its eMerge stage.
 */
function RowWhere({ row, tabs }: { row: BoardRow; tabs: BoardTabDef[] }) {
  if (row.stage === 'CLOSED') {
    return <span className="um-tag um-rowwhere closed" title="Closed: in no view, found by search and in All cases.">Closed</span>;
  }
  const views = (row.board_views || []).map((k) => tabs.find((t) => t.key === k)?.label || k);
  const text = views.length ? views.join(' · ') : `Main table · ${stageLabelOf(row.stage, isApproved(row))}`;
  return (
    <span className="um-tag um-rowwhere" title={views.length ? `In ${views.join(', ')}` : 'In no view but Main table'}>
      <span className="um-sr-only">In </span>{text}
    </span>
  );
}

/** The resident's other open UMEDs, shown with the opened case: the word, the date, who moves it. */
function OtherOpenList({ others }: { others?: OtherUmed[] | null }) {
  if (!others || !others.length) return null;
  return (
    <section className="um-othercases" aria-label="Other open UMEDs for this resident">
      <h3>Also open for this resident</h3>
      <ul>
        {others.map((o, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <li key={i}>
            <strong>{sayOther(o)}</strong>
            {datedOther(o) && <> · {datedOther(o)}</>}
            {' · '}{o.source === 'EMERGE' ? 'eMerge' : 'the board'}
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * Who manages the resident's funds — readable, and now changeable.
 *
 * Josie Evans, 2026-09-29: "The who manages funds box: I need to also be able
 * to update this in case something has changed. There is no where for me to
 * change the status."
 *
 * The column shipped on 2026-09-23 read-only, so a facility that handed the
 * money to a family could only be corrected by a Patient Advocate on another
 * screen — or not at all, where no PA case existed.
 *
 * It writes the SAME resident-scoped row the PA side writes. Two surfaces, one
 * truth. It never touches `patients.payor_source`: that column is MONDAY
 * authority, and the write would be refused and then overwritten by the next
 * full refresh. Where the board used a word eMerge cannot map, the word is
 * shown AS WRITTEN and recording a real one replaces it.
 */
/**
 * Whose word "who manages the funds" is (Josie Evans, 2026-10-15: "needs to be pulled from the PA board"). The server
 * reads it as the PA board does (parties.currentFinancialResponsibility): eMerge's own record first -- the Patient
 * Advocate's, or this board's Change, one resident-scoped row -- then Monday's payor source. Said in words; a Monday
 * word that disagrees with eMerge's is shown, never hidden.
 */
/** A recorded instant as the business's own (Florida) day -- never the UTC date of a timestamp. */
const floridaDay = (iso: string): string => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric', year: 'numeric' });
};
export function FundsProvenance({ fr }: { fr: BoardRow['financial_responsibility'] }) {
  if (!fr || !fr.source || fr.source === 'NONE') return null;
  if (fr.source === 'MONDAY') return <span className="um-sub" data-testid="funds-provenance">Monday&rsquo;s word</span>;
  const label = (t: string | null | undefined) => (t ? FINANCIAL_RESPONSIBILITY_LABEL[t as FinancialResponsibilityType] || t : null);
  return (
    <span className="um-sub" data-testid="funds-provenance">
      Recorded in eMerge{fr.recorded_by ? ` by ${fr.recorded_by}` : ''}{fr.recorded_at ? ` · ${floridaDay(fr.recorded_at)}` : ''}
      {fr.board_disagrees && fr.board_type ? <> · Monday said {label(fr.board_type)}</> : null}
    </span>
  );
}

export const FUNDS_MISSING = 'Who manages the funds missing';
/** No answer anywhere -- not eMerge's record, not a Monday word -- on a case still open. A warning only. */
export const fundsMissing = (row: Pick<BoardRow, 'stage' | 'financial_responsibility'>): boolean => {
  const fr = row.financial_responsibility;
  return row.stage !== 'CLOSED' && Boolean(fr) && !fr?.type && !fr?.raw && !fr?.ach_on_file;
};

function FundsCell({ row }: { row: BoardRow }) {
  const acts = React.useContext(BoardActs);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const fr = row.financial_responsibility;
  const [type, setType] = useState<string>(fr?.type || '');
  const [ach, setAch] = useState<boolean>(Boolean(fr?.ach_on_file));
  /* Held until the board's refetch catches up — the same staleness that made
   * the follow-up date appear to revert on 2026-09-29. */
  const [just, setJust] = useState<{ type: string; ach: boolean } | null>(null);
  useEffect(() => { setOpen(false); setErr(null); setJust(null); setType(fr?.type || ''); setAch(Boolean(fr?.ach_on_file)); }, [row.id, fr?.type, fr?.ach_on_file]);
  const back = useReturnFocus<HTMLButtonElement>(open);
  const cancel = () => { back.restore(); setOpen(false); setErr(null); };

  const shownType = just ? just.type : fr?.type || null;
  const shownAch = just ? just.ach : Boolean(fr?.ach_on_file);
  const shown = (
    <span>
      {shownType
        ? FINANCIAL_RESPONSIBILITY_LABEL[shownType as FinancialResponsibilityType] || shownType
        : fr?.raw && !shownAch
          ? <span title="The board used a word eMerge does not map.">{fr.raw}</span>
          : <NotRecorded />}
      {shownAch && <span className="um-sub">ACH on file</span>}
      {!just && <FundsProvenance fr={fr} />}
      {/* Isaiah Cotto, 2026-10-15: a UMED already open without the answer is warned, never blocked (the stop is the PA's Ready to submit). */}
      {!just && fundsMissing(row) && <span className="um-tag attn" data-testid="funds-missing">{FUNDS_MISSING}</span>}
    </span>
  );

  if (!acts?.canTerms || row.stage === 'CLOSED') return shown;
  if (!open) {
    return (
      <span className="um-followup">
        {shown}
        <button type="button" className="um-link" disabled={busy} ref={back.opener} onClick={(e) => { e.stopPropagation(); setOpen(true); }}
          aria-label={`Change who manages the funds for ${personName(row)}`}>Change</button>
        {err && <span className="um-notice stop" role="alert">{err}</span>}
      </span>
    );
  }
  return (
    <div className="um-rowpanel" onClick={(e) => e.stopPropagation()} onKeyDown={busy ? undefined : escapeCancels(cancel)}>
      <label className="um-rowfield"><span>Who manages the funds</span>
        <select value={type} disabled={busy} autoFocus aria-label={`Who manages the funds for ${personName(row)}`}
          onChange={(e) => setType(e.target.value)}>
          <option value="">Choose…</option>
          {(Object.keys(FINANCIAL_RESPONSIBILITY_LABEL) as FinancialResponsibilityType[]).map((k) => (
            <option key={k} value={k}>{FINANCIAL_RESPONSIBILITY_LABEL[k]}</option>
          ))}
        </select>
      </label>
      <label className="um-rowcheck">
        <input type="checkbox" checked={ach} disabled={busy} onChange={(e) => setAch(e.target.checked)} />
        <span>ACH on file</span>
      </label>
      <p className="um-rowpanel-what">Recorded against the resident, so the Patient Advocate sees the same answer.</p>
      {err && <p className="um-notice stop" role="alert">{err}</p>}
      <div className="um-rowpanel-acts">
        <button type="button" className="um-open um-primary" disabled={busy || !type} aria-describedby={(!type && 'Choose who manages the funds first.') ? `why-funds-${row.id}` : undefined} onClick={async () => {
          setBusy(true); setErr(null);
          try {
            const r = await umedApi.recordFinancialResponsibility(row.id, { type, ach_on_file: ach, idempotency_key: freshKey() });
            if (!r.success) { setErr(refusal(r)); return; }
            setJust({ type, ach });
            setOpen(false);
            acts?.announce(`Who manages the funds for ${personName(row)} is recorded: ${FINANCIAL_RESPONSIBILITY_LABEL[type as FinancialResponsibilityType] || type}${ach ? ', ACH on file' : ''}.`,
              row.id, { focusTo: () => back.opener.current });
          } catch { setErr('Could not reach the server. Nothing was recorded.'); } finally { setBusy(false); }
        }}>{busy ? 'Recording…' : 'Record'}</button><Why id={`why-funds-${row.id}`} reason={!type && 'Choose who manages the funds first.'} className="um-text um-why" />
        <button type="button" className="um-link" disabled={busy} onClick={cancel}>Cancel</button>
      </div>
    </div>
  );
}

/**
 * The terms, and a way to change them.
 *
 * Josie Evans, 2026-09-28: "I must be able to update them. Just because we
 * submit for terms to start in September does not mean DCF will approve for
 * that month. I need to be able to go in and change the months if necessary
 * or if they approved for different terms."
 *
 * What is shown is the later fact: terms a UMED operator settled if there are
 * any, otherwise the advocate's snapshot from when the lead closed, otherwise
 * the board's own sentence — quoted, never parsed, because deriving a number
 * from it would be putting a figure on a submission nobody made.
 *
 * Recording does not overwrite the sale. That figure is what was SOLD and it
 * feeds Finance and the monthly sales projection.
 */
/**
 * Terms: she types the months, and nothing else.
 *
 * Josie Evans, 2026-09-28: "On the submitted terms -- it pulls down a long box
 * of questions. It would be easier if I can just switch the months to what I
 * need it to be. Right now, I can click on it and just type in it instead of
 * doing extra steps."
 *
 * The panel shipped that morning asking four questions: months, each month,
 * starting, ending. Only the first is a decision -- DCF approves a NUMBER OF
 * MONTHS against a total that was already settled when the lead closed.
 *
 * Her own examples prove the arithmetic is ours to do, not hers. Every line
 * she pasted carries the same total against a different term:
 *
 *     4 months at $1,449.25 = $5,797        9 months at $644.11 = $5,797
 *     6 months at   $966.16 = $5,797        5 months at $1,159.40 = $5,797
 *
 * That is `monthly = total / months`, rounded to the cent, with the TOTAL held
 * fixed -- which is why 6 x $966.16 comes to $5,796.96 and the line still says
 * $5,797. The total is the sale, not the product, and the board has always
 * shown it that way.
 *
 * So eMerge derives what it can derive and asks only for what it cannot know.
 * Where no sale total is held -- and 2,879 open cases have none -- it cannot
 * do the arithmetic, and it asks for the monthly figure rather than invent
 * one. Start and end dates move behind "Dates", because DCF approving a
 * different month is the uncommon case and it should not tax the common one.
 */
/*
 * == One box (Josie Evans, 2026-10-02) ====================================
 *
 * "I also need to be able to type in the UMED terms and months if needed to be
 * updated. Just let me type in the box, I do not need different boxes to click
 * on for months/payments each month."
 *
 * So the editor is ONE text box, "Submitted terms", holding the line as she
 * writes it -- `9/1/26-3/1/27 (7 months at $828.14 = $5,797)`, or just `7 months
 * at 828.14`. The line is stored verbatim and the row shows it as typed. What
 * eMerge read from it is echoed underneath BEFORE it is recorded: months, the
 * amount, the dates. Where it reads only the months and a closed lead total is
 * held, the amount is worked out exactly as before (floored, the total held).
 * Where it reads nothing, the line is still kept, as typed, with no numbers --
 * the server never refuses an unread line.
 *
 * The structured fields are not gone: they are under "Details", for the rare
 * case she wants to set a number the line does not say. Used, they set the
 * numbers and re-write the line to match, so the row never shows one thing and
 * stores another.
 */
const boardSentence = (months: number, monthlyCents: number, totalCents: number | null, startsOn: string | null, endsOn: string | null): string => {
  const dates = startsOn && endsOn ? `${boardDate(startsOn)}-${boardDate(endsOn)} ` : startsOn ? `${boardDate(startsOn)} ` : '';
  return `${dates}(${months} ${months === 1 ? 'month' : 'months'} at ${cents(monthlyCents)}${totalCents !== null ? ` = ${cents(totalCents)}` : ''})`;
};

function TermsCell({ row }: { row: BoardRow }) {
  const acts = React.useContext(BoardActs);
  const [open, setOpen] = useState(false);
  const [details, setDetails] = useState(false);
  const t = row.submitted_terms;
  const totalCents = row.sale_amount === null || row.sale_amount === undefined ? null : Math.round(Number(row.sale_amount) * 100);
  const lineNow = (): string => (t?.text
    || (t && t.months !== null && t.monthly_cents !== null
      ? boardSentence(t.months, t.monthly_cents, totalCents, t.starts_on || null, t.ends_on || null)
      : '')
    || row.source_terms_raw || '');
  const [line, setLine] = useState(lineNow);
  const [months, setMonths] = useState(String(t?.months ?? ''));
  const [monthly, setMonthly] = useState(t && t.monthly_cents !== null ? (t.monthly_cents / 100).toFixed(2) : '');
  const [range, setRange] = useState(() => {
    const a = t?.starts_on ? String(t.starts_on).slice(0, 10) : '';
    const b = t?.ends_on ? String(t.ends_on).slice(0, 10) : '';
    return a && b ? `${a} to ${b}` : a || '';
  });
  const [note, setNote] = useState('');
  /*
   * Whether a Details field has been EDITED (verification of 1d86ec3). Opening
   * the fold changes nothing; editing a field there sets the numbers and
   * re-writes the line; typing in the line again hands the numbers back to the
   * server's reading of it.
   */
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setOpen(false); setDetails(false); setTouched(false); setErr(null); setLine(lineNow()); }, [row.id]);
  // Change takes focus back when the editor closes: Cancel, Escape, or recorded (P2-4).
  const back = useReturnFocus<HTMLButtonElement>(open);
  const cancel = () => { back.restore(); setOpen(false); setErr(null); };

  /* The Details fold, as before: months typed, the amount derived from the closed lead total or typed. */
  const m = Number(months);
  const monthsOk = Number.isInteger(m) && m >= 1 && m <= 60;
  /*
   * FLOOR, not round. Every sentence Josie pasted floors to the cent:
   * 5,797 / 6 is 966.1667 and the board has always said $966.16, never
   * $966.17. The remainder stays with the total, which is what was sold.
   */
  const derivedCents = totalCents !== null && monthsOk ? Math.floor(totalCents / m) : null;
  const cents_ = derivedCents !== null ? derivedCents : (Number.isFinite(Number(monthly)) && Number(monthly) > 0 ? Math.round(Number(monthly) * 100) : null);
  const parsedDates = parseTermDates(range);
  const datesOk = !range.trim() || parsedDates !== null;
  const detailsUsed = details && touched && months.trim() !== '';
  const detailsOk = monthsOk && cents_ !== null && cents_ > 0 && datesOk;

  // Details set the numbers: the line follows them, so what is shown is what is stored.
  useEffect(() => {
    if (open && touched && detailsUsed && detailsOk) {
      setLine(boardSentence(m, cents_ as number, totalCents, parsedDates ? parsedDates.starts_on : null, parsedDates ? parsedDates.ends_on : null));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [months, monthly, range, touched]);

  /*
   * The terms as ONE string, computed once: what the cell shows is what the
   * Copy button copies (Josie Evans, 2026-10-21: she could not copy the terms).
   * The "settled here" note and the Change button stay outside this string, so
   * a copy never carries a label.
   */
  const termsText = t
    ? (t.text
      /* The line she typed, as she typed it (2026-10-02). */
      || (
        /*
         * The board's own sentence, on one line: "8/1/26-5/1/27 (10 months at
         * $579.70 = $5,797)" (Josie Evans, 2026-09-29). The total is the
         * SALE, not months x monthly.
         */
        `${(t.starts_on || t.ends_on) ? `${t.starts_on ? boardDate(t.starts_on) : '?'}-${t.ends_on ? boardDate(t.ends_on) : '?'} ` : ''}`
        + `(${t.months} ${t.months === 1 ? 'month' : 'months'} at ${cents(t.monthly_cents)}`
        + `${row.sale_amount !== null && row.sale_amount !== undefined ? ` = ${cents(Math.round(Number(row.sale_amount) * 100))}` : ''})`
      ))
    : (row.source_terms_raw || '');
  const shown = t
    ? (
      /* A click on the terms belongs to the text, not the row: a real double click is a click, a click, then a dblclick,
         and the first click would otherwise open the case before the word is selected (Josie Evans, 2026-10-21). */
      <span onClick={(e) => e.stopPropagation()}>
        {termsText}
        {t.source === 'EMERGE' && <span className="um-sub">settled here</span>}
      </span>
    )
    : row.source_terms_raw
      ? <span className="um-text" title="The board's own words. eMerge did not produce this figure and does not parse it.">{row.source_terms_raw}</span>
      : <NotRecorded why="Neither eMerge nor the board holds terms for this case." />;
  const shownWithCopy = termsText
    ? <span className="um-followup">{shown}<CopyCell value={termsText} what="the submitted terms" /></span>
    : shown;

  if (!acts?.canTerms || row.stage === 'CLOSED') return shownWithCopy;
  if (!open) {
    return (
      <span className="um-followup">
        {shownWithCopy}
        <button type="button" className="um-link" ref={back.opener} onClick={(e) => { e.stopPropagation(); setLine(lineNow()); setOpen(true); }}
          aria-label={`Change the terms for ${personName(row)}`}>Change</button>
      </span>
    );
  }

  const typed = line.trim();
  const read = parseTermsLine(typed);
  /*
   * The typed path sends the LINE and nothing else: the server reads it and its
   * reading is what is stored (verification of 1d86ec3). The screen only says
   * what will be read; it never works out an amount for the line itself.
   */
  const fromLine = !detailsUsed && read.parsed;
  const tooLong = typed.length > 200;
  const ok = !tooLong && (detailsUsed ? detailsOk : typed.length > 0);

  return (
    <div className="um-rowpanel" onClick={(e) => e.stopPropagation()}>
      <label className="um-rowfield um-termsfield"><span>Submitted terms</span>
        <input value={line} disabled={busy} autoFocus maxLength={200}
          aria-label={`Submitted terms for ${personName(row)}`}
          placeholder="9/1/26-3/1/27 (7 months at $828.14 = $5,797)"
          onChange={(e) => { setLine(e.target.value); setTouched(false); }}
          onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); cancel(); } }} />
      </label>
      {tooLong && <p className="um-notice stop" role="alert">At most 200 characters.</p>}
      {!detailsUsed && typed && (fromLine
        ? (
          <p className="um-termsline um-termsread" aria-live="polite">
            Reads as {read.months} {read.months === 1 ? 'month' : 'months'} at <strong>{cents(read.monthly_cents)}</strong>
            {read.total_cents !== null && <> = {cents(read.total_cents)}</>}
            {read.starts_on && <> · {shortDate(read.starts_on)}{read.ends_on ? <> to {shortDate(read.ends_on)}</> : null}</>}
          </p>
        )
        : read.months_only !== null && totalCents !== null
          ? <p className="um-sub um-termsread" aria-live="polite">Reads {read.months_only} {read.months_only === 1 ? 'month' : 'months'}. eMerge will work the monthly amount out from the closed lead total when it is recorded.</p>
          : <p className="um-sub um-termsread" aria-live="polite">eMerge cannot read months and a monthly amount from this, so it will be kept exactly as you typed it, with no figures.</p>)}

      {details ? (
        <div className="um-termsdetails" role="group" aria-label="Terms details">
          <label className="um-rowfield"><span>Months</span>
            <input type="number" min={1} max={60} value={months} disabled={busy}
              aria-label={`Months of terms for ${personName(row)}`}
              onChange={(e) => { setMonths(e.target.value); setTouched(true); }} />
          </label>
          {derivedCents !== null ? (
            /* The sentence she reads on the board, formed as she types. */
            <p className="um-termsline" aria-live="polite">
              {m} {m === 1 ? 'month' : 'months'} at <strong>{cents(derivedCents)}</strong> = {cents(totalCents as number)}
            </p>
          ) : (
            <label className="um-rowfield"><span>Each month</span>
              <input type="number" min="0.01" step="0.01" value={monthly} disabled={busy}
                aria-label="What DCF approved each month"
                onChange={(e) => { setMonthly(e.target.value); setTouched(true); }} />
              <span className="um-sub">No closed lead total on this case, so eMerge cannot work it out.</span>
            </label>
          )}
          <label className="um-rowfield"><span>Dates</span>
            <input value={range} disabled={busy} maxLength={40}
              aria-label={`The term dates for ${personName(row)}`}
              placeholder="8/1/26 - 5/1/27"
              onChange={(e) => { setRange(e.target.value); setTouched(true); }} />
          </label>
          {range.trim() && (parsedDates
            ? (
              <p className="um-termsline" aria-live="polite">
                {shortDate(parsedDates.starts_on)}
                {parsedDates.ends_on ? <> to {shortDate(parsedDates.ends_on)}</> : <> · no end date</>}
              </p>
            )
            : <p className="um-notice stop" role="alert">Cannot read those dates. Try 8/1/26 - 5/1/27.</p>)}
          <label className="um-rowfield"><span>Why (optional)</span>
            <input value={note} maxLength={2000} disabled={busy} onChange={(e) => setNote(e.target.value)}
              placeholder="What DCF approved" />
          </label>
        </div>
      ) : (
        <button type="button" className="um-link" disabled={busy} aria-expanded={false} onClick={() => setDetails(true)}>
          Details
        </button>
      )}
      <p className="um-rowpanel-what">Recorded beside the sale, not over it — the closed lead total stays as it was.</p>
      {err && <p className="um-notice stop" role="alert">{err}</p>}
      <div className="um-rowpanel-acts">
        <button type="button" className="um-open um-primary" disabled={busy || !ok} aria-describedby={(!ok && firstReason([tooLong, 'Keep the terms to 200 characters.'], [detailsUsed, 'Fix the Details first: the months, the amount and the dates must each read.'], [true, 'Enter the terms first.'])) ? `why-terms-${row.id}` : undefined} onClick={async () => {
          setBusy(true); setErr(null);
          try {
            // Details EDITED: the numbers, said to come from there. Otherwise the line alone; the server reads it.
            const numbers = detailsUsed
              ? {
                entered: 'details' as const,
                months: m, monthly_cents: cents_ as number,
                // The total is the sale's, sent as such -- never months x monthly.
                total_cents: totalCents,
                starts_on: parsedDates ? parsedDates.starts_on : null,
                ends_on: parsedDates ? parsedDates.ends_on : null,
              }
              : {};
            const r = await umedApi.recordTerms(row.id, {
              terms_text: typed || null,
              ...numbers,
              note: note.trim() || null, idempotency_key: freshKey(),
            });
            if (!r.success) { setErr(refusal(r)); return; }
            setOpen(false);
            acts?.announce(`The terms for ${personName(row)} are recorded${typed ? `: ${typed}` : ''}.`, row.id, { focusTo: () => back.opener.current });
          } catch { setErr('Could not reach the server. Nothing was recorded.'); } finally { setBusy(false); }
        }}>{busy ? 'Recording…' : 'Record'}</button><Why id={`why-terms-${row.id}`} reason={!ok && firstReason([tooLong, 'Keep the terms to 200 characters.'], [detailsUsed, 'Fix the Details first: the months, the amount and the dates must each read.'], [true, 'Enter the terms first.'])} className="um-text um-why" />
        <button type="button" className="um-link" disabled={busy} onClick={cancel}>Cancel</button>
      </div>
    </div>
  );
}

/**
 * eMerge's stage. "Approved" when a UMED operator approved the case here; the
 * same stage without the act is the board's ("Approved in Monday · not approved in eMerge"), P2-6 and P3.
 * The Monday difference sits in the UMED status cell (StatusCell), where it is
 * on screen at 1440 -- this column is not.
 */
/**
 * P0 2026-10-07: where this episode's automatically prepared packet stands, in
 * words with a text marker (never colour alone), so UMEDs Needed tells a
 * prepared packet from one that is held, failed or still being made.
 */
const PREP_WORDS: Record<string, [string, string]> = {
  READY: ['!', 'Packet prepared - review required'], PREPARING: ['…', 'Preparing packet'], ACTION_REQUIRED: ['!', 'Action required'],
  FAILED: ['✕', 'Packet generation failed'], WITHDRAWN: ['–', 'Withdrawn'], SUBMITTED: ['✓', 'Submitted to DCF'],
};
export function PacketPrepLine({ prep }: { prep: BoardRow['packet_preparation'] }) {
  if (!prep) return null;
  const [marker, words] = prep.state === 'READY' && prep.review_recorded
    ? ['✓', 'Packet review recorded'] : PREP_WORDS[prep.state] || ['·', prep.state];
  const first = prep.state === 'ACTION_REQUIRED' && prep.blockers.length ? prep.blockers[0] : null;
  const detail = prep.state === 'READY' && prep.version
    ? ` · v${prep.version}${prep.pages ? ` · ${prep.pages} pp` : ''}${prep.revision_kind === 'FINAL' ? ' · dated for upload' : ''}`
    : prep.state === 'FAILED' && prep.error_code ? ` · ${prep.error_code}` : '';
  return (
    <span className={`um-prepline um-prep-${prep.state.toLowerCase()}`} title={first ? `${first.message} Resolved by ${PACKET_BLOCKER_OWNER_LABEL[first.owner] || first.owner}.` : undefined}>
      <span aria-hidden="true">{marker}</span> {words}{detail}
      {first ? <span className="um-quiet"> · {first.message}{prep.blockers.length > 1 ? ` (+${prep.blockers.length - 1} more)` : ''}</span> : null}
    </span>
  );
}

function StageCell({ row }: { row: BoardRow }) {
  return (
    <span className="um-stagecell">
      <span>{stageLabelOf(row.stage, isApproved(row))}</span>
      <PacketPrepLine prep={row.packet_preparation} />
    </span>
  );
}

/* ── Columns ──────────────────────────────────────────────────────────── */

/**
 * The column set, from §11. Not every tab shows every column: a table of 20
 * columns where 8 are empty is harder to reconcile than one that shows what
 * the tab is about, and §11 says so explicitly ("Do not force every tab to
 * show every column").
 *
 * `always` columns are on every tab; the rest name the tabs that carry them.
 */
interface Col {
  key: string;
  label: string;
  sort?: BoardSort;
  always?: boolean;
  tabs?: BoardTabKey[];
  /** Withheld unless the caller may read the approval facts. */
  needsApproval?: boolean;
  render: (r: BoardRow) => React.ReactNode;
}

/**
 * A dash, and on request the reason for it.
 *
 * Isaiah relaying Josie, 2026-09-23: "We also need the submitted terms
 * values, case #'s and Approval dates as of rn they are showing as -".
 *
 * Measured: submitted terms exist on 0 of 3,126 sales, case numbers on 69 of
 * 32,461 residents, and on this tab an approval date is empty BY DEFINITION
 * because the tab is cases that are not approved yet. None of those is a
 * broken column, and none of them can be filled by this screen.
 *
 * So the dash stays -- inventing a value would be worse -- but it carries the
 * reason, and the footer names the three together. A person who cannot tell a
 * missing value from a broken report will assume the report is broken, and
 * they will be right to.
 */
const NotRecorded = ({ why }: { why?: string }) => (
  <span className="um-text none" title={why}>{'—'}{why ? <span className="um-sr-only"> {why}</span> : null}</span>
);

/**
 * The one-click check's review (APR 02): FLMMIS checked, not yet reflected. It carries the NOCA as the case stands --
 * never "not recorded" over a NOCA that is on record -- so the review history says what was true when it was made.
 */
export const flmmisCheckReview = (row: Pick<BoardRow, 'approval'>, on: string) => ({
  noca: (nocaForward(row.approval.noca) || 'NOT_RECORDED') as NocaResult, flmmis: 'NOT_REFLECTED' as FlmmisResult, decision: 'CONTINUE' as const, follow_up_on: on,
});

/** The FLMMIS check marker, with the one-click check where the case is a partial approval with its correction open (APR 02, PAR 02-03). */
function FlmmisCheckCell({ row }: { row: BoardRow }) {
  const acts = React.useContext(BoardActs);
  const onCheck = partialApprovalOpen(row) && acts
    ? async (on: string) => {
      const r = await umedApi.review(row.id, flmmisCheckReview(row, on));
      if (!r.success) return refusal(r);
      acts.announce(`FLMMIS check recorded for ${personName(row)}.`, row.id);
      return null;
    }
    : undefined;
  return <FlmmisCheckMarker row={row} onCheck={onCheck} />;
}

const COLUMNS: Col[] = [
  { key: 'status', label: 'UMED status', sort: 'status', always: true, render: (r) => <StatusCell row={r} /> },
  { key: 'native-stage', label: 'eMerge stage', always: true, render: (r) => <StageCell row={r} /> },
  // APR 02 (2026-10-13): when FLMMIS was last checked and whether a check is due, where cases wait on FLMMIS.
  { key: 'flmmis-check', label: 'FLMMIS check', tabs: ['new-approvals-noca', 'flmmis-check-due'], needsApproval: true, render: (r) => <FlmmisCheckCell row={r} /> },
  {
    key: 'enrollment', label: 'Enrollment', always: true,
    render: (r) => (r.enrollment_state === 'ENROLLED'
      ? <span className="um-tag ok">Enrolled{r.enrollment_confirmed_at ? ` · ${shortDate(r.enrollment_confirmed_at)}` : ''}</span>
      : <span className={r.enrollment_state === 'NOT_ENROLLED_FINAL' ? 'um-tag stop' : 'um-tag'}>{ENROLLMENT_LABEL[r.enrollment_state]}</span>),
  },
  {
    key: 'liability', label: 'Patient Liability', sort: 'liability', always: true,
    render: (r) => (r.patient_liability_cents === null
      ? <NotRecorded />
      : <span className="um-money">{cents(r.patient_liability_cents)}{r.patient_liability_status === 'VERIFIED' ? <span className="um-tag ok">Verified</span> : null}</span>),
  },
  {
    key: 'terms', label: 'Submitted terms', always: true,
    /*
     * Josie Evans, 2026-09-24: "All patients' terms are still not under
     * submitted."
     *
     * They were not. The column read only the native sale's term snapshot,
     * which EP-010 began recording on 2026-09-22 and which exists on TWO
     * rows. The board's own terms sentence is on 1,505 cases -- 215 of them
     * at SUBMITTED -- and the column never looked at it.
     *
     * So: the native snapshot when there is one, because it is structured
     * and eMerge produced it; otherwise the board's sentence, shown as the
     * board wrote it. Deriving a number from that sentence would be putting
     * a figure on a submission nobody made, so it is quoted, not parsed.
     */
    render: (r) => <TermsCell row={r} />,
  },
  {
    key: 'sale', label: 'Closed lead total', sort: 'sale', always: true,
    render: (r) => (r.sale_amount === null
      ? <NotRecorded />
      : (
        <span className="um-money">
          {dollars(r.sale_amount)}
          <CopyCell value={dollars(r.sale_amount) ?? String(r.sale_amount)} what="the closed lead total" />
          {r.sale_product_label && <span className="um-sub">{r.sale_product_label}</span>}
        </span>
      )),
  },
  {
    key: 'funds', label: 'Who manages the funds', always: true,
    // Josie Evans, 2026-09-23, listing her New Approvals columns in order:
    // "who manages the funds" is third. Read-only, from Patient Demographics,
    // and honest about its gaps -- four in five open cases carry nothing, and
    // a blank is shown as a blank rather than defaulted to a payor nobody
    // chose. A word the vocabulary does not hold is shown AS WRITTEN.
    render: (r) => <FundsCell row={r} />,
  },
  {
    key: 'case-number', label: 'Case #', always: true,
    // Held for 69 of 32,461 residents. The dash is the truth, and saying which
    // truth it is beats leaving Josie to wonder whether the column is broken.
    render: (r) => (r.case_number ? <span className="um-followup"><span>{r.case_number}</span><CopyCell value={r.case_number} what="the case number" /></span> : <NotRecorded why="eMerge holds a case number for very few residents. It has never been imported in bulk." />),
  },
  { key: 'dob', label: 'DOB', always: true, render: (r) => (r.resident_dob ? <span className="um-followup"><span>{shortDate(r.resident_dob)}</span><CopyCell value={shortDate(r.resident_dob)} what="the date of birth" /></span> : <NotRecorded />) },
  {
    key: 'ssn', label: 'SSN', always: true,
    // Isaiah, 2026-09-22: "make the SSN viewable when clicked on, not
    // necessarily out in the open ... it is very important for the work they
    // do." So the number is in the table and one click away — and never in the
    // payload that draws the table. See SsnCell.
    render: (r) => <SsnCell row={r} />,
  },
  {
    /*
     * Josie Evans, 2026-09-24: "Next to the SS# - it would be nice to have
     * the patients Medicaid # listed so I can copy & paste it instead of
     * going into CounselEAR."
     *
     * CounselEAR does not expose it -- PatientByID returns 23 fields and
     * none is an employee or Medicaid identifier -- so it is typed once here
     * and copied ever after. Shown in full: it is an identifier she already
     * reads off CounselEAR's screen, and a masked one she cannot copy would
     * defeat the entire request.
     */
    key: 'medicaid', label: 'Medicaid #', always: true,
    render: (r) => <MedicaidCell row={r} />,
  },
  {
    key: 'available', label: 'Lead available', sort: 'available', always: true,
    render: (r) => (r.lead_available
      ? (
        <span>
          {shortDate(r.lead_available.on)}
          <span className="um-sub">
            {relativeDay(r.lead_available.on)}
            {r.lead_available.basis === 'FIRST_SEEN' ? ' · first seen' : ''}
          </span>
        </span>
      )
      : <NotRecorded />),
  },
  {
    key: 'dcf', label: 'DCF account', always: true,
    // um-dcfcell (Josie, 2026-10-15, "a large gap"): the cell wraps within a fixed measure. A stuck mark's reason is free
    // text up to 500 characters; unwrapped in an uppercase tag it made this column hundreds of pixels wide on every row.
    render: (r) => (
      <span className="um-dcfcell">
        {r.dcf_account?.email
          ? <><span className="um-mono">{r.dcf_account.email}</span><CopyCell value={r.dcf_account.email} what="the DCF account email" /></>
          : <span className="um-text none">No email</span>}
        {/*
          * Two lines, not three. Making the password button always visible on
          * 2026-09-28 put it on a line of its own, which made every row taller
          * -- and Josie's very next note was "I can still only see 3 patients
          * at once." The tallest cell sets the row height, so the button sits
          * beside the state rather than beneath it.
          *
          * The password is never printed here. "Copy password" reveals it on
          * the server, audited per reveal, and writes it straight to the
          * clipboard (Josie 2026-09-24; approved by Isaiah).
          */}
        <span className="um-sub">
          {r.dcf_account?.state === 'EMAIL_ASSIGNED' && r.dcf_account?.revealable ? <DcfLoginReady caseId={r.id} name={personName(r)} /> : <>
          {/*
            * Review for Approvals holds cases with no DCF login made (director's ruling, 2026-10-06), so the
            * cell says it plainly: no account recorded in eMerge, for this case or another of the resident's,
            * is "No account made" -- and the board's own "no DCF account" box is named as Monday's word.
            */}
          {/* "No account made" only where an account is needed and absent (verification of 676fdb9, P2-3); Not required and Unknown as before. */}
          {/*
            * 2026-10-19: on a SUBMITTED case the Monday board carries, NOT_REQUIRED with no email only means nobody ticked
            * Monday's no-account box -- not that no account is needed. Said as what it is (dcfNeedWords).
            */}
          {r.dcf_account?.need === 'PROVISIONED' ? 'Account made'
            : r.dcf_account?.need === 'ISSUE' ? 'Could not be made'
              : r.dcf_account?.need === 'NOT_REQUIRED' ? notRequiredWords(r)
                : r.dcf_account?.need !== 'NEEDED' ? 'Unknown'
                  : r.dcf_account?.source === 'EMERGE' ? 'Needed'
                    : <>No account made{r.dcf_account_state === 'REQUIRED' && <span className="um-quiet"> · Monday&rsquo;s word: no DCF account</span>}</>}
          {r.dcf_account?.state === 'ACCOUNT_CREATED' && <CopyPassword caseId={r.id} />}
          </>}
          {/* Ruling #6 (2026-10-07): Monday's DCF EMAIL CREATED = Stuck, a marker only -- shown, never applied. */}
          <MondayDcfEmailMarker row={r} />
          {/* Gwynn, 2026-10-12: eMerge's own record of it -- "(STUCK) Can't link benefits", with her reason. */}
          {r.dcf_benefits?.state === 'STUCK' && (
            <span className="um-tag stop" data-testid="dcf-benefits-stuck" title={benefitsLine(r.dcf_benefits) || undefined}>
              {DCF_BENEFITS_STUCK_LABEL}{r.dcf_benefits.reason ? <span className="um-quiet">: {r.dcf_benefits.reason}</span> : null}
            </span>
          )}
          {/* Josie, 2026-10-15: STUCK where the account is read, on every open case not approved. */}
          <DcfStuckCellAct row={r} />
        </span>
      </span>
    ),
  },
  {
    key: 'documents', label: 'UMED · Audiogram', always: true,
    render: (r) => (
      <span className="um-docs">
        <span className={`um-pip${r.documents?.umed ? ' on' : ''}`} aria-hidden="true">U</span>
        <span className={`um-pip${r.documents?.audiogram ? ' on' : ''}`} aria-hidden="true">A</span>
        <span className="um-sr-only">
          UMED {r.documents?.umed ? 'on file' : r.documents?.umed === null ? 'not verified' : 'not on file'};
          audiogram {r.documents?.audiogram ? 'on file' : r.documents?.audiogram === null ? 'not verified' : 'not on file'}.
        </span>
      </span>
    ),
  },
  /**
   * Josie Evans, 2026-09-28: "I can record NOCA and FLMMIS in a different
   * category" and "If I can have a box to put the NOCA and just upload it."
   *
   * Both columns already existed and were read-only -- the two facts could be
   * SEEN here and only CHANGED from inside the follow-up panel, which is why
   * that panel had to ask three questions to record a date. Each is now
   * changeable where it is read, and the follow-up went back to being a date.
   */
  {
    key: 'noca', label: 'NOCA', needsApproval: true, always: true,
    render: (r) => <NocaCell row={r} />,
  },
  {
    key: 'flmmis', label: 'FLMMIS', needsApproval: true, always: true,
    render: (r) => <FlmmisCell row={r} />,
  },
  // Josie Evans, 2026-10-21: Monday's single NOCA/FLM… column -- the two files at a glance, a click opens one.
  {
    key: 'noca-flmmis-files', label: 'NOCA / FLMMIS', needsApproval: true, always: true,
    render: (r) => <FileIconsCell row={r} />,
  },
  /**
   * The board's FAXED? column, mirrored.
   *
   * April, 2026-09-24, naming what her view turns on: "Key notations:
   * STATUS TYPE OF UMED / FAXED? DISTINCTION / SENT TO DCF". Its values are
   * sentences rather than a yes or no -- "(FAXED) still NEED SSN #",
   * "Need SIGNED UMED", "NEEDS NEW UMED" -- so it is rendered as the board
   * wrote it and never reduced to a tick.
   */
  {
    key: 'faxed', label: 'Faxed?', always: true,
    render: (r) => (r.source_faxed_status_raw
      ? <span className="um-text">{r.source_faxed_status_raw}</span>
      : <NotRecorded why="The board holds no faxed note for this case." />),
  },
  { key: 'submitted-on', label: 'UMED submitted', sort: 'submitted', always: true, render: (r) => (r.submitted_on ? <span>{shortDate(r.submitted_on)}</span> : <NotRecorded />) },
  {
    key: 'sent-to-dcf', label: 'Sent to DCF', sort: 'sent-to-dcf', always: true,
    // 2026-10-13: the step of the first sending still owed (DCF needs both a fax and an upload). A marker only: no tab moves.
    render: (r) => <SentToDcfCell row={r} />,
  },
  /**
   * Josie Evans, 2026-09-24: she works an approval queue "oldest 'date lead
   * closed' sale to the newest". The field was on the row and had no column,
   * so the thing she orders her day by could not be seen or sorted.
   */
  /*
   * Isaiah Cotto, 2026-10-14 (second ruling): the day the Patient Advocate closed the SALE behind this UMED
   * (pa_case_sales.sale_closed_at, the New York day), and sortable on it. Until then it read umed_cases.closed_on,
   * which no open case has, so it said "Not recorded" on every open row and sorted nothing. "Not recorded" now means
   * exactly that no sale is on record for the PA case behind the UMED.
   */
  {
    key: 'lead-closed', label: 'Date lead closed', sort: 'lead-closed', always: true,
    render: (r) => (r.lead_closed_on
      ? <span title={r.lead_closed_confidence === 'SOURCE_REPORTED' ? "Monday's Date Closed for the sale" : 'Recorded in eMerge when the sale closed'}>{shortDate(r.lead_closed_on)}</span>
      : <NotRecorded why="No sale is on record for the Patient Advocate's case behind this UMED." />),
  },
  { key: 'last-follow-up', label: 'Last follow-up', sort: 'last-follow-up', always: true, render: (r) => <FollowUpCell row={r} /> },
  /**
   * Josie Evans, 2026-09-28: "It is also not showing me who did the sale. I
   * need to be able to see who the patient advocate is in case there are
   * questions on the account."
   *
   * The name was already on the row and had no column -- the same shape of
   * miss as "Date lead closed" four days earlier. Nothing was computed for
   * this; it was only ever hidden.
   */
  {
    key: 'advocate', label: 'Patient advocate', always: true,
    render: (r) => (r.advocate_name
      ? <span>{r.advocate_name}</span>
      : <NotRecorded why="No advocate is recorded against this case." />),
  },
  {
    key: 'approved-on', label: 'Approval date', sort: 'approved', always: true,
    // The board's APPROVED date is a source claim. It is shown, and shown to be
    // one: eMerge's own approval needs FLMMIS reflected.
    // Approved in eMerge, the date is the act's; the board's date it replaced is kept beside it (P2-3, 2026-10-06).
    render: (r) => (r.approved_on
      ? (
        <span>
          {shortDate(r.approved_on)}
          {r.approval.canonical !== 'PROVEN'
            ? <span className="um-sub">source claim</span>
            : r.board_approved_on ? <span className="um-sub">board said {shortDate(r.board_approved_on)}</span> : null}
        </span>
      )
      : <NotRecorded why="No approval date: the case is not approved in eMerge, and the board holds no date for it." />),
  },
];

/**
 * The order a tab's columns sit in, where the business has asked for one.
 *
 * Josie Evans, 2026-09-23, for New Approvals: "Patient name, Facility name,
 * who manages the funds, liability, dcf account & password next to each
 * other, terms submitted, closed lead total, NOCA, Last umed follow up date."
 *
 * Patient and Facility are not in this list because they are not in COLUMNS:
 * the patient is the frozen row header and the facility is the cell beside
 * it, so they are already first and second.
 *
 * Naming an order does NOT hide the rest. Every other column still follows,
 * in the table's own order -- a reconciliation view that silently dropped a
 * column would be worse than one with columns in the wrong place.
 */
const TAB_COLUMN_ORDER: Partial<Record<BoardTabKey, string[]>> = {
  // The status word IS this tab (SFS CORRECTION-FLMMIS ISSUE), so it leads (design verification P2-8).
  'new-approvals-noca': ['status', 'funds', 'liability', 'dcf', 'terms', 'sale', 'noca', 'noca-flmmis-files', 'last-follow-up'],
  // April's own reading order for this group, 2026-09-24: the UMED status,
  // whether it was faxed, whether it reached DCF, then the two identifiers
  // she checks a row against.
  'board-umeds-needed': ['status', 'sent-to-dcf', 'faxed', 'ssn', 'dob', 'documents', 'dcf'],
  // Oldest first is how Josie works this queue, so the date she sorts by
  // sits beside the two approval facts she is checking.
  // The UMED status (with Approve) sits right after FLMMIS: record the evidence, then approve beside it --
  // at 1280 the Approve control was off-screen behind DCF (design verification 2026-10-06, P1-a).
  'review-for-approvals': ['lead-closed', 'noca', 'flmmis', 'noca-flmmis-files', 'status', 'sent-to-dcf', 'last-follow-up', 'dcf'],
  // 2026-10-06: a completed correction is waiting on DCF like any sending -- the approval columns lead.
  'dcf-correction-completed': ['status', 'lead-closed', 'noca', 'flmmis', 'sent-to-dcf', 'last-follow-up', 'dcf'],
  // 2026-10-06: Monday says it went to DCF; the row's Mark submitted is in the status cell, beside eMerge's stage.
  'sent-in-monday': ['status', 'native-stage', 'sent-to-dcf', 'submitted-on', 'dcf', 'documents'],
  // UM-R1: a problem account goes back to the PA, so who that is and how to reach the account come first.
  'problem-accounts': ['status', 'advocate', 'dcf', 'funds', 'last-follow-up'],
  // Gwynn, 2026-10-12: the DCF account cell carries the stuck mark and its reason, so it leads.
  'dcf-benefits-stuck': ['dcf', 'status', 'documents'],
};

/*
 * Josie Evans, 2026-10-15: "can we have the Medicaid # right next to the DCF account and then the terms after that" --
 * on every UMED table. Wherever the DCF account column is, Medicaid # and Submitted terms follow it, in that order; every
 * other column keeps its place.
 */
const AFTER_DCF = ['medicaid', 'terms'];
export function dcfThenMedicaidThenTerms<T extends { key: string }>(cols: T[]): T[] {
  const at = cols.findIndex((c) => c.key === 'dcf');
  if (at < 0) return cols;
  const moved = AFTER_DCF.map((k) => cols.find((c) => c.key === k)).filter(Boolean) as T[];
  const rest = cols.filter((c) => !AFTER_DCF.includes(c.key));
  const dcf = rest.findIndex((c) => c.key === 'dcf');
  return [...rest.slice(0, dcf + 1), ...moved, ...rest.slice(dcf + 1)];
}

export const columnsFor = (tab: BoardTabKey, approvalReview: boolean) => {
  const shown = COLUMNS.filter((c) => (c.always || (c.tabs || []).includes(tab)) && (!c.needsApproval || approvalReview));
  const wanted = TAB_COLUMN_ORDER[tab];
  if (!wanted) return dcfThenMedicaidThenTerms(shown);
  const first = wanted.map((k) => shown.find((c) => c.key === k)).filter(Boolean) as typeof shown;
  const rest = shown.filter((c) => !wanted.includes(c.key));
  return dcfThenMedicaidThenTerms([...first, ...rest]);
};

/**
 * What the rows are, while a search is on (round 4, 2026-10-07). The number said is the number of rows, always:
 * in a tab, the tab's matching rows (residents on a work tab, cases on Main table); across every case, the cases.
 * The offer to look across every case carries the number of rows it opens.
 */
export function SearchBanner({ q, data, tabLabel, perResident, onEvery, onBack, onClear }: {
  q: string; data: BoardPage | null; tabLabel: string; perResident: boolean;
  onEvery: () => void; onBack: () => void; onClear?: () => void;
}) {
  const every = Boolean(data && data.scope === 'EVERYWHERE');
  const n = data ? data.total : null;
  const noun = every ? 'case' : perResident ? 'resident' : 'case';
  const counted = n === null ? null : `${n} ${noun}${n === 1 ? '' : 's'}`;
  const more = !every && data && typeof data.everywhere_total === 'number' ? data.everywhere_total : null;
  return (
    <div className={`um-everywhere${every ? '' : ' um-intab'}`} role="status">
      <span className="um-everywhere-mark" aria-hidden="true">⌕</span>
      {every ? (
        <span>
          <strong>Results from every case</strong> for “{q}”{counted ? <> — {counted}, open first</> : null}.
          {' '}Not only {tabLabel}; the counts on the tabs still count their own view.
        </span>
      ) : (
        <span>
          <strong>In {tabLabel}</strong>{counted ? <>: {counted} {n === 1 ? 'matches' : 'match'}</> : null} “{q}”.
          {' '}{perResident ? 'One row per resident; their other cases are behind “other cases” on the row.' : 'One row per case.'}
        </span>
      )}
      {every
        ? <button type="button" className="um-link" onClick={onBack}>Back to {tabLabel}</button>
        : more !== null && more > 0 && (
          <button type="button" className="um-link um-everybtn" onClick={onEvery}
            aria-label={`Search every case: ${more} ${more === 1 ? 'case matches' : 'cases match'} “${q}”`}>
            Search every case ({more})
          </button>
        )}
      {onClear && <button type="button" className="um-link" onClick={onClear}>Clear search</button>}
    </div>
  );
}

/* ── The table ────────────────────────────────────────────────────────── */

export default function BoardTable({ scope, tab, q, page, onTab, onPage, onSelect, selected, detail, onClearSearch, onContinue, changed = null }: {
  scope: Scope;
  /**
   * An act recorded in the case panel under a row (a fresh `n` per act): the rows are read again, so the row says what
   * was recorded without a manual reload (2026-10-12). When the act took the case out of this view, the board's status
   * line says so, since the panel that said it went with the row.
   */
  changed?: { n: number; msg: string; caseId: string | null } | null;
  tab: BoardTabKey;
  q: string;
  /** Clear the search and go back to the tab (2026-10-06). */
  onClearSearch?: () => void;
  page: number;
  onTab: (t: BoardTabKey) => void;
  onPage: (p: number) => void;
  onSelect: (id: string) => void;
  /**
   * An act chosen on a row that continues in the case panel (Gwynn, 2026-10-12): UMED UPLOADED TO DCF opens the packet's
   * Confirm DCF upload, DCF EMAIL CREATED the DCF account form -- one act each, never a second copy of it on the row.
   * Without it the case is simply opened.
   */
  onContinue?: (id: string, kind: CaseFocus['kind']) => void;
  selected: string | null;
  /**
   * The open case, rendered UNDER its own row (Isaiah, 2026-09-22: "when
   * opening a case it should open underneath the record instead of the right
   * of the component").
   *
   * A pane beside the table cost roughly 40% of the width on every screen,
   * including while nothing was open — and on a table this wide that width is
   * the whole point. Underneath, the case also keeps its row in view, so the
   * resident you opened is still the resident you are reading.
   */
  detail?: React.ReactNode;
}) {
  const tabs: BoardTabDef[] = scope.board_tabs || [];
  const legend: LegendEntry[] = scope.status_legend || [];
  const [data, setData] = useState<BoardPage | null>(null);
  // Which view `data` answers (third verification of #446): set with it, from the request's own view key.
  const [dataView, setDataView] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [sort, setSort] = useState<{ col: BoardSort; dir: 'asc' | 'desc' } | null>(null);

  // A different tab is a different question, so it starts unsorted rather than
  // carrying the last tab's ordering into it.
  useEffect(() => { setSort(null); }, [tab]);

  /*
   * Josie Evans, round 4 (2026-10-07), points 1 and 2: "search shows 3 in Review for Approvals" -- one case in
   * Review and two old episodes, each a full row, read as duplicates in Review. A search IN A TAB is now that
   * tab's answer: its own matching rows, one per resident, the resident's other cases behind "N other cases".
   * Every case is one explicit step away ("Search every case", with the number of rows it opens), and it ends
   * when the search or the tab changes. All cases keeps searching everything.
   */
  const [everyCase, setEveryCase] = useState(false);
  useOnRowChange(`${tab}|${q}`, () => setEveryCase(false));
  const searching = Boolean(q.trim());
  const wantEverywhere = searching && everyCase;
  /*
   * The view the rows on screen answer (verification of #446): tab, search, page, every-case and sort each change which
   * rows are listed. Kept current DURING render, so an act's answer can ask, when it arrives, whether the board is
   * still showing the view the act was started in. Every callback below is made for one view and does nothing once
   * the board has moved on: an Approve answered after a search for someone else names nobody, keeps no row, opens no
   * case, and never re-reads the old view over the new one.
   */
  const viewKey = `${tab}|${q}|${page}|${wantEverywhere ? 'every' : 'view'}|${sort ? `${sort.col}:${sort.dir}` : ''}`;
  const viewNow = React.useRef(viewKey);
  viewNow.current = viewKey;
  /*
   * And only while the board is on screen (second verification of #446): an answer that arrives after the table was
   * left -- for the Kanban, another view -- must not open a case (which navigates back), say anything or read rows.
   */
  const mounted = React.useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const live = useCallback((key: string) => mounted.current && viewNow.current === key, []);
  // Search as you type (round 4): a newer request makes the one in flight stale -- it is cancelled, and its answer, if it
  // still arrives, is never shown over the newer one.
  const inFlight = React.useRef<AbortController | null>(null);
  const readsDone = React.useRef(0);
  const load = useCallback(async () => {
    // A re-read asked for by an act of a view the board has left is not made: it would cancel the current view's read.
    if (!live(viewKey)) return;
    if (inFlight.current) inFlight.current.abort();
    const ctl = typeof AbortController === 'function' ? new AbortController() : null;
    inFlight.current = ctl;
    const stale = () => inFlight.current !== ctl;
    setLoading(true); setErr(null);
    try {
      const r = await umedApi.board({ tab, q, page, sort: sort?.col ?? null, dir: sort?.dir ?? null, everywhere: wantEverywhere }, ctl ? ctl.signal : undefined);
      if (stale()) return;
      readsDone.current += 1;
      if (r.success && r.data) { setData(r.data); setDataView(viewKey); }
      else { setData(null); setErr(r.code === 'TAB_UNKNOWN' ? 'That view does not exist.' : (r.error || r.message || 'The server refused this view.')); }
    } catch { if (!stale()) { setData(null); setErr('Could not reach the server.'); } } finally { if (!stale()) setLoading(false); }
  }, [tab, q, page, sort, wantEverywhere, viewKey, live]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => () => { if (inFlight.current) inFlight.current.abort(); }, []);
  const seenChange = React.useRef(changed ? changed.n : 0);
  const afterChange = React.useRef<{ msg: string; caseId: string | null; readsBefore: number } | null>(null);
  // The rows on screen now (the server's and any kept copy), set below once they are known.
  const onScreen = React.useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!changed || changed.n === seenChange.current) return;
    seenChange.current = changed.n;
    /*
     * "The case has moved out of this view" is only true of a case this view was showing (second verification of
     * #446): an act in the case panel can be answered after a search for someone else -- the case stays open across a
     * search -- and its sentence then named a resident this view never listed. The rows are read again either way;
     * the sentence is said only for a case on screen when the act's answer came. A view change before the re-read
     * drops it as before (the clear-on-change hook).
     */
    afterChange.current = changed.caseId && onScreen.current.has(changed.caseId)
      ? { msg: changed.msg, caseId: changed.caseId, readsBefore: readsDone.current }
      : null;
    load();
  }, [changed, load]);

  /*
   * Asked once for the whole board. A failure is `false`, not `null`: if we
   * cannot establish that the reveal is available, the screen does not offer
   * it. Refusing to offer an act we are unsure of is the safe direction for a
   * control that decrypts a social security number.
   */
  const [canReveal, setCanReveal] = useState<boolean | null>(null);
  useEffect(() => {
    let live = true;
    (async () => {
      try { const r = await residentsApi.scope(); if (live) setCanReveal(Boolean(r.success && r.data && r.data.can.reveal_ssn)); }
      catch { if (live) setCanReveal(false); }
    })();
    return () => { live = false; };
  }, []);

  /*
   * The board's one status line (design verification 2026-10-06, P1-b). Every
   * row act writes what it did here -- "Ann Nelson is approved. The Finance
   * handoff is open." -- because the control that made it is gone by the time
   * the board re-reads. Cleared first, then set, so the same sentence twice is
   * announced twice. Always in the DOM, so a screen reader is already
   * listening to it before anything is said.
   */
  const [rowNotice, setRowNotice] = useState('');
  const noticeRef = React.useRef<HTMLParagraphElement | null>(null);
  const focusAfterLoad = React.useRef<{ rowId: string; focusTo?: () => HTMLElement | null } | null>(null);
  const noticeTimer = React.useRef<number | null>(null);
  useEffect(() => () => { if (noticeTimer.current !== null) window.clearTimeout(noticeTimer.current); }, []);
  const announce = useCallback((msg: string, rowId: string, opts?: AnnounceOpts) => {
    // Said only in the view the act was made in (verification of #446).
    if (!live(viewKey)) return;
    setRowNotice('');
    if (noticeTimer.current !== null) window.clearTimeout(noticeTimer.current);
    noticeTimer.current = window.setTimeout(() => { noticeTimer.current = null; setRowNotice(msg); }, 30);
    focusAfterLoad.current = opts?.keepFocus ? null : { rowId, focusTo: opts?.focusTo };
    load();
  }, [load, viewKey, live]);

  /*
   * Rows approved here, kept on the board until it is left (2026-10-20). Taken from the rows on screen at the
   * moment of the approval, so the copy sits where the row was.
   */
  const [approvedKept, setApprovedKept] = useState<ReadonlyMap<string, KeptApproval>>(() => new Map());
  const shownData = React.useRef<BoardPage | null>(null);
  shownData.current = data;
  const approvedInPlace = useCallback((row: BoardRow, outcome: ApprovedOutcome) => {
    if (!live(viewKey)) return;
    const at = shownData.current ? shownData.current.items.findIndex((r) => r.id === row.id) : -1;
    setApprovedKept((m) => new Map(m).set(row.id, { row: approvedCopy(row), index: at < 0 ? 0 : at, outcome }));
  }, [viewKey, live]);

  /*
   * Another view is another question (2026-10-20): the status line said what was done to a row of the view that was
   * left -- "Ann Nelson is approved" over a different tab or page named the wrong resident -- so it is cleared, with
   * one still waiting to be said, and the rows kept from approvals here go with it. Never on the first render.
   */
  useOnRowChange(viewKey, () => {
    if (noticeTimer.current !== null) { window.clearTimeout(noticeTimer.current); noticeTimer.current = null; }
    setRowNotice('');
    afterChange.current = null;
    focusAfterLoad.current = null;
    setApprovedKept(new Map());
  });
  const shown = React.useMemo(() => (data ? withApprovedKept(data.items, approvedKept) : { items: [] as BoardRow[], keptIds: new Set<string>() }), [data, approvedKept]);
  /*
   * Only rows that answer the view on screen count (third verification of #446): after a search, tab or page change
   * `data` still holds the left view's rows until the new read lands, and a case-panel answer in that window would
   * find its case "on screen" and then say it moved out of a view that never listed it. Until the new rows land,
   * nothing is on screen for that purpose; the rows are read again either way.
   */
  onScreen.current = dataView === viewKey ? new Set(shown.items.map((r) => r.id)) : new Set();
  const opsWorkspaces = useOpsWorkspaces('UMED');
  const financePath = (opsWorkspaces.find((w) => w.key === 'FINANCE') || null)?.path || null;
  /*
   * After the re-read: back to the control that opened the act when it is
   * still there (P2-4), else that row's name, or -- when the act took the row
   * out of this tab -- the status line that says where it went. Never left on
   * the page body. preventScroll throughout: a re-read must not throw the
   * table sideways (P3, the late-NOCA jump).
   */
  useEffect(() => {
    const after = afterChange.current;
    if (!after || loading || readsDone.current <= after.readsBefore || !data) return;
    afterChange.current = null;
    if (!after.caseId || data.items.some((r) => r.id === after.caseId)) return;
    setRowNotice(`${after.msg} The case has moved out of this view.`);
    window.setTimeout(() => noticeRef.current?.focus({ preventScroll: true }), 0);
  }, [data, loading]);
  useEffect(() => {
    const target = focusAfterLoad.current;
    if (!target || loading) return;
    focusAfterLoad.current = null;
    const own = target.focusTo ? target.focusTo() : null;
    if (own && own.isConnected && !(own as HTMLButtonElement).disabled) { own.focus({ preventScroll: true }); return; }
    const opener = Array.from(document.querySelectorAll<HTMLButtonElement>('.um-rowopen'))
      .find((b) => b.dataset.rowId === target.rowId);
    if (opener) opener.focus({ preventScroll: true });
    else noticeRef.current?.focus({ preventScroll: true });
  }, [data, loading]);

  /*
   * The selected tab is on screen (P3, 2026-10-06): at 375 the tab bar scrolls sideways and a tab opened from a
   * link -- New Approvals - NOCA is the eleventh -- sat off the right edge, so the bar showed no selected tab at all.
   * The bar scrolls itself, never the page.
   */
  const tabBar = React.useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const bar = tabBar.current;
    const on = bar?.querySelector<HTMLElement>('[aria-selected="true"]');
    if (!bar || !on) return;
    const delta = on.getBoundingClientRect().left - bar.getBoundingClientRect().left;
    if (delta < 0 || delta + on.offsetWidth > bar.clientWidth) bar.scrollLeft = Math.max(0, bar.scrollLeft + delta - 16);
  }, [tab, tabs.length]);

  const cols = columnsFor(tab, scope.approval_review);
  const badge = (t: BoardTabDef) => scope.counts.board_tabs?.[t.key as BoardTabKey];
  const everywhere = Boolean(data && data.scope === 'EVERYWHERE');
  // Clearing the search gives focus to the search box it came from, not to the page body (P2-1).
  const clearSearch = onClearSearch ? () => {
    onClearSearch();
    window.setTimeout(() => document.querySelector<HTMLInputElement>('.um-search input[type="search"]')?.focus(), 0);
  } : undefined;

  /*
   * What may be recorded from a row, asked once. `canDo` already requires
   * writes to be NATIVE, so a process with the gate shut offers nothing
   * rather than offering a control that 404s.
   */
  const boardActs: BoardActsValue = React.useMemo(() => ({
    // The capabilities are the scope's, shared with the docked record's Set status (boardActsCapabilities).
    ...boardActsCapabilities(scope),
    openCase: (id) => { if (selected !== id) onSelect(id); },
    continueCase: (id, kind) => {
      // An act answered after the board moved on opens nothing in the new view (verification of #446).
      if (!live(viewKey)) return;
      if (onContinue) onContinue(id, kind); else if (selected !== id) onSelect(id);
    },
    reload: load,
    announce,
    approvedInPlace,
    approvedHere: (id) => {
      const k = approvedKept.get(id);
      return k ? { outcome: k.outcome, kept: shown.keptIds.has(id) } : null;
    },
    financeHref: (patientId) => (financePath ? `${financePath}?view=search&resident=${encodeURIComponent(patientId)}` : null),
  }), [scope, load, announce, onSelect, selected, onContinue, approvedInPlace, approvedKept, shown, financePath, viewKey, live]);

  return (
    <BoardActs.Provider value={boardActs}>
    <CanReveal.Provider value={canReveal}>
    <section className="um-card um-board" aria-label="Board views">
      {/* The board's own tab bar, in the board's own order. */}
      <div className="um-boardtabs" role="tablist" aria-label="Board views" ref={tabBar}>
        {tabs.map((t) => (
          <button key={t.key} type="button" role="tab" aria-selected={tab === t.key} title={`${t.what} Replaces the board's ${t.board}.`}
            className={`um-boardtab${tab === t.key ? ' on' : ''}`} onClick={() => onTab(t.key as BoardTabKey)}>
            {t.label}
            {badge(t) !== undefined && <span className="um-count">{badge(t)}</span>}
          </button>
        ))}
      </div>

      {searching && (
        <SearchBanner q={q.trim()} data={data} tabLabel={tabs.find((t) => t.key === tab)?.label || 'this tab'} perResident={(tabs.find((t) => t.key === tab)?.rows || 'RESIDENT') !== 'CASE'}
          onEvery={() => setEveryCase(true)} onBack={() => setEveryCase(false)} onClear={clearSearch} />
      )}

      <div className="um-board-head">
        {/* While the rows are every case, the tab's own description is not what they are: muted, and said so (P2-7). */}
        <p className={`why${everywhere ? ' um-muted' : ''}`}>
          {everywhere && <span className="um-sr-only">Not these rows: </span>}
          {data ? data.what : tabs.find((t) => t.key === tab)?.what}
          {data && <span className="um-text none"> Replaces the board&rsquo;s {data.board_view}; nothing is moved into a group.</span>}
        </p>
        <StatusLegend legend={legend} />
      </div>

      {data && (
        <div className="um-board-sort">
          <SortControl page={data} onSort={(s, d) => setSort({ col: s, dir: d })}
            columns={[{ sort: 'resident', label: 'Patient' }, { sort: 'facility', label: 'Facility' },
              ...cols.filter((c) => c.sort).map((c) => ({ sort: c.sort as BoardSort, label: c.label }))]} />
        </div>
      )}

      <p className={`um-rownotice${rowNotice ? ' on' : ''}`} role="status" aria-live="polite" tabIndex={-1} ref={noticeRef}>{rowNotice}</p>

      {err && <p className="um-notice stop" role="alert">{err}</p>}
      {!data && loading && <p className="um-text none">Loading…</p>}
      {data && shown.items.length === 0 && (
        <p className="um-empty">
          {!q.trim() ? 'Nothing in this view.'
            : everywhere ? `No case in your scope matches “${q.trim()}”.`
              : `Nothing in ${tabs.find((t) => t.key === tab)?.label || 'this view'} matches “${q.trim()}”.${data.everywhere_total ? ' Search every case to see the rest.' : ''}`}
        </p>
      )}

      {data && data.sections && data.sections.length > 0 && data.scope !== 'EVERYWHERE' && (
        <ul className="um-sections" aria-label={`${data.board_view} sections`}>
          {data.sections.map((x) => (
            <li key={x.key} className="um-section-sum">
              <FilledStatusChip word={x.word} column={UMED_STATUS} label={x.label} />
              <strong className="um-section-n">{x.count}</strong>
              <span className="um-section-what">{x.what}</span>
            </li>
          ))}
        </ul>
      )}

      {data && shown.items.length > 0 && (
        <div className="um-tablewrap">
          <table className="um-table um-boardtable" aria-label={`${everywhere ? 'Every case' : data.board_view} — ${data.total}${everywhere ? '' : ` case${data.total === 1 ? '' : 's'}`}`}>
            <thead>
              <tr>
                <SortHead col="resident" label="Patient" page={data} onSort={(c, d) => setSort({ col: c, dir: d })} className="um-col-who"
                  title="Spelling comes from CounselEAR — correct it there. patients.first_name and patients.last_name are EXTERNAL-authoritative, so a correction made here would be replaced by the next sync." />
                <SortHead col="facility" label="Facility" page={data} onSort={(c, d) => setSort({ col: c, dir: d })} />
                {cols.map((c) => (
                  <SortHead key={c.key} col={c.sort || null} label={c.label} page={data} onSort={(s, d) => setSort({ col: s, dir: d })} />
                ))}
              </tr>
            </thead>
            <tbody>
              {shown.items.map((r, i) => (
                <React.Fragment key={r.id}>
                {/* A section starts where its first row is (Review for Approvals: DCF RESUB NEEDED, then UMED SUBMITTED TO DCF). */}
                {!everywhere && data.sections && r.review_section && (i === 0 || shown.items[i - 1].review_section !== r.review_section) && (() => {
                  const x = data.sections.find((y) => y.key === r.review_section);
                  return x ? (
                    <tr className="um-sectionrow">
                      <th scope="rowgroup" colSpan={cols.length + 2}>
                        <span className="um-sectionhead">
                          <FilledStatusChip word={x.word} column={UMED_STATUS} label={x.label} />
                          <span className="um-section-n">{x.count} {x.count === 1 ? 'resident' : 'residents'}</span>
                        </span>
                      </th>
                    </tr>
                  ) : null;
                })()}
                <tr className={`um-oprow${selected === r.id ? ' on' : ''}${shown.keptIds.has(r.id) ? ' um-kept' : ''}`}
                  /*
                   * The whole record is the control (Isaiah, 2026-09-22: "Each
                   * record should be clickable and closable"). The Open button
                   * used to be the LAST column, so on a table this wide you had
                   * to scroll right to find the way in.
                   *
                   * A click that landed on something else in the row — the SSN
                   * reveal, a sort control, a link — belongs to that thing, not
                   * to the row. Nobody clicking "show me the SSN" is also asking
                   * to open the case.
                   */
                  onClick={(e) => {
                    if ((e.target as HTMLElement).closest('button, a, input, select, textarea')) return;
                    // A double or triple click is selecting a word or a line to copy (Josie Evans, 2026-10-21), not asking to open the case.
                    if (e.detail > 1) return;
                    // Selecting text IS the other thing people do on a table
                    // this wide. A click that ended a selection is a copy in
                    // progress, not a request to open the case -- opening one
                    // here would also blow the selection away.
                    const sel = window.getSelection();
                    if (sel && !sel.isCollapsed && sel.toString().trim()) return;
                    onSelect(r.id);
                  }}>
                  {/* Frozen identity column (§17): the name stays while the rest
                      scrolls — which is exactly why the toggle lives here. It is
                      the one cell that is on screen whatever the horizontal
                      scroll is doing. */}
                  <th scope="row" className="um-col-who">
                    <button type="button" className="um-rowopen" aria-expanded={selected === r.id} data-row-id={r.id}
                      aria-label={`${selected === r.id ? 'Close' : 'Open'} ${personName(r)}`}
                      onClick={(e) => { e.stopPropagation(); onSelect(r.id); }}>
                      <span className="um-caret" aria-hidden="true" />
                      <span className="um-rowname">
                        <strong>{personName(r)}</strong>
                        <FromAnAdvocate row={r} />
                      </span>
                    </button>
                    {/* Beside the name, never inside its button: a count, not a sentence (Josie Evans, 2026-10-02). */}
                    <AlsoOpen others={r.other_open_umeds} name={personName(r)} onOpen={() => { if (selected !== r.id) onSelect(r.id); }} />
                    {r.two_open_after_relink && <TwoOpenFlag />}
                    <CopyCell value={personName(r)} what={`the name ${personName(r)}`} />
                    {everywhere && <RowWhere row={r} tabs={tabs} />}
                    {/* SCRUM-128 (2026-11-03): a Main table search lists closed cases too, last -- each says so. */}
                    {!everywhere && data.closed_included && r.stage === 'CLOSED' && <span className="um-tag um-rowwhere closed" data-testid="row-closed" title="Closed: listed because the search reaches closed cases. Its status word is in the UMED status column.">Closed</span>}
                    {/* 2026-10-20: the copy of a row approved here, which this view no longer lists -- said, not left to be guessed. */}
                    {shown.keptIds.has(r.id) && <span className="um-quiet um-keptnote" data-testid="kept-note">{KEPT_NOTE}</span>}
                  </th>
                  <td>
                    {r.facility_name ? <>{r.facility_name}<CopyCell value={r.facility_name} what="the facility name" /></> : <BuildingOrClinic row={r} fallback={<NotRecorded />} />}
                  </td>
                  {/*
                    * The kept copy is a copy (verification of #446): a write from one of its cells would land, and the copy
                    * would go on showing the value from before it. So its cells are read-only -- a disabled fieldset turns
                    * off every control inside, links stay links -- and the note beside the name says where to act: the
                    * case, which reads the case afresh. A row the server lists again is the server's row, never the copy.
                    */}
                  {cols.map((c) => <td key={c.key}>{shown.keptIds.has(r.id)
                    ? <fieldset className="um-keptcells" disabled aria-label={`${c.label}, as it was when approved`}><ReadOnlyRow.Provider value>{c.render(r)}</ReadOnlyRow.Provider></fieldset>
                    : c.render(r)}</td>)}
                </tr>
                {selected === r.id && detail && (
                  <tr className="um-detailrow">
                    <td colSpan={cols.length + 2}>
                      <div className="um-detailwrap">
                        <OtherOpenList others={r.other_open_umeds} />
                        {detail}
                      </div>
                    </td>
                  </tr>
                )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data && (data.total > data.limit || data.page > 1) && (
        <div className="um-pager">
          <span className="num">{(data.page - 1) * data.limit + 1}–{Math.min(data.page * data.limit, data.total)} of {data.total}</span>
          <div className="btns">
            <button type="button" onClick={() => onPage(data.page - 1)} disabled={data.page <= 1}>Previous</button>
            <button type="button" onClick={() => onPage(data.page + 1)} disabled={data.page >= Math.ceil(data.total / data.limit)}>Next</button>
          </div>
        </div>
      )}

      {data && (
        <p className="um-text none um-board-foot">
          {data.scope === 'EVERYWHERE' ? 'Search results from every case in your scope.' : <>Counted and listed by the same predicate ({data.lens}).</>} {updatedStamp(scope.counts)}.{mondayRetiredLine(scope.counts) && <> {mondayRetiredLine(scope.counts)}.</>}
          {' '}A dash means eMerge holds no value, never that the column failed — hover one to see which.
          {/*
            * Not "empty by definition" (verification 2026-10-06, P2-5): the view is the FLMMIS-issue word, and a case
            * can carry it with the board's approval date, or after an approval here when a correction was raised.
            */}
          {tab === 'new-approvals-noca' && ' On this view an approval date marked “source claim” is the board’s date, not an approval in eMerge; a case approved here shows its own date.'}
        </p>
      )}
    </section>
    </CanReveal.Provider>
    </BoardActs.Provider>
  );
}

/* ═══ Processes 5 and 6: PL correction and Medicaid benefits lost (2026-10-12) ═════════════════════════════════════
 * Business source: "PA Sale to Approval - IF THIS THEN THAT" (Kaleigh Vazquez & Josie Evans, 2026-10-08).
 * Offered only on an approved case the server calls eligible (`after_approval.eligible`) to a caller who holds
 * `can.record_after_approval`; the outcome tile only where a PL correction is open. Every panel says what will happen
 * before it happens. The server decides again on every call and its refusal is shown as written.
 */
type AfterApprovalAct = 'PL_CORRECTION' | 'BENEFITS_LOST' | 'PL_OUTCOME';
const AFTER_APPROVAL_ACTS: readonly string[] = ['PL_CORRECTION', 'BENEFITS_LOST', 'PL_OUTCOME'];
const isAfterApprovalAct = (a: string): a is AfterApprovalAct => AFTER_APPROVAL_ACTS.includes(a);

/** The two statuses are the server's words with the server's colours: mondayPalette has no hex for them. */
const legendHex = (legend: LegendEntry[] | undefined, word: string): string | null =>
  (legend || []).find((e) => e.word === word)?.color || null;

export function afterApprovalTilesFor(row: BoardRow, acts: BoardActsValue | null): ActTile[] {
  const aa = row.after_approval;
  if (!acts || !acts.canAfterApproval || !aa) return [];
  const out: ActTile[] = [];
  if (aa.eligible) {
    out.push({ act: 'PL_CORRECTION', label: 'PL CORRECTION…', hex: legendHex(acts.legend, 'PL CORRECTION') });
    out.push({ act: 'BENEFITS_LOST', label: 'MEDICAID BENEFITS LOST…', hex: legendHex(acts.legend, 'MEDICAID BENEFITS LOST') });
  }
  if (aa.pl_correction && aa.pl_correction.state === 'OPEN') {
    out.push({ act: 'PL_OUTCOME', label: 'PL CORRECTION OUTCOME…', hex: legendHex(acts.legend, 'PL CORRECTION') });
  }
  return out;
}

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
/** 'YYYY-MM' or 'YYYY-MM-DD' -> 'October 2026'. */
const monthLabel = (v: string | null | undefined): string => {
  const m = /^(\d{4})-(\d{2})/.exec(v || '');
  return m && MONTH_NAMES[Number(m[2]) - 1] ? `${MONTH_NAMES[Number(m[2]) - 1]} ${m[1]}` : (v || '');
};
const dollarsOfCents = (c: number): string => `$${(c / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
/** Dollars as typed ("1234.5") -> integer cents, or null when it is not a plain amount. */
export const centsOfDollars = (text: string): number | null => {
  const m = /^\$?\s*(\d{1,9})(?:\.(\d{1,2}))?$/.exec(text.trim().replace(/,/g, ''));
  return m ? Number(m[1]) * 100 + Number(((m[2] || '') + '00').slice(0, 2)) : null;
};
/** Calendar-day arithmetic in UTC, so a zone never moves the date. */
const addDaysIso = (iso: string, days: number): string | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return null;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + days));
  return d.toISOString().slice(0, 10);
};

export function AfterApprovalPanel({ row, act, onClose }: { row: BoardRow; act: AfterApprovalAct; onClose: () => void }) {
  const acts = React.useContext(BoardActs);
  const name = personName(row);
  const open = row.after_approval?.pl_correction || null;
  const revetDays = acts?.revetDays || 90;
  const [dollars, setDollars] = useState('');
  const [month, setMonth] = useState(todayLocal().slice(0, 7));
  const [reason, setReason] = useState('');
  const [on, setOn] = useState(todayLocal());
  const [outcome, setOutcome] = useState<'CORRECTED' | 'DNQ' | ''>('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // One key per opening of the panel: a retry of this click is a replay, a second opening is a new act.
  const [key] = useState(() => freshKey());
  const cents = centsOfDollars(dollars);
  const noteOk = reason.trim().length >= 3;

  const run = async () => {
    setBusy(true); setErr(null);
    try {
      if (act === 'PL_CORRECTION') {
        if (cents === null) return;
        const r = await umedApi.recordPlCorrection(row.id, { new_pl_cents: cents, effective_month: month, reason: reason.trim(), idempotency_key: key });
        if (!r.success) { setErr(refusal(r)); return; }
        onClose();
        acts?.announce(`PL correction recorded for ${name}: ${dollarsOfCents(cents)} from ${monthLabel(month)}. A corrected UMED packet is being prepared; Finance can see the change and no amount was changed.`, row.id);
      } else if (act === 'BENEFITS_LOST') {
        const r = await umedApi.recordBenefitsLost(row.id, { effective_on: on, reason: reason.trim(), idempotency_key: key });
        if (!r.success) { setErr(refusal(r)); return; }
        onClose();
        const d = r.data;
        acts?.announce(`Medicaid benefits lost recorded for ${name}. UMED work is suspended and Finance is flagged; no amount was changed.${d ? ` Re-vet due ${shortDate(d.revetDueOn)}${d.revetOwnerName ? `, owned by ${d.revetOwnerName}` : ''}.` : ''}`, row.id);
      } else {
        if (!open || !outcome) return;
        const r = await umedApi.resolvePlCorrection(open.id, { outcome, note: reason.trim(), ...(outcome === 'DNQ' ? { effective_on: on } : {}), idempotency_key: key });
        if (!r.success) { setErr(refusal(r)); return; }
        onClose();
        acts?.announce(outcome === 'CORRECTED'
          ? `PL correction for ${name} is corrected. The case stays approved and the Finance handoff is amended once.`
          : `${name} is NO LONGER QUALIFIES. Open UMED work is closed and Finance is flagged; no amount was changed.`, row.id);
      }
    } catch { setErr('Could not reach the server. Nothing was recorded.'); } finally { setBusy(false); }
  };

  const dueOn = addDaysIso(on, revetDays);
  let what: string;
  let ready: boolean;
  let confirm: string;
  if (act === 'PL_CORRECTION') {
    what = 'This opens an SFS correction episode and a corrected UMED packet is prepared. Finance sees the PL change. No amount is changed. The case stays approved until the correction is answered.';
    ready = cents !== null && /^\d{4}-\d{2}$/.test(month) && noteOk;
    confirm = 'Record PL correction';
  } else if (act === 'BENEFITS_LOST') {
    what = `The status becomes MEDICAID BENEFITS LOST and UMED work is suspended. Finance is flagged; no amount is changed. One re-vet is due ${dueOn ? shortDate(dueOn) : `${revetDays} days after the effective date`}, owned by the Patient Advocate who covers the building.`;
    ready = /^\d{4}-\d{2}-\d{2}$/.test(on) && noteOk;
    confirm = 'Record benefits lost';
  } else {
    what = outcome === 'CORRECTED'
      ? 'Corrected: the case stays approved and the Finance handoff is amended once. No amount is changed.'
      : outcome === 'DNQ'
        ? 'Does not qualify: the status becomes NO LONGER QUALIFIES, open UMED work is closed and Finance is flagged. No amount is changed.'
        : 'Choose the outcome of the corrected UMED. Nothing is recorded until you confirm.';
    ready = Boolean(outcome) && noteOk && (outcome !== 'DNQ' || /^\d{4}-\d{2}-\d{2}$/.test(on));
    confirm = outcome === 'DNQ' ? 'Record: does not qualify' : outcome === 'CORRECTED' ? 'Record: corrected' : 'Record outcome';
  }
  const title = act === 'PL_CORRECTION' ? 'PL correction' : act === 'BENEFITS_LOST' ? 'Medicaid benefits lost' : 'PL correction outcome';
  return (
    <div className="um-rowpanel um-afterapproval" role="group" aria-label={`${title} for ${name}`} data-act={act}
      onClick={(e) => e.stopPropagation()} onKeyDown={busy ? undefined : escapeCancels(onClose)}>
      {act === 'PL_OUTCOME' && open && (
        <p className="um-quiet">Open: new PL {dollarsOfCents(open.new_pl_cents)} from {monthLabel(open.effective_month)}.</p>
      )}
      <p className="um-rowpanel-what">{what}</p>
      {act === 'PL_CORRECTION' && (
        <>
          <label className="um-rowfield"><span>New patient liability ($)</span>
            <input inputMode="decimal" value={dollars} disabled={busy} onChange={(e) => setDollars(e.target.value)} placeholder="0.00" aria-label="New patient liability in dollars" />
          </label>
          <label className="um-rowfield"><span>Takes effect</span>
            <input type="month" value={month} disabled={busy} onChange={(e) => setMonth(e.target.value)} aria-label="Month the new patient liability takes effect" />
          </label>
        </>
      )}
      {act === 'PL_OUTCOME' && (
        <label className="um-rowfield"><span>Outcome</span>
          <select value={outcome} disabled={busy} onChange={(e) => setOutcome(e.target.value as 'CORRECTED' | 'DNQ' | '')} aria-label="Outcome of the PL correction">
            <option value="">Choose…</option>
            <option value="CORRECTED">Corrected</option>
            <option value="DNQ">Does not qualify</option>
          </select>
        </label>
      )}
      {(act === 'BENEFITS_LOST' || (act === 'PL_OUTCOME' && outcome === 'DNQ')) && (
        <label className="um-rowfield"><span>Effective</span>
          <input type="date" value={on} max={todayLocal()} disabled={busy} onChange={(e) => setOn(e.target.value)}
            aria-label={act === 'BENEFITS_LOST' ? 'The day the Medicaid benefits ended' : 'The day the resident stopped qualifying'} />
        </label>
      )}
      <label className="um-rowfield"><span>{act === 'PL_OUTCOME' ? 'Note' : 'Reason'}</span>
        <input value={reason} maxLength={2000} disabled={busy} onChange={(e) => setReason(e.target.value)}
          placeholder="Why — kept with the case" aria-label={act === 'PL_OUTCOME' ? 'Note for this outcome' : 'Why this was recorded'} />
      </label>
      {err && <p className="um-notice stop" role="alert">{err}</p>}
      <div className="um-rowpanel-acts">
        <button type="button" className="um-open um-primary" disabled={busy || !ready} aria-describedby={(!ready && 'Fill in what is asked above first.') ? `why-after-approval-${row.id}` : undefined} onClick={run}>{busy ? 'Recording…' : confirm}</button><Why id={`why-after-approval-${row.id}`} reason={!ready && 'Fill in what is asked above first.'} className="um-text um-why" />
        <button type="button" className="um-link" disabled={busy} onClick={onClose}>Cancel</button>
      </div>
    </div>
  );
}

/** What is open after approval, said on the row so the two tabs read clearly: the new PL and month; the re-vet's date and owner. */
export function AfterApprovalFacts({ row }: { row: BoardRow }) {
  const aa = row.after_approval;
  if (!aa || (!aa.pl_correction && !aa.benefits_lost)) return null;
  const pl = aa.pl_correction;
  const bl = aa.benefits_lost;
  return (
    <span className="um-afterfacts">
      {pl && (
        <span className="um-tag" data-testid="open-pl-correction" title={pl.reason}>
          PL correction open: {dollarsOfCents(pl.new_pl_cents)} from {monthLabel(pl.effective_month)}
        </span>
      )}
      {bl && (
        <span className={`um-tag${bl.revet_visible ? ' stop' : ''}`} data-testid="open-revet" title={bl.reason}>
          Benefits lost {shortDate(bl.effective_on)} · re-vet due {shortDate(bl.revet_due_on)} · {bl.revet_owner_name || 'no owner yet'}
        </span>
      )}
    </span>
  );
}
/* ═══ end Processes 5 and 6 ══════════════════════════════════════════════════════════════════════════════════════ */
